import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import { ArrowsFullscreen, ChevronLeft, ChevronRight, FullscreenExit, Gear, Plus, Search, X } from 'react-bootstrap-icons';
import JointDecisionsEncounterNote from './JointDecisionsEncounterNote';
import {
  buildEncounterNote,
  chartTabNoteLabel,
  chartUiWithEncounterNote,
  defaultProcedureLevelsText,
  emptyEncounterNoteDrafts,
  emptyEncounterNoteEdited,
  encounterNoteUiFromChart,
  ENCOUNTER_DECISION,
  ENCOUNTER_NOTE_TYPE,
} from '../utils/jointdecEncounterNote';
import {
  buildJointDecisionsDecisionPayload,
  parseSeriesKey,
  seriesKeysFromSurgeries,
} from '../utils/jointdecDecisionSnapshot';
import { Alert, Badge, Button, Card, Form, Modal, Nav, Spinner } from 'react-bootstrap';
import {
  fetchJointDecisionsSpreadsheetCharacteristics,
  fetchJointDecisionsRegressionModels,
  fetchJointDecisionsPracticeStats,
  fetchJointDecisionsStrataSlice,
  fetchJointDecisionsValue,
  fetchJointDecisionsDecision,
  fetchOrganizationJointDecisionsProcedureConfig,
  isDemoOrDevApp,
  saveJointDecisionsDecision,
  saveOrganizationJointDecisionsProcedureConfig,
} from '../api';
import { SURGERIES as MED_MANAGEMENT_SURGERIES, MEDICATIONS as ANTITHROMBOTIC_MEDICATIONS } from './MedicationCheckboxForm';
import {
  canonicalMedicationKey,
  formatMedicationKey,
  mergeEquivalentMedicationsForDisplay,
} from '../utils/medicationEquivalence';
import { observeElementResize } from '../utils/observeElementResize';
import {
  formatPracticeMatchCaption,
  practiceBenefitMean,
  practiceRateForRisk,
} from '../utils/jointdecPracticeStats';
import {
  DEMO_PRACTICE_OUTCOME_CHANGE_SCALE,
  demoPracticeOverlayFactor,
  resolvePracticeAwareRiskPct,
  scaleOutcomeChangeForPractice,
} from '../utils/demoPracticeOverlay';
import { runAfterPaint } from '../utils/runAfterPaint';
import { nonUnionApplies, scoreRegressionStratumRow } from '../utils/jointdecStrataRegression';
import {
  computeProbabilisticExpectedValue,
  computeCostPerPointMetrics,
  formatExpectedValueFormula,
  formatPointReduction,
  formatUsd,
  formatUsdAxisTick,
  VALUE_SETTINGS,
} from '../utils/jointdecValue';
import {
  compareProcedureKeys,
  getProcedureDisplayLabel,
  getSpreadsheetSurgeryColor,
  procedureRegion,
  SPREADSHEET_SURGERY_DISPLAY_LABELS,
} from '../utils/surgeryTypeColors';
import './JointDecisionsSpreadsheet.css';

const SPREADSHEET_CONTROL_LABEL = 'Control';

/** Flip to true to resume sex×age CDN slice fetch (strata lookup / regression fallback). */
const LOAD_STRATA_CDN_SLICE = false;

const JD_CHART_TAB_KEYS = {
  BENEFITS: 'benefits',
  PAIN: 'pain',
  DISABILITY: 'disability',
  MEDICAL_RISKS: 'medical_risks',
  PROCEDURE_RISKS: 'procedure_risks',
  RECOVERY: 'recovery',
  LONG_TERM: 'long_term',
  VALUE: 'value',
};

/** Chart and label typography — larger, black text for readability. */
const JD_TEXT = '#000000';
const JD_MODIFICATION_COLOR = '#198754';
/** Lifestyle overlay: reduced slice of the original bar, same fill, lower opacity. */
const JD_SCENARIO_FADE_OPACITY = 0.38;

function isComplicationChartTab(tabKey) {
  return (
    tabKey === JD_CHART_TAB_KEYS.MEDICAL_RISKS ||
    tabKey === JD_CHART_TAB_KEYS.PROCEDURE_RISKS ||
    tabKey === JD_CHART_TAB_KEYS.LONG_TERM
  );
}

function showsPracticeRateSourceToggle(tabKey) {
  return (
    tabKey === JD_CHART_TAB_KEYS.BENEFITS ||
    isComplicationChartTab(tabKey) ||
    tabKey === JD_CHART_TAB_KEYS.RECOVERY
  );
}
const JD_CHART_FONT = {
  axis: '14px',
  value: '13px',
  subtitle: '14px',
  legend: '13px',
  legendLead: '14px',
  barPct: '12px',
};

function styleChartAxis(axisGroup, fontSize = JD_CHART_FONT.axis) {
  axisGroup
    .selectAll('text')
    .style('font-size', fontSize)
    .attr('fill', JD_TEXT);
  axisGroup.selectAll('line, path').attr('stroke', '#333');
}

/** Chart labels: drop leading 0 before a decimal (0.05 → .05). */
function formatChartDecimal(value, decimals) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return '0';
  const absText = Math.abs(n).toFixed(decimals).replace(/^0\./, '.');
  return n < 0 ? `-${absText}` : absText;
}

/** Rates below this floor display as ~.01 (true N/A stays "N/A"). */
const COMPLICATION_DISPLAY_FLOOR = 0.01;

/** Complication labels: always show 2 decimal places (medical / procedure / long-term). */
function formatComplicationPct(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 'N/A';
  if (n < COMPLICATION_DISPLAY_FLOOR) return '~.01';
  return formatChartDecimal(n, 2);
}

/**
 * Value-axis tick formatter: precision follows the tick step so labels stay unique
 * (avoids .1% / .1% / .2% / .2% collapse). Bar labels still use formatComplicationPct.
 */
function makeComplicationPctAxisTickFormat(scale, tickCount) {
  const ticks = typeof scale.ticks === 'function' ? scale.ticks(tickCount) : [];
  let decimals = 0;
  for (let i = 1; i < ticks.length; i += 1) {
    const step = Math.abs(ticks[i] - ticks[i - 1]);
    if (!(step > 0) || step >= 1) continue;
    decimals = Math.max(decimals, Math.min(4, Math.ceil(-Math.log10(step) - 1e-12)));
  }
  // Small domains (e.g. 0–0.2) need at least 2 places so .10% ≠ .12%.
  const domainMax = Math.max(...(scale.domain?.() || [0]).map((v) => Math.abs(Number(v) || 0)));
  if (domainMax > 0 && domainMax < 1) {
    decimals = Math.max(decimals, 2);
  }
  return (value) => `${formatChartDecimal(value, decimals)}%`;
}

/** Score change for lower-is-better outcomes (post − pre). Negative = improvement. */
function isFiniteOutcomeValue(value) {
  // Number(null) === 0, so empty practice benefits must be checked before coerce.
  if (value == null || value === '') return false;
  return Number.isFinite(Number(value));
}

function computeOutcomeImprovement(prior, after) {
  if (!isFiniteOutcomeValue(prior) || !isFiniteOutcomeValue(after)) {
    return { absDiff: null, pctDiff: null };
  }
  const p = Number(prior);
  const a = Number(after);
  const absDiff = a - p;
  const pctDiff = p !== 0 ? (absDiff / p) * 100 : null;
  return { absDiff, pctDiff };
}

function formatSignedDelta(value, decimals = 1, unit = '') {
  if (value == null || !Number.isFinite(value)) return 'N/A';
  const sign = value > 0 ? '+' : '';
  return `${sign}${formatChartDecimal(value, decimals)}${unit}`;
}

function formatPctChange(pctDiff, decimals = 1, { includePercentSign = false } = {}) {
  if (pctDiff == null || !Number.isFinite(pctDiff)) return 'N/A';
  const sign = pctDiff > 0 ? '+' : '';
  const body = `${sign}${formatChartDecimal(pctDiff, decimals)}`;
  return includePercentSign ? `${body}%` : body;
}

/** Always `±X (±Y)` — signed absolute change, then signed % in parentheses. */
function formatDeltaAnnotation(absDiff, pctDiff, { absDecimals = 1, includePercentSign = false } = {}) {
  const abs = formatSignedDelta(absDiff, absDecimals);
  const pct = formatPctChange(pctDiff, 1, { includePercentSign });
  return `${abs} (${pct})`;
}

/** Stable left-to-right surgery order: alphabetical by display label. */
function sortProfilesAlphabetically(profiles) {
  return [...profiles].sort((a, b) => {
    const labelA = a?.label || getProcedureDisplayLabel(a?.surgery) || '';
    const labelB = b?.label || getProcedureDisplayLabel(b?.surgery) || '';
    return labelA.localeCompare(labelB);
  });
}

/** Left-to-right metric order within each chart tab — labels match spreadsheet headers. */
const MEDICAL_RISK_LABELS = [
  'Deep Vein Thrombosis',
  'Pulmonary Embolism',
  'Myocardial Infarction',
  'Stroke',
  'Death',
];
const MEDICAL_RISK_LABEL_SET = new Set(MEDICAL_RISK_LABELS);
const PROCEDURE_RISK_LABELS = [
  'Postoperative Bleeding/Wound Hematoma',
  'Intraoperative Incidental Durotomy',
  'Vascular Injury',
  'Visceral Organ Injury',
  'Neurological Injury',
  'Surgical Site Infection',
];
const RECOVERY_METRIC_LABELS = [
  'Total Surgery Time (Minutes)',
  'Length of Hospital Stay (Days)',
];
const LONG_TERM_RISK_LABELS = [
  'Revision Surgery',
  'Reherniation',
  'Non-Union',
  'Adjacent Segment Disease',
  'Abdominal Hernia',
];

const MEANINGFUL_DIFFERENCE_THRESHOLDS = {
  'Myocardial Infarction': { abs: 0.05, rel: 20, largeAbs: 0.10, largeRel: 30, unit: 'pp' },
  Stroke: { abs: 0.05, rel: 20, largeAbs: 0.10, largeRel: 30, unit: 'pp' },
  Death: { abs: 0.05, rel: 20, largeAbs: 0.10, largeRel: 30, unit: 'pp' },
  'Pulmonary Embolism': { abs: 0.10, rel: 20, largeAbs: 0.20, largeRel: 30, unit: 'pp' },
  'Deep Vein Thrombosis': { abs: 0.20, rel: 20, largeAbs: 0.40, largeRel: 30, unit: 'pp' },
  'Surgical Site Infection': { abs: 0.20, rel: 20, largeAbs: 0.40, largeRel: 30, unit: 'pp' },
  'Adjacent Segment Disease': { abs: 1.0, rel: 20, largeAbs: 2.0, largeRel: 30, unit: 'pp' },
  'Non-Union': { abs: 1.0, rel: 20, largeAbs: 2.0, largeRel: 30, unit: 'pp' },
  'Revision Surgery': { abs: 1.0, rel: 20, largeAbs: 2.0, largeRel: 30, unit: 'pp' },
  'Neurological Injury': { abs: 0.05, rel: 20, largeAbs: 0.10, largeRel: 30, unit: 'pp' },
  'Vascular Injury': { abs: 0.05, rel: 20, largeAbs: 0.10, largeRel: 30, unit: 'pp' },
  'Epidural Hematoma': { abs: 0.05, rel: 20, largeAbs: 0.10, largeRel: 30, unit: 'pp' },
  'Postoperative Bleeding/Wound Hematoma': { abs: 0.10, rel: 20, largeAbs: 0.20, largeRel: 30, unit: 'pp' },
  'Intraoperative Incidental Durotomy': { abs: 0.50, rel: 20, largeAbs: 1.0, largeRel: 30, unit: 'pp' },
  'Excessive Blood Loss >=500 mL': { abs: 1.0, rel: 20, largeAbs: 2.0, largeRel: 30, unit: 'pp' },
  'Abdominal Hernia': { abs: 0.50, rel: 20, largeAbs: 1.0, largeRel: 30, unit: 'pp' },
  Reherniation: { abs: 1.0, rel: 20, largeAbs: 2.0, largeRel: 30, unit: 'pp' },
  'Visceral Organ Injury': { abs: 0.05, rel: 20, largeAbs: 0.10, largeRel: 30, unit: 'pp' },
  'Lumbar Plexus Injury': { abs: 0.10, rel: 20, largeAbs: 0.20, largeRel: 30, unit: 'pp' },
  'Total Surgery Time (Minutes)': { abs: 15, largeAbs: 30, unit: 'min' },
  'Length of Hospital Stay (Days)': { abs: 0.5, largeAbs: 1, unit: 'day' },
};

const BENEFIT_DELTA_THRESHOLDS = {
  [JD_CHART_TAB_KEYS.PAIN]: { abs: 1, largeAbs: 2, unit: 'point' },
  functionOdi: { abs: 13, largeAbs: 19, unit: 'point' },
  functionNdi: { abs: 15, largeAbs: 20, unit: 'point' },
};

const CHART_RISK_LABEL_ORDER = [
  ...MEDICAL_RISK_LABELS,
  ...PROCEDURE_RISK_LABELS,
  ...RECOVERY_METRIC_LABELS,
  ...LONG_TERM_RISK_LABELS,
];

function sortRiskLabelsForChart(labels, preferredOrder = CHART_RISK_LABEL_ORDER) {
  const order = new Map(preferredOrder.map((name, index) => [name, index]));
  return [...labels].sort((a, b) => {
    const indexA = order.has(a) ? order.get(a) : preferredOrder.length;
    const indexB = order.has(b) ? order.get(b) : preferredOrder.length;
    if (indexA !== indexB) return indexA - indexB;
    return a.localeCompare(b);
  });
}

/** Spreadsheet follow-up window (or unit) shown on complication category axes. */
const RISK_LABEL_TIMEFRAME = {
  Death: '30 Days',
  Stroke: '30 Days',
  'Myocardial Infarction': '30 Days',
  'Pulmonary Embolism': '30 Days',
  'Deep Vein Thrombosis': '30 Days',
  'Surgical Site Infection': '30 Days',
  'Neurological Injury': '30 Days',
  'Epidural Hematoma': '30 Days',
  'Postoperative Bleeding/Wound Hematoma': '30 Days',
  'Vascular Injury': '30 Days',
  'Intraoperative Incidental Durotomy': 'Immediate',
  'Visceral Organ Injury': 'Immediate',
  'Lumbar Plexus Injury': 'Immediate',
  'Revision Surgery': '1 Year',
  'Adjacent Segment Disease': '1 Year',
  'Non-Union': '1 Year',
  'Abdominal Hernia': '1 Year',
  Reherniation: '5 Years',
};

function formatRiskLabelForChart(label) {
  const name = String(label ?? '').trim();
  if (!name) return '';
  if (/\(.*\)/.test(name)) return name;
  const timeframe = RISK_LABEL_TIMEFRAME[name];
  return timeframe ? `${name} (${timeframe})` : name;
}

/** Keep parenthetical timeframes/units as one token so they wrap onto a single line. */
function splitRiskAxisLabelTokens(label) {
  const raw = String(label ?? '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!raw) return [];
  return raw.match(/\([^)]+\)|[^\s(]+/g) || [raw];
}

/** Em gap from axis line down to the first word of a bottom-axis label. */
const RISK_AXIS_LABEL_GAP_EM = 1.55;
const RISK_AXIS_LINE_HEIGHT_EM = 1.15;

function countRiskLabelWords(label) {
  return splitRiskAxisLabelTokens(formatRiskLabelForChart(label)).length;
}

/** Bottom margin that fits wrapped x-axis labels without spare empty space. */
function riskChartBottomMargin(labels, categoryAxisFont) {
  const fontPx = fontPxSize(categoryAxisFont);
  const maxWords = Math.max(1, ...labels.map(countRiskLabelWords));
  const labelBlockEm = RISK_AXIS_LABEL_GAP_EM + Math.max(0, maxWords - 1) * RISK_AXIS_LINE_HEIGHT_EM;
  // Tick stub (~6px) + wrapped label block + small pad so descenders aren't clipped.
  return Math.ceil(6 + labelBlockEm * fontPx + 6);
}

function fontPxSize(fontSize) {
  const px = parseFloat(fontSize);
  return Number.isFinite(px) ? px : 11;
}

/** Put each word on its own line (horizontal text, stacked tspans). */
function wrapRiskAxisTickWords(
  textSelection,
  { anchor = 'middle', lineHeightEm = RISK_AXIS_LINE_HEIGHT_EM, stack = 'down', getLabel } = {},
) {
  textSelection.each(function wrapWords(d) {
    const text = d3.select(this);
    // Prefer bound datum — text.text() also picks up nested <title> tooltip content.
    const existingTip = text.select('title');
    const tipText = existingTip.empty() ? null : existingTip.text();
    const source = typeof getLabel === 'function' ? getLabel(d) : d;
    const raw = formatRiskLabelForChart(String(source ?? ''))
      .replace(/\s+/g, ' ')
      .trim();
    if (!raw) return;

    // Clear BOTH child nodes and the text element's own character data so labels
    // are not painted twice (once as plain text, once as tspans).
    text.text(null);
    text.attr('dy', null).attr('dx', null).attr('text-anchor', anchor);

    // Split on spaces; keep slash compounds and parenthetical timeframes as one token.
    const words = splitRiskAxisLabelTokens(raw);

    if (words.length <= 1) {
      text.text(words[0] || raw);
      if (stack === 'down') text.attr('dy', `${RISK_AXIS_LABEL_GAP_EM}em`);
      if (tipText) text.append('title').text(tipText);
      return;
    }

    const startDyEm =
      stack === 'center'
        ? -((words.length - 1) * lineHeightEm) / 2
        : RISK_AXIS_LABEL_GAP_EM;
    words.forEach((word, i) => {
      text
        .append('tspan')
        .attr('x', 0)
        .attr('dy', `${i === 0 ? startDyEm : lineHeightEm}em`)
        .attr('text-anchor', anchor)
        .text(word);
    });
    if (tipText) text.append('title').text(tipText);
  });
}

/** Full spreadsheet header (with timeframe) for hover when useful. */
function riskLabelTooltip(label) {
  const tips = {
    Death: 'Death Surgery (30 Days)',
    Stroke: 'Stroke Surgery (30 Days)',
    'Myocardial Infarction': 'Myocardial Infarction Surgery (30 Days)',
    'Pulmonary Embolism': 'Pulmonary Embolism Surgery (30 Days)',
    'Deep Vein Thrombosis': 'Deep Vein Thrombosis Surgery (30 Days)',
    'Surgical Site Infection': 'Surgical Site Infection (30 Days)',
    'Neurological Injury': 'Neurological Injury (30 Days)',
    'Postoperative Bleeding/Wound Hematoma': 'Postoperative Bleeding/Wound Hemotoma (30 Days)',
    'Intraoperative Incidental Durotomy': 'Intraoperative Incidental Durotomy (Immediate)',
    'Vascular Injury': 'Vasculary Injury (30 Days)',
    'Visceral Organ Injury':
      'Visceral Organ Injury (Bowel, Spleen, Kidney, Ureter: Immediate)',
    'Total Surgery Time (Minutes)': 'Total Surgery Time (Minutes)',
    'Length of Hospital Stay (Days)': 'Length of Hospital Stay (Days)',
    'Revision Surgery': 'Revision Surgery (1 Year)',
    'Adjacent Segment Disease': 'Adjacent Segment Disease (1 Year)',
    'Non-Union': 'Non-Union (1 Year)',
    Reherniation: 'Reherniation (Micro Disc, 5 Years)',
    'Abdominal Hernia': 'Abdominal Hernia (1 Year)',
  };
  return tips[label] || null;
}

function formatMetricBarValue(value, valueKind = 'percent') {
  // Missing / not-applicable (null from rubric) — not the same as a measured 0.
  if (value == null || value === '') return 'N/A';
  const n = Number(value);
  if (!Number.isFinite(n)) return 'N/A';
  if (valueKind === 'absolute') {
    return formatChartDecimal(n, Math.abs(n) >= 100 ? 0 : 1);
  }
  return formatComplicationPct(n);
}

/** Geometry helper: N/A and non-positive rates draw as a zero-length stub bar. */
function metricBarDrawValue(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n;
}

/** Skip scenario arrow when both ends format to the same label (e.g. ~.01 → ~.01). */
function scenarioRateDisplayChanges(baselineValue, scenarioValue, formatValue) {
  if (scenarioValue == null) return false;
  return formatValue(baselineValue) !== formatValue(scenarioValue);
}

/** Lifestyle overlay wins when present. Practice vs research is a source toggle, not an overlay. */
function resolveRateOverlay(entry, bar, formatValue) {
  if (
    !entry?.hasNoScenarioImprovement &&
    scenarioRateDisplayChanges(bar?.pct, bar?.scenarioPct, formatValue)
  ) {
    return {
      pct: bar.scenarioPct,
      fill: JD_MODIFICATION_COLOR,
      className: 'jd-scenario-overlap-bar',
    };
  }
  return null;
}

function getFunctionOutcomeThreshold(regionKey) {
  return regionKey === 'CERVICAL'
    ? BENEFIT_DELTA_THRESHOLDS.functionNdi
    : BENEFIT_DELTA_THRESHOLDS.functionOdi;
}

function getBenefitDeltaThreshold(outcomeType, regionKey) {
  return outcomeType === JD_CHART_TAB_KEYS.PAIN
    ? BENEFIT_DELTA_THRESHOLDS[JD_CHART_TAB_KEYS.PAIN]
    : getFunctionOutcomeThreshold(regionKey);
}

function formatThresholdValue(value, unit) {
  const formatted = formatChartDecimal(value, Number(value) >= 10 ? 0 : 2).replace(/\.00$/, '');
  if (unit === 'pp') return `${formatted} pp`;
  if (unit === 'min') return `${formatted} min`;
  if (unit === 'day') return `${formatted} day${Number(value) === 1 ? '' : 's'}`;
  return `${formatted} point${Number(value) === 1 ? '' : 's'}`;
}

function formatThresholdSummary(threshold) {
  if (!threshold) return '';
  const parts = [`Meaningful ${formatThresholdValue(threshold.abs, threshold.unit)}`];
  if (threshold.rel != null) parts.push(`>=${threshold.rel}% relative`);
  parts.push(`Large ${formatThresholdValue(threshold.largeAbs, threshold.unit)}`);
  if (threshold.largeRel != null) parts.push(`>=${threshold.largeRel}% relative`);
  return parts.join(' | ');
}

function thresholdRelativeChange(referenceValue, comparisonValue) {
  const reference = Math.abs(Number(referenceValue));
  const comparison = Math.abs(Number(comparisonValue));
  const diff = Math.abs(comparison - reference);
  if (!Number.isFinite(reference) || !Number.isFinite(comparison)) return 0;
  if (reference === 0) return diff > 0 ? Infinity : 0;
  return (diff / reference) * 100;
}

function differenceMeetsThreshold(referenceValue, comparisonValue, threshold) {
  if (!threshold) return true;
  const reference = Number(referenceValue);
  const comparison = Number(comparisonValue);
  if (!Number.isFinite(reference) || !Number.isFinite(comparison)) return false;
  const absDiff = Math.abs(comparison - reference);
  if (absDiff < threshold.abs) return false;
  if (threshold.rel == null) return true;
  return thresholdRelativeChange(reference, comparison) >= threshold.rel;
}

function riskValuesMeetThreshold(values, threshold) {
  const finiteValues = (values || []).filter((value) => Number.isFinite(value));
  if (finiteValues.length < 2) return false;
  const min = Math.min(...finiteValues);
  const max = Math.max(...finiteValues);
  return differenceMeetsThreshold(min, max, threshold);
}

function outcomeDeltaMeetsThreshold(prior, after, threshold) {
  const beforeValue = Number(prior);
  const afterValue = Number(after);
  if (!Number.isFinite(beforeValue) || !Number.isFinite(afterValue)) return false;
  return beforeValue - afterValue >= threshold.abs;
}

function profileHasMeaningfulOutcomeDelta(profile, outcomeType, regionKey) {
  const threshold = getBenefitDeltaThreshold(outcomeType, regionKey);
  const outcomes = normalizeProfileOutcomes(profile);
  const priorKey = outcomeType === JD_CHART_TAB_KEYS.PAIN ? 'pain_prior' : 'function_prior';
  const afterKey = outcomeType === JD_CHART_TAB_KEYS.PAIN ? 'pain_after' : 'function_after';
  return outcomeDeltaMeetsThreshold(outcomes[priorKey], outcomes[afterKey], threshold);
}

function scenarioOutcomeDirectionallyImproves(baselineProfile, scenarioProfile, outcomeType) {
  if (!baselineProfile || !scenarioProfile) return false;
  const afterKey = outcomeType === JD_CHART_TAB_KEYS.PAIN ? 'pain_after' : 'function_after';
  const baselineAfter = normalizeProfileOutcomes(baselineProfile)[afterKey];
  const scenarioAfter = normalizeProfileOutcomes(scenarioProfile)[afterKey];
  return Number.isFinite(baselineAfter) && Number.isFinite(scenarioAfter) && scenarioAfter < baselineAfter;
}

function scenarioOutcomeHasMeaningfulImprovement(baselineProfile, scenarioProfile, outcomeType, regionKey) {
  return (
    scenarioOutcomeDirectionallyImproves(baselineProfile, scenarioProfile, outcomeType) &&
    profileHasMeaningfulOutcomeDelta(scenarioProfile, outcomeType, regionKey)
  );
}

function collectRiskComparisonValues({
  riskLabel,
  profiles,
  spreadsheetControls,
  includeControl,
  scenarioProfiles = [],
  scenarioSpreadsheetControls = null,
}) {
  const slots = buildSlotsForRisk(riskLabel, spreadsheetControls, profiles, includeControl);
  const values = slots.map((slot) => slot.pct);
  if (scenarioProfiles?.length) {
    const scenarioSlots = buildSlotsForRisk(
      riskLabel,
      scenarioSpreadsheetControls || spreadsheetControls,
      scenarioProfiles,
      includeControl,
    );
    values.push(...scenarioSlots.map((slot) => slot.pct));
  }
  return values;
}

function riskLabelHasMeaningfulDifference(args) {
  const threshold = MEANINGFUL_DIFFERENCE_THRESHOLDS[args.riskLabel];
  return riskValuesMeetThreshold(collectRiskComparisonValues(args), threshold);
}

function riskLabelHasAnySurgeryValue({ riskLabel, profiles, spreadsheetControls, scenarioProfiles = [], scenarioSpreadsheetControls = null }) {
  const values = collectRiskComparisonValues({
    riskLabel,
    profiles,
    spreadsheetControls,
    includeControl: false,
    scenarioProfiles,
    scenarioSpreadsheetControls,
  });
  return values.some((value) => Number.isFinite(value) && Math.abs(value) > 0);
}

function riskLabelHasMeaningfulScenarioImprovement({
  riskLabel,
  profiles,
  scenarioProfiles = [],
}) {
  if (!scenarioProfiles?.length) return false;
  const scenarioBySurgery = new Map(scenarioProfiles.map((profile) => [profile.surgery, profile]));
  const threshold = MEANINGFUL_DIFFERENCE_THRESHOLDS[riskLabel];
  return (profiles || []).some((profile) => {
    const scenarioProfile = scenarioBySurgery.get(profile.surgery);
    if (!scenarioProfile) return false;
    const baselineValue = parsePct(profile?.risks?.[riskLabel]) ?? parseOutcomeNumber(profile?.risks?.[riskLabel]);
    const scenarioValue = parsePct(scenarioProfile?.risks?.[riskLabel]) ?? parseOutcomeNumber(scenarioProfile?.risks?.[riskLabel]);
    if (!Number.isFinite(baselineValue) || !Number.isFinite(scenarioValue)) return false;
    return scenarioValue < baselineValue && differenceMeetsThreshold(baselineValue, scenarioValue, threshold);
  });
}

function countMeaningfulRiskLabels(args) {
  return (args.riskLabels || []).filter((riskLabel) =>
    riskLabelHasAnySurgeryValue({ ...args, riskLabel }) &&
    (args.scenarioProfiles?.length
      ? riskLabelHasMeaningfulScenarioImprovement({ ...args, riskLabel })
      : riskLabelHasMeaningfulDifference({ ...args, riskLabel })),
  ).length;
}

function countMeaningfulOutcomeProfiles(profiles, outcomeType, regionKey) {
  return (profiles || []).filter((profile) =>
    profileHasMeaningfulOutcomeDelta(profile, outcomeType, regionKey),
  ).length;
}

function countMeaningfulScenarioOutcomeProfiles(profiles, scenarioProfiles, outcomeType, regionKey) {
  if (!scenarioProfiles?.length) {
    return countMeaningfulOutcomeProfiles(profiles, outcomeType, regionKey);
  }
  const scenarioBySurgery = new Map(scenarioProfiles.map((profile) => [profile.surgery, profile]));
  return (profiles || []).filter((profile) => {
    const scenarioProfile = scenarioBySurgery.get(profile.surgery);
    return (
      scenarioOutcomeHasMeaningfulImprovement(profile, scenarioProfile, outcomeType, regionKey)
    );
  }).length;
}

function makeMetricAxisTickFormat(scale, tickCount, valueKind = 'percent') {
  if (valueKind === 'percent' || valueKind === 'percentImprovement') {
    return makeComplicationPctAxisTickFormat(scale, tickCount);
  }
  const ticks = typeof scale.ticks === 'function' ? scale.ticks(tickCount) : [];
  let decimals = 0;
  for (let i = 1; i < ticks.length; i += 1) {
    const step = Math.abs(ticks[i] - ticks[i - 1]);
    if (!(step > 0) || step >= 1) continue;
    decimals = Math.max(decimals, Math.min(2, Math.ceil(-Math.log10(step) - 1e-12)));
  }
  return (value) => formatChartDecimal(value, decimals);
}

const SPREADSHEET_REGION_OPTIONS = [
  { value: 'CERVICAL', label: 'Cervical' },
  { value: 'THORACIC', label: 'Thoracic' },
  { value: 'LUMBAR', label: 'Lumbar' },
];

const SPREADSHEET_SURGERY_KEYS = Object.keys(SPREADSHEET_SURGERY_DISPLAY_LABELS);

const SPREADSHEET_RISK_CSV_TO_LABEL = {
  'MI SURGERY': 'Myocardial Infarction',
  'STROKE SURGERY': 'Stroke',
  'DEATH SURGERY': 'Death',
  'PE SURGERY': 'Pulmonary Embolism',
  'DVT SURGERY': 'Deep Vein Thrombosis',
  INFECTION: 'Surgical Site Infection',
  'ADJACENT SEGMENT DISEASE': 'Adjacent Segment Disease',
  'NON-UNION': 'Non-Union',
  'REVISION SURGERY (1 YEAR)': 'Revision Surgery',
};

const SPREADSHEET_CONTROL_CSV_TO_LABEL = {
  'MI CONTROL': 'Myocardial Infarction',
  'STROKE CONTROL': 'Stroke',
  'DEATH CONTROL': 'Death',
  'PE CONTROL': 'Pulmonary Embolism',
  'DVT CONTROL': 'Deep Vein Thrombosis',
};

const SPREADSHEET_OUTCOME_CSV_TO_KEY = {
  'PAIN PRIOR (NRS)': 'pain_prior',
  'PAIN AFTER (NRS)': 'pain_after',
  'PAIN AFTER (NRS @ 6 MONTHS)': 'pain_after',
  'FUNCTION PRIOR (NDI/ODI)': 'function_prior',
  'FUNCTION AFTER (NDI/ODI)': 'function_after',
  'FUNCTION AFTER (NDI/ODI @ 6 MONTHS)': 'function_after',
};

const EMPTY_OUTCOMES = {
  pain_prior: null,
  pain_after: null,
  function_prior: null,
  function_after: null,
};

/** Disability uses 0–100; pain stays on the native 0–10 NRS scale. */
const OUTCOME_CHART_Y_MAX = 100;
const PAIN_NRS_SCALE_MAX = 10;

/** NRS pain is 0–10; NDI/ODI disability is 0–100. */
function normalizeOutcomeForChart(value, outcomeKey) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return n;
}

const SPREADSHEET_BMI_ORDER = [
  'Less than 18.5',
  '18.5-24.9',
  '25.0-34.9',
  '35.0 and over',
];

/** Display labels for spreadsheet BMI ranges (internal values stay numeric ranges). */
const SPREADSHEET_BMI_VERBAL_BY_RANGE = {
  'Less than 18.5': 'Underweight',
  '18.5-24.9': 'Healthy',
  '25.0-34.9': 'Overweight',
  '35.0 and over': 'Obese',
};

const SPREADSHEET_SMOKING_OPTIONS = [
  { value: 'NO', label: 'Non-smoker' },
  { value: 'YES', label: 'Smoker' },
];

const SPREADSHEET_DIABETES_OPTIONS = [
  { value: '0', label: 'No diabetes' },
  { value: '1', label: 'Prediabetes' },
  { value: '2', label: 'Diabetes' },
];

const SPREADSHEET_ANTITHROMBOTIC_OPTIONS = [
  { value: '0', label: 'No antithrombotics' },
  { value: '1', label: 'Yes, managed' },
  { value: '2', label: 'Yes, unmanaged' },
];

const SPREADSHEET_INSURANCE_OPTIONS = [
  { value: 'workers_comp', label: 'Workers Comp' },
  { value: 'other', label: 'Other' },
];

/** levels: 0 = 1–2 level(s), 1 = 3+ levels — picker dropdown + series keys */
const SPREADSHEET_LEVEL_SERIES = [
  { levels: 0, shortLabel: '1–2', label: '1–2 level(s)' },
  { levels: 1, shortLabel: '3+', label: '3+ levels' },
];

const SPREADSHEET_LEVELS_OPTIONS = SPREADSHEET_LEVEL_SERIES.map(({ levels, label }) => ({
  value: String(levels),
  label,
}));

/**
 * Procedures where multilevel (3+) does not apply — only list under 1–2,
 * never under the 3+ category.
 */
const LEVELLESS_PROCEDURE_KEYS = new Set([
  'cervical_epidural_injection',
  'thoracic_epidural_injection',
  'lumbar_epidural_injection',
  'lumbar_facet_joint_replacement',
  'lumbar_coflex',
  'cervical_scs',
  'thoracic_scs',
  'lumbar_scs',
]);

function procedureSupportsMultilevel(procedureKey) {
  return Boolean(procedureKey) && !LEVELLESS_PROCEDURE_KEYS.has(procedureKey);
}

/** revision_surgery: 0 = primary, 1 = revision (global header filter) */
const SPREADSHEET_REVISION_OPTIONS = [
  { value: '0', label: 'Primary' },
  { value: '1', label: 'Revision' },
];

const STRATA_SURGERY_RISK_FIELDS = {
  death_surgery: 'Death',
  stroke_surgery: 'Stroke',
  mi_surgery: 'Myocardial Infarction',
  pe_surgery: 'Pulmonary Embolism',
  dvt_surgery: 'Deep Vein Thrombosis',
  surgical_site_infection: 'Surgical Site Infection',
  neurological_injury: 'Neurological Injury',
  postop_bleeding_hematoma: 'Postoperative Bleeding/Wound Hematoma',
  intraop_durotomy: 'Intraoperative Incidental Durotomy',
  vascular_injury: 'Vascular Injury',
  visceral_organ_injury: 'Visceral Organ Injury',
  total_surgery_time_minutes: 'Total Surgery Time (Minutes)',
  length_of_stay_days: 'Length of Hospital Stay (Days)',
  revision_surgery_1yr: 'Revision Surgery',
  adjacent_segment_disease: 'Adjacent Segment Disease',
  non_union: 'Non-Union',
  reherniation: 'Reherniation',
  abdominal_hernia: 'Abdominal Hernia',
};

const STRATA_CONTROL_RISK_FIELDS = {
  mi_control: 'Myocardial Infarction',
  stroke_control: 'Stroke',
  death_control: 'Death',
  pe_control: 'Pulmonary Embolism',
  dvt_control: 'Deep Vein Thrombosis',
};

const STRATA_OUTCOME_FIELDS = ['pain_prior', 'pain_after', 'function_prior', 'function_after'];

function bmiLabelToBucket(label) {
  if (!label) return null;
  const idx = SPREADSHEET_BMI_ORDER.indexOf(String(label).trim());
  return idx >= 0 ? idx : null;
}

function bmiBucketToLabel(bucket) {
  if (bucket == null || bucket === '') return null;
  const idx = Number(bucket);
  return Number.isInteger(idx) && idx >= 0 && idx < SPREADSHEET_BMI_ORDER.length
    ? SPREADSHEET_BMI_ORDER[idx]
    : null;
}

function bmiRangeToVerbalLabel(rangeLabel) {
  if (!rangeLabel) return null;
  const key = String(rangeLabel).trim();
  return SPREADSHEET_BMI_VERBAL_BY_RANGE[key] || key;
}

function bmiBucketToVerbalLabel(bucket) {
  return bmiRangeToVerbalLabel(bmiBucketToLabel(bucket));
}

function encodeSmokingForStratum(smokingValue) {
  if (smokingValue === 'YES') return 1;
  if (smokingValue === 'NO') return 0;
  return null;
}

function encodeInsuranceForStratum(insuranceValue) {
  if (!insuranceValue) return null;
  return insuranceValue === 'workers_comp' ? 1 : 0;
}

function encodeBucketFilter(value) {
  if (value === undefined || value === null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function makeLevelSeriesKey(procedureKey, levels) {
  return `${procedureKey}|L${Number(levels)}`;
}

function parseLevelSeriesKey(key) {
  if (!key || typeof key !== 'string') return null;
  const match = key.match(/^(.+)\|L(\d+)$/);
  if (!match) {
    return {
      procedureKey: key,
      levels: 0,
      key: makeLevelSeriesKey(key, 0),
    };
  }
  return {
    procedureKey: match[1],
    levels: Number(match[2]),
    key,
  };
}

function formatLevelSeriesLabel(procedureKey, levels) {
  const base = getProcedureDisplayLabel(procedureKey) || procedureKey;
  if (!procedureSupportsMultilevel(procedureKey)) return base;
  return Number(levels) === 1 ? `${base} · 3+` : `${base} · 1–2`;
}

function surgeryKeyToNoteLabel(surgeryKey) {
  const parsed = parseLevelSeriesKey(surgeryKey);
  if (!parsed) return String(surgeryKey || '');
  return formatLevelSeriesLabel(parsed.procedureKey, parsed.levels);
}

/** X-axis: procedure name only. 1–2 vs 3+ is shown by hatch, not the label. */
function formatSurgeryChartAxisLabel(procedureKey, fallback = '') {
  const base = getProcedureDisplayLabel(procedureKey);
  if (base) return base;
  return String(fallback || procedureKey || '').replace(/\s*·\s*(1–2|3\+)\s*$/u, '').trim();
}

/** Map Joint Decisions procedure + levels to periop note-form surgery tokens. */
function mapJointDecisionsSurgeryToPeriop(procedureKey, levels) {
  const multilevel = Number(levels) === 1;
  switch (procedureKey) {
    case 'acdf':
      return multilevel ? 'ACDF_4_PLUS' : 'ACDF_1_3';
    case 'cervical_laminectomy':
      return 'CERVICAL_LAMINECTOMY';
    case 'thoracic_laminectomy':
      return 'THORACIC_LAMINECTOMY';
    case 'lumbar_laminectomy':
      return multilevel ? 'LUMBAR_LAMIS_4_PLUS' : 'LUMBAR_LAMIS_1_3';
    case 'lumbar_coflex':
      return 'COFLEX';
    case 'tlif':
      return multilevel ? 'OPEN_TLIF' : 'MINIMAL_ACCESS_TLIF';
    case 'cervical_laminoplasty':
      return 'LAMINOSPLASTY';
    case 'cervical_psf_instrumentation':
      return 'CERVICAL_FUSION';
    case 'thoracic_psf_instrumentation':
      return 'THORACIC_FUSION';
    case 'lumbar_psf_instrumentation':
    case 'in_situ_fusion':
    case 'standalone_alif':
    case 'alif_with_psf':
    case 'olif':
    case 'xlif':
    case 'plif':
      return 'LUMBAR_FUSION';
    case 'cervical_scs':
    case 'thoracic_scs':
    case 'lumbar_scs':
      return 'SPINAL_CORD_STIMULATOR';
    case 'cervical_hemilaminotomy_foraminotomy':
    case 'lumbar_hemilaminotomy_foraminotomy':
    case 'hemilaminotomy_micro_discectomy':
    case 'hemilaminotomy_micro_discectomy_barricade':
      return 'DISCECTOMY_FORAMINOTOMY';
    default:
      return String(procedureKey || 'SURGERY').toUpperCase();
  }
}

function defaultDecideSurgeryDate() {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function formatMedManagementSurgeryLabel(token) {
  return String(token || '').replace(/_/g, ' ');
}

/** Typical adult maintenance daily dosages for the antithrombotic list. */
const DEFAULT_DAILY_DOSAGE_BY_MEDICATION = {
  APIXABAN: '5 mg twice daily',
  ASPIRIN: '162 mg',
  CLOPIDOGREL: '75 mg',
  DABIGATRAN_ETEXILATE: '150 mg twice daily',
  EDOXABAN: '60 mg',
  PRASUGREL: '10 mg',
  RIVAROXABAN: '20 mg',
  TICAGRELOR: '90 mg twice daily',
  WARFARIN: '5 mg',
};

function defaultDailyDosageForMedication(token) {
  return DEFAULT_DAILY_DOSAGE_BY_MEDICATION[token] || '';
}

let decideMedRowSeq = 0;
function newDecideMedRow(overrides = {}) {
  decideMedRowSeq += 1;
  return { id: `decide-med-${decideMedRowSeq}`, token: '', dosage: '', ...overrides };
}

function recommendationInputList(recommendation) {
  let inputs = recommendation?.inputs;
  if (typeof inputs === 'string') {
    try {
      inputs = JSON.parse(inputs);
    } catch {
      return [];
    }
  }
  return Array.isArray(inputs) ? inputs : [];
}

/** Antithrombotics already on the recommendation/chart, with dosage when present. */
function chartAntithromboticsFromRecommendation(recommendation) {
  const dosageByToken = new Map();

  const parsedMeds = recommendation?.parsed_output?.medications;
  if (parsedMeds && typeof parsedMeds === 'object' && !Array.isArray(parsedMeds)) {
    const merged = mergeEquivalentMedicationsForDisplay(parsedMeds);
    Object.entries(merged).forEach(([name, details]) => {
      const token = canonicalMedicationKey(formatMedicationKey(name));
      if (!ANTITHROMBOTIC_MEDICATIONS.includes(token)) return;
      const dosage = details?.dosage != null ? String(details.dosage).trim() : '';
      dosageByToken.set(token, dosage);
    });
  }

  recommendationInputList(recommendation).forEach((item) => {
    if (!item?.medication) return;
    const token = canonicalMedicationKey(formatMedicationKey(item.medication));
    if (!ANTITHROMBOTIC_MEDICATIONS.includes(token)) return;
    const dosage = item?.dosage != null ? String(item.dosage).trim() : '';
    if (!dosageByToken.has(token)) {
      dosageByToken.set(token, dosage);
      return;
    }
    if (!dosageByToken.get(token) && dosage) {
      dosageByToken.set(token, dosage);
    }
  });

  return ANTITHROMBOTIC_MEDICATIONS.filter((token) => dosageByToken.has(token)).map((token) => ({
    token,
    dosage: dosageByToken.get(token) || '',
  }));
}

function seedDecideMedicationRows(chartMeds) {
  const rows = (chartMeds || []).map((med) =>
    newDecideMedRow({
      token: med.token,
      dosage: med.dosage || '',
    })
  );
  return [...rows, newDecideMedRow()];
}

function availableDecideMedicationOptions(rows, currentId, currentToken) {
  const usedTokens = new Set(
    (rows || [])
      .filter((row) => row.id !== currentId && row.token)
      .map((row) => row.token),
  );
  return ANTITHROMBOTIC_MEDICATIONS.filter(
    (token) => token === currentToken || !usedTokens.has(token),
  );
}

function decideMedicationNoteChanges(chartMeds, rows) {
  const originalByToken = new Map(
    (chartMeds || []).map((med) => [med.token, String(med.dosage || '').trim()])
  );
  const currentFilled = (rows || []).filter((row) => row.token);
  const currentByToken = new Map(
    currentFilled.map((row) => [row.token, String(row.dosage || '').trim()])
  );

  const added = [];
  const removed = [];

  originalByToken.forEach((dosage, token) => {
    if (!currentByToken.has(token)) {
      removed.push({ token, dosage });
      return;
    }
    if (currentByToken.get(token) !== dosage) {
      removed.push({ token, dosage });
      added.push({ token, dosage: currentByToken.get(token) });
    }
  });

  currentByToken.forEach((dosage, token) => {
    if (!originalByToken.has(token)) {
      added.push({ token, dosage });
    }
  });

  return { added, removed };
}

function clinicalFactsFromRecommendation(recommendation) {
  const facts = { diagnoses: [], chiefComplaint: null, previousSpineSurgeries: null };
  recommendationInputList(recommendation).forEach((item) => {
    const variable = item?.variable;
    const value = item?.value;
    if (!variable || value == null || value === '') return;
    if (variable === 'Diagnosis') {
      if (Array.isArray(value)) {
        value.forEach((entry) => {
          if (entry) facts.diagnoses.push(String(entry));
        });
      } else {
        facts.diagnoses.push(String(value));
      }
    } else if (variable === 'Chief complaint preop') {
      facts.chiefComplaint = String(value);
    } else if (variable === 'Number of previous spine surgeries') {
      facts.previousSpineSurgeries = String(value);
    }
  });
  return facts;
}

function leftoverMedManagementSurgeryToken(selectedSurgeries) {
  const leftover = (selectedSurgeries || [])
    .map((surgeryKey) => {
      const parsed = parseLevelSeriesKey(surgeryKey);
      return mapJointDecisionsSurgeryToPeriop(parsed?.procedureKey || surgeryKey, parsed?.levels ?? 0);
    })
    .find((token) => MED_MANAGEMENT_SURGERIES.includes(token));
  return leftover || MED_MANAGEMENT_SURGERIES[0] || '';
}

function isMultilevel(levels) {
  return Number(levels) === 1;
}

function profileProcedureKey(profile) {
  if (!profile) return null;
  if (profile.procedureKey) return profile.procedureKey;
  return parseLevelSeriesKey(profile.surgery)?.procedureKey || profile.surgery || null;
}

function surgeryStripePatternId(procedureKey) {
  return `jd-stripe-${String(procedureKey || 'x').replace(/[^a-zA-Z0-9_-]/g, '_')}`;
}

/** Diagonal hatch over surgery color — used for 3+ level bars. */
function ensureSurgeryStripePattern(svgSelection, procedureKey, color) {
  const id = surgeryStripePatternId(procedureKey);
  let defs = svgSelection.select('defs');
  if (defs.empty()) defs = svgSelection.append('defs');
  if (!defs.select(`#${id}`).empty()) return `url(#${id})`;

  const pattern = defs
    .append('pattern')
    .attr('id', id)
    .attr('patternUnits', 'userSpaceOnUse')
    .attr('width', 8)
    .attr('height', 8)
    .attr('patternTransform', 'rotate(45)');
  pattern
    .append('rect')
    .attr('width', 8)
    .attr('height', 8)
    .attr('fill', color);
  pattern
    .append('path')
    .attr('d', 'M0,0 L0,8')
    .attr('stroke', 'rgba(255,255,255,0.9)')
    .attr('stroke-width', 3.25);
  return `url(#${id})`;
}

function resolveBarFill(svgSelection, bar) {
  if (!bar) return '#6c757d';
  if (bar.striped && bar.procedureKey) {
    return ensureSurgeryStripePattern(svgSelection, bar.procedureKey, bar.fill || '#6c757d');
  }
  return bar.fill || '#6c757d';
}

function formatStrataRiskValue(raw) {
  if (raw == null || raw === '') return null;
  const text = String(raw).trim();
  if (!text || ['NA', 'N/A', '-', 'NONE'].includes(text.toUpperCase())) return null;
  return text;
}

function resolveStratumFilters({
  selectedBmi,
  resolvedBmiValue,
  selectedSmoking,
  selectedDiabetes,
  selectedAntithrombotic,
  selectedInsurance,
  selectedRevisionSurgery,
}) {
  const bmiLabel = selectedBmi || calculateBmiBucketFromBmi(resolvedBmiValue);
  return {
    bmi_bucket: bmiLabel ? bmiLabelToBucket(bmiLabel) : null,
    smoking: encodeSmokingForStratum(selectedSmoking),
    diabetes: encodeBucketFilter(selectedDiabetes),
    antithrombotic: encodeBucketFilter(selectedAntithrombotic),
    workers_comp: encodeInsuranceForStratum(selectedInsurance),
    revision_surgery: encodeBucketFilter(selectedRevisionSurgery) ?? 0,
    // levels come from each selected procedure series
    levels: null,
  };
}

function coerceFilterNumber(value) {
  if (value === undefined || value === null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function stratumFieldEquals(rowValue, filterValue) {
  if (filterValue == null) return true;
  const left = coerceFilterNumber(rowValue);
  const right = coerceFilterNumber(filterValue);
  if (left != null && right != null) return left === right;
  return rowValue === filterValue;
}

function stratumRowMatchesFilters(row, filters, { requireBmi = true } = {}) {
  if (!row) return false;
  if (requireBmi && filters.bmi_bucket != null && !stratumFieldEquals(row.bmi_bucket, filters.bmi_bucket)) {
    return false;
  }
  if (!stratumFieldEquals(row.smoking, filters.smoking)) return false;
  if (!stratumFieldEquals(row.diabetes, filters.diabetes)) return false;
  if (!stratumFieldEquals(row.antithrombotic, filters.antithrombotic)) return false;
  if (!stratumFieldEquals(row.workers_comp, filters.workers_comp)) return false;
  if (!stratumFieldEquals(row.revision_surgery, filters.revision_surgery)) return false;
  if (!stratumFieldEquals(row.levels, filters.levels)) return false;
  return true;
}

function filterSpannerStratumRows(rows, filters, { omit = [] } = {}) {
  return (rows || []).filter((row) => {
    const effective = { ...filters };
    omit.forEach((key) => {
      effective[key] = null;
    });
    const requireBmi = !omit.includes('bmi_bucket');
    return stratumRowMatchesFilters(row, effective, { requireBmi });
  });
}

function uniqueBmiOptionsFromSpannerRows(rows, filters) {
  const partialRows = filterSpannerStratumRows(rows, filters, { omit: ['bmi_bucket'] });
  const labels = [];
  const seen = new Set();
  partialRows.forEach((row) => {
    const label = bmiBucketToLabel(row.bmi_bucket);
    if (label && !seen.has(label)) {
      seen.add(label);
      labels.push(label);
    }
  });
  const ordered = SPREADSHEET_BMI_ORDER.filter((label) => seen.has(label));
  const extras = labels.filter((label) => !SPREADSHEET_BMI_ORDER.includes(label)).sort();
  return [...ordered, ...extras];
}

function rowsMatchingLevelRevision(procedureRows, filters) {
  return (procedureRows || []).filter((row) => {
    if (!stratumFieldEquals(row.levels, filters?.levels) && filters?.levels != null) return false;
    if (!stratumFieldEquals(row.revision_surgery, filters?.revision_surgery) && filters?.revision_surgery != null) {
      return false;
    }
    return true;
  });
}

function rowsMatchingLevels(procedureRows, levels) {
  if (levels == null) return procedureRows || [];
  return (procedureRows || []).filter((row) => {
    const rowLevels = coerceFilterNumber(row.levels);
    // Missing levels in sparse rows → treat as 1–2 (0), matching historical default.
    const effective = rowLevels == null ? 0 : rowLevels;
    return effective === Number(levels);
  });
}

/**
 * Prefer exact patient-factor match; fall back within the same levels×revision
 * pool so sparser regions (e.g. Lumbar) still list procedures.
 */
function pickSpannerStratumRow(procedureRows, filters) {
  if (!procedureRows?.length) return null;
  const pool = rowsMatchingLevelRevision(procedureRows, filters);
  // Never pick a row from a different levels/revision variant.
  if ((filters?.levels != null || filters?.revision_surgery != null) && pool.length === 0) {
    return null;
  }
  const searchPool = pool.length ? pool : procedureRows;

  if (filters?.bmi_bucket != null) {
    const exact = searchPool.find((row) => stratumRowMatchesFilters(row, filters, { requireBmi: true }));
    if (exact) return exact;
  }
  const partial = searchPool.find((row) => stratumRowMatchesFilters(row, filters, { requireBmi: false }));
  if (partial) return partial;
  return searchPool[0] || null;
}

function stratumRowToProfile(row, levels = null) {
  if (!row) {
    return {
      surgery: null,
      procedureKey: null,
      levels: levels ?? 0,
      risks: {},
      outcomes: { ...EMPTY_OUTCOMES },
      controls: {},
      matched: false,
    };
  }
  const risks = {};
  Object.entries(STRATA_SURGERY_RISK_FIELDS).forEach(([field, label]) => {
    if (field === 'non_union' && !nonUnionApplies(row.procedure_key)) return;
    const formatted = formatStrataRiskValue(row[field]);
    if (formatted != null) risks[label] = formatted;
  });
  const outcomes = { ...EMPTY_OUTCOMES };
  STRATA_OUTCOME_FIELDS.forEach((key) => {
    const n = Number(row[key]);
    outcomes[key] = Number.isFinite(n) ? n : null;
  });
  const controls = {};
  Object.entries(STRATA_CONTROL_RISK_FIELDS).forEach(([field, label]) => {
    const formatted = formatStrataRiskValue(row[field]);
    if (formatted != null) controls[label] = formatted;
  });
  const procedureKey = row.procedure_key;
  const levelValue = levels != null ? Number(levels) : Number(row.levels) || 0;
  return {
    surgery: makeLevelSeriesKey(procedureKey, levelValue),
    procedureKey,
    levels: levelValue,
    label: formatLevelSeriesLabel(procedureKey, levelValue),
    risks,
    outcomes,
    controls,
    matched: true,
  };
}

function buildRegionBundleFromSpannerStrata(stratumRows, regionKey, patientFilters) {
  const regionRows = (stratumRows || []).filter(
    (row) => row.procedure_key && procedureRegion(row.procedure_key) === regionKey,
  );
  const procedureKeys = [...new Set(regionRows.map((row) => row.procedure_key))];

  const profilesBySurgery = {};
  const availableSurgeries = [];

  procedureKeys.forEach((procedureKey) => {
    const procedureRows = regionRows.filter((row) => row.procedure_key === procedureKey);
    SPREADSHEET_LEVEL_SERIES.forEach(({ levels, shortLabel }) => {
      // Epidural / SCS cannot have levels — never offer them under 3+.
      if (Number(levels) === 1 && !procedureSupportsMultilevel(procedureKey)) return;
      // List a level whenever any rows exist for it; revision only affects which
      // stratum is matched for chart values (so Lumbar isn't wiped by sparse revision).
      const levelRows = rowsMatchingLevels(procedureRows, levels);
      if (!levelRows.length) return;
      const variantFilters = {
        ...patientFilters,
        levels,
      };
      const row =
        pickSpannerStratumRow(procedureRows, variantFilters) ||
        pickSpannerStratumRow(levelRows, { ...variantFilters, revision_surgery: null }) ||
        levelRows[0];
      if (!row) return;
      const key = makeLevelSeriesKey(procedureKey, levels);
      const profile = stratumRowToProfile(row, levels);
      profilesBySurgery[key] = profile;
      availableSurgeries.push({
        surgery: key,
        procedureKey,
        levels: Number(levels),
        label: profile.label,
        shortLabel,
        procedureLabel: getProcedureDisplayLabel(procedureKey),
      });
    });
  });

  availableSurgeries.sort((a, b) => {
    const byProcedure = compareProcedureKeys(a.procedureKey, b.procedureKey);
    if (byProcedure !== 0) return byProcedure;
    return a.levels - b.levels;
  });

  const referenceFilters = { ...patientFilters, levels: 0 };
  const referenceRow =
    pickSpannerStratumRow(regionRows, referenceFilters) ||
    pickSpannerStratumRow(regionRows, { ...patientFilters, levels: null });
  const spreadsheetControls = referenceRow ? stratumRowToProfile(referenceRow, 0).controls : {};

  return {
    available_surgeries: availableSurgeries,
    profiles_by_surgery: profilesBySurgery,
    spreadsheet_controls: spreadsheetControls,
    region_label:
      SPREADSHEET_REGION_OPTIONS.find((region) => region.value === regionKey)?.label || regionKey,
  };
}

function buildRegressionFactorRow({
  sexCode,
  ageBucket,
  patientFilters,
  procedureKey,
  levels,
}) {
  return {
    sex: Number(sexCode),
    age_bucket: Number(ageBucket),
    bmi_bucket: patientFilters?.bmi_bucket,
    smoking: patientFilters?.smoking,
    diabetes: patientFilters?.diabetes,
    antithrombotic: patientFilters?.antithrombotic ?? 0,
    workers_comp: patientFilters?.workers_comp ?? 0,
    revision_surgery: patientFilters?.revision_surgery ?? 0,
    levels: Number(levels) || 0,
    procedure_key: procedureKey,
  };
}

/**
 * Procedures regression may offer for a region: org catalog minus hidden.
 * Falls back to modern snake_case labels (never legacy v2 keys).
 */
function resolveOrgEnabledProcedureKeys(regionKey, procedureCatalog, hiddenProcedures) {
  const hidden = new Set(hiddenProcedures || []);
  const catalogKeys = (procedureCatalog || []).map((item) => item?.key).filter(Boolean);
  const source = catalogKeys.length
    ? catalogKeys
    : Object.keys(SPREADSHEET_SURGERY_DISPLAY_LABELS).filter(
        (key) => !key.includes(' ') && key === key.toLowerCase(),
      );
  return source.filter((key) => !hidden.has(key) && procedureRegion(key) === regionKey);
}

/** Same profile shape as strata lookup, values from OLS β·x. */
function buildRegionBundleFromRegression(
  models,
  regionKey,
  patientFilters,
  { sexCode, ageBucket, allowedProcedureKeys = null } = {},
) {
  if (!models?.outcomes || sexCode == null || ageBucket == null) return null;

  const procedureKeys = Array.isArray(allowedProcedureKeys)
    ? allowedProcedureKeys.filter((key) => procedureRegion(key) === regionKey)
    : Object.keys(SPREADSHEET_SURGERY_DISPLAY_LABELS).filter(
        (key) =>
          !key.includes(' ') &&
          key === key.toLowerCase() &&
          procedureRegion(key) === regionKey,
      );
  const profilesBySurgery = {};
  const availableSurgeries = [];

  procedureKeys.forEach((procedureKey) => {
    SPREADSHEET_LEVEL_SERIES.forEach(({ levels, shortLabel }) => {
      if (Number(levels) === 1 && !procedureSupportsMultilevel(procedureKey)) return;
      const factors = buildRegressionFactorRow({
        sexCode,
        ageBucket,
        patientFilters,
        procedureKey,
        levels,
      });
      const row = scoreRegressionStratumRow(models, factors);
      if (!row) return;
      const key = makeLevelSeriesKey(procedureKey, levels);
      const profile = stratumRowToProfile(row, levels);
      profilesBySurgery[key] = profile;
      availableSurgeries.push({
        surgery: key,
        procedureKey,
        levels: Number(levels),
        label: profile.label,
        shortLabel,
        procedureLabel: getProcedureDisplayLabel(procedureKey),
      });
    });
  });

  availableSurgeries.sort((a, b) => {
    const byProcedure = compareProcedureKeys(a.procedureKey, b.procedureKey);
    if (byProcedure !== 0) return byProcedure;
    return a.levels - b.levels;
  });

  const controlProcedure =
    availableSurgeries.find((item) => Number(item.levels) === 0)?.procedureKey ||
    procedureKeys[0];
  const controlRow = controlProcedure
    ? scoreRegressionStratumRow(
        models,
        buildRegressionFactorRow({
          sexCode,
          ageBucket,
          patientFilters,
          procedureKey: controlProcedure,
          levels: 0,
        }),
      )
    : null;
  const spreadsheetControls = controlRow ? stratumRowToProfile(controlRow, 0).controls : {};

  return {
    available_surgeries: availableSurgeries,
    profiles_by_surgery: profilesBySurgery,
    spreadsheet_controls: spreadsheetControls,
    region_label:
      SPREADSHEET_REGION_OPTIONS.find((region) => region.value === regionKey)?.label || regionKey,
  };
}

function isSpreadsheetFlag(value) {
  const n = Number(value);
  return Number.isFinite(n) && n >= 1;
}

function resolveInitialSmoking(patientProfile) {
  const smoking = patientProfile?.smoking;
  if (smoking === 'YES' || smoking === true) return 'YES';
  // Omitted smoking in conditions ⇒ assume non-smoker.
  return 'NO';
}

function resolveInitialDiabetes(patientProfile) {
  if (patientProfile?.diabetes_bucket != null && patientProfile?.diabetes_bucket !== '') {
    return String(patientProfile.diabetes_bucket);
  }
  if (patientProfile?.diabetes === true) return '2';
  // Omitted diabetes in conditions ⇒ assume no diabetes.
  return '0';
}

function resolveInitialAntithrombotic(patientProfile) {
  if (patientProfile?.antithrombotic != null && patientProfile?.antithrombotic !== '') {
    return String(patientProfile.antithrombotic);
  }
  return '';
}

function resolveInitialInsurance(patientProfile) {
  const raw = patientProfile?.insurance;
  if (!raw) return '';
  if (raw === 'workers_comp') return 'workers_comp';
  // Backend historically returns phi/auto for non–workers-comp; both map to Other.
  return 'other';
}

function resolveInitialRevisionSurgery(patientProfile) {
  if (patientProfile?.revision_surgery != null && patientProfile?.revision_surgery !== '') {
    return String(patientProfile.revision_surgery);
  }
  return '0';
}

function isBlankFilterValue(value) {
  return value === undefined || value === null || value === '';
}

const REQUIRED_FACTOR_LABELS = {
  bmi: 'BMI',
  antithrombotic: 'Antithrombotics',
  insurance: 'Insurance',
};

function collectMissingRequiredFactors({
  hasExactBmi,
  hasCompleteVitals,
  selectedBmi,
  selectedAntithrombotic,
  selectedInsurance,
}) {
  const missing = [];
  const hasBmi =
    hasExactBmi ||
    hasCompleteVitals ||
    !isBlankFilterValue(selectedBmi);
  if (!hasBmi) missing.push('bmi');
  if (isBlankFilterValue(selectedAntithrombotic)) missing.push('antithrombotic');
  if (isBlankFilterValue(selectedInsurance)) missing.push('insurance');
  return missing;
}

const SETUP_FILL_ORDER = ['height', 'weight', 'bmi', 'insurance', 'antithrombotic'];
const SETUP_SELECT_FACTORS = new Set(['insurance', 'antithrombotic']);

function collectIncompleteSetupFactors({
  heightFeetInput,
  heightInchesInput,
  weightInput,
  hasBmi,
  selectedInsurance,
  selectedAntithrombotic,
}) {
  const incomplete = [];
  if (isBlankFilterValue(heightFeetInput) && isBlankFilterValue(heightInchesInput)) {
    incomplete.push('height');
  }
  if (isBlankFilterValue(weightInput)) incomplete.push('weight');
  if (!hasBmi) incomplete.push('bmi');
  if (isBlankFilterValue(selectedInsurance)) incomplete.push('insurance');
  if (isBlankFilterValue(selectedAntithrombotic)) incomplete.push('antithrombotic');
  return incomplete;
}

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - ((-2 * t + 2) ** 3) / 2;
}

let setupScrollFrame = 0;

function animateScrollLeft(scroller, delta, durationMs) {
  if (!scroller || !delta) return;
  const from = scroller.scrollLeft;
  const to = from + delta;
  const start = performance.now();
  if (setupScrollFrame) cancelAnimationFrame(setupScrollFrame);
  const step = (now) => {
    const t = Math.min(1, (now - start) / durationMs);
    scroller.scrollLeft = from + (to - from) * easeInOutCubic(t);
    if (t < 1) {
      setupScrollFrame = requestAnimationFrame(step);
    } else {
      setupScrollFrame = 0;
    }
  };
  setupScrollFrame = requestAnimationFrame(step);
}

function nudgeScrollerToFactor(scroller, factorKey) {
  if (!scroller || !factorKey) return;
  const el = scroller.querySelector(`[data-jd-factor="${factorKey}"]`);
  if (!el) return;
  const scrollerRect = scroller.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();
  const pad = 20;
  let delta = 0;
  if (elRect.right > scrollerRect.right - pad) {
    delta = elRect.right - scrollerRect.right + pad;
  } else if (elRect.left < scrollerRect.left + pad) {
    delta = elRect.left - scrollerRect.left - pad;
  }
  if (!delta) return;
  // Native smooth scroll is ~400ms; 80% speed ⇒ 1.25× duration.
  animateScrollLeft(scroller, delta, 500);
}

function uniqueBmiOptionsFromRows(stratumRows) {
  const found = new Set(
    (stratumRows || []).map((r) => r.BMI).filter((b) => b && String(b).trim() && String(b).trim() !== '-'),
  );
  const ordered = SPREADSHEET_BMI_ORDER.filter((b) => found.has(b));
  const extras = [...found].filter((b) => !SPREADSHEET_BMI_ORDER.includes(b)).sort();
  return [...ordered, ...extras];
}

function resolveInitialBmi(patientProfile, bmiOptions) {
  const bucket = patientProfile?.bmi_bucket;
  if (bucket && bmiOptions.includes(bucket)) return bucket;
  return null;
}

function parsePositiveNumber(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function parseNonNegativeNumber(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** Convert total inches (chart vitals) into feet + remaining inches display parts. */
function splitInchesToFeetInches(totalInches) {
  const n = parsePositiveNumber(totalInches);
  if (n == null) return { feet: '', inches: '' };
  const rounded = Math.round(n);
  return {
    feet: String(Math.floor(rounded / 12)),
    inches: String(rounded % 12),
  };
}

/** Combine feet/inches inputs into total inches for BMI math. */
function totalInchesFromFeetInches(feet, inches) {
  const feetNum = parseNonNegativeNumber(feet);
  const inchesNum = parseNonNegativeNumber(inches);
  if (feetNum == null && inchesNum == null) return null;
  // Feet required for a clinical height; bare inches alone is treated as incomplete.
  if (feetNum == null) return null;
  const total = feetNum * 12 + (inchesNum ?? 0);
  return total > 0 ? total : null;
}

function calculateBmiFromVitals(height, weight) {
  const heightInches = parsePositiveNumber(height);
  const weightPounds = parsePositiveNumber(weight);
  if (!heightInches || !weightPounds) {
    return null;
  }

  return (weightPounds / (heightInches * heightInches)) * 703;
}

function calculateBmiBucketFromBmi(bmiValue) {
  const bmi = parsePositiveNumber(bmiValue);
  if (!bmi) return null;
  if (bmi < 18.5) return 'Less than 18.5';
  if (bmi < 25) return '18.5-24.9';
  if (bmi < 35) return '25.0-34.9';
  return '35.0 and over';
}

function calculateWeightFromBmi(height, bmi) {
  const heightInches = parsePositiveNumber(height);
  const bmiValue = parsePositiveNumber(bmi);
  if (!heightInches || !bmiValue) {
    return null;
  }
  return (bmiValue * heightInches * heightInches) / 703;
}

function formatVitalValue(value) {
  if (value === undefined || value === null || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return Number.isInteger(n) ? String(n) : String(n);
}

function formatCalculatedBmi(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return n.toFixed(1);
}

function formatCalculatedWeight(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return String(Math.round(n));
}

function pickStratumRow(rows, bmiBucket, smokingValue) {
  if (!rows?.length) return null;

  if (bmiBucket && smokingValue) {
    const exact = rows.find((r) => r.BMI === bmiBucket && r.SMOKING === smokingValue);
    if (exact) return exact;
  }
  if (bmiBucket) {
    const bmiMatch = rows.find((r) => r.BMI === bmiBucket);
    if (bmiMatch) return bmiMatch;
  }
  if (smokingValue) {
    const smokeMatch = rows.find((r) => r.SMOKING === smokingValue);
    if (smokeMatch) return smokeMatch;
  }
  return rows[0];
}

function buildRegionBundleFromStratumRows(stratumRows, regionKey, bmiBucket, smokingValue) {
  const regionRows = (stratumRows || []).filter((r) => isSpreadsheetFlag(r[regionKey]));
  const availableSurgeries = SPREADSHEET_SURGERY_KEYS.filter((surgery) =>
    regionRows.some((r) => isSpreadsheetFlag(r[surgery])),
  )
    .map((surgery) => ({
      surgery,
      label: SPREADSHEET_SURGERY_DISPLAY_LABELS[surgery] || surgery,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const refRow = pickStratumRow(regionRows, bmiBucket, smokingValue);
  const spreadsheetControls = {};
  if (refRow) {
    Object.entries(SPREADSHEET_CONTROL_CSV_TO_LABEL).forEach(([csvCol, label]) => {
      const raw = refRow[csvCol];
      if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
        spreadsheetControls[label] = String(raw);
      }
    });
  }

  const profilesBySurgery = {};
  availableSurgeries.forEach(({ surgery, label }) => {
    const surgeryRows = regionRows.filter((r) => isSpreadsheetFlag(r[surgery]));
    const row = pickStratumRow(surgeryRows, bmiBucket, smokingValue);
    const risks = {};
    const outcomes = { ...EMPTY_OUTCOMES };
    if (row) {
      Object.entries(SPREADSHEET_RISK_CSV_TO_LABEL).forEach(([csvCol, riskLabel]) => {
        const raw = row[csvCol];
        if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
          risks[riskLabel] = String(raw);
        }
      });
      Object.entries(SPREADSHEET_OUTCOME_CSV_TO_KEY).forEach(([csvCol, key]) => {
        outcomes[key] = parseOutcomeNumber(row[csvCol]);
      });
    }
    profilesBySurgery[surgery] = {
      surgery,
      label,
      risks,
      outcomes,
      matched: Boolean(row),
    };
  });

  const regionLabel =
    SPREADSHEET_REGION_OPTIONS.find((r) => r.value === regionKey)?.label || regionKey;

  return {
    region: regionKey,
    region_label: regionLabel,
    available_surgeries: availableSurgeries,
    profiles_by_surgery: profilesBySurgery,
    spreadsheet_controls: spreadsheetControls,
  };
}

function StratumFilterSelect({
  label,
  value,
  onChange,
  options,
  ariaLabel,
  minWidth = '7.5rem',
  selectClassName = '',
  inlineNote = null,
  isMissing = false,
  allowBlank = false,
  blankLabel = '',
  factorKey = null,
}) {
  const showBlank = allowBlank || isMissing || isBlankFilterValue(value);
  return (
    <div
      className={`jd-compact-filter ${isMissing ? 'jd-compact-filter--missing' : ''}`}
      data-jd-factor={factorKey || undefined}
    >
      <span className="jd-stratum-label">{label}</span>
      <Form.Select
        size="sm"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: 'auto', minWidth }}
        aria-label={ariaLabel}
        disabled={!options.length}
        className={selectClassName}
      >
        {showBlank && (
          <option value="">{blankLabel}</option>
        )}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Form.Select>
      {inlineNote && <span className="jd-filter-inline-note">{inlineNote}</span>}
    </div>
  );
}

function FactorChangePill({ action, onClear }) {
  if (!action) return null;
  return (
    <span className="jd-factor-change-pill">
      {action.chip}
      {onClear && (
        <button
          type="button"
          className="jd-factor-change-pill-clear"
          aria-label={`Revert ${action.chip}`}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onClear(action);
          }}
        >
          <X size={10} aria-hidden />
        </button>
      )}
    </span>
  );
}

function FactorModeToggle({ exploreSelected, onExplore, onCorrect, describedBy }) {
  return (
    <div
      className="jd-factor-mode-toggle"
      role="group"
      aria-label="Patient factor mode"
      aria-describedby={describedBy}
    >
      <button
        type="button"
        className={`jd-factor-mode-toggle-btn${!exploreSelected ? ' jd-factor-mode-toggle-btn--active' : ''}`}
        aria-pressed={!exploreSelected}
        title="Use this tab to alter the baseline characteristics of the patient."
        onClick={onCorrect}
      >
        Patient chart
      </button>
      <button
        type="button"
        className={`jd-factor-mode-toggle-btn${exploreSelected ? ' jd-factor-mode-toggle-btn--active' : ''}`}
        aria-pressed={exploreSelected}
        title="Use this tab to explore pre operative improvements"
        onClick={onExplore}
      >
        Explore improvements
      </button>
    </div>
  );
}

function ModifiableActionChip({ action, applied, onClick }) {
  if (!action) return null;
  return (
    <button
      type="button"
      className={`jd-modifiable-action-chip${applied ? ' jd-modifiable-action-chip--applied' : ''}`}
      aria-pressed={applied}
      title={applied ? `Undo ${action.title}` : action.title}
      onClick={() => onClick(action)}
    >
      {action.title}
    </button>
  );
}

function formatWeightDeltaChip(deltaLbs) {
  if (!Number.isFinite(deltaLbs) || deltaLbs === 0) return null;
  const rounded = Math.round(deltaLbs);
  if (rounded === 0) return null;
  return `${rounded > 0 ? '+' : ''}${rounded} lb`;
}

/** When height is unknown, infer weight from BMI ratio against a baseline. */
function scaleWeightForBmiChange(baselineWeight, baselineBmi, nextBmi) {
  const weight = parsePositiveNumber(baselineWeight);
  const fromBmi = parsePositiveNumber(baselineBmi);
  const toBmi = parsePositiveNumber(nextBmi);
  if (weight == null || fromBmi == null || toBmi == null || fromBmi === 0) return null;
  return (weight * toBmi) / fromBmi;
}

function VitalFactorInput({
  label,
  value,
  unit,
  onChange,
  ariaLabel,
  inputClassName = '',
  isMissing = false,
  afterUnit = null,
  factorKey = null,
}) {
  const isUnknown = isMissing || value === undefined || value === null || value === '';
  return (
    <label
      className={`jd-vital-factor ${isUnknown ? 'jd-vital-factor--unknown' : ''}`}
      data-jd-factor={factorKey || undefined}
    >
      <span className="jd-vital-factor-label">{label}</span>
      <input
        type="number"
        min="0"
        step="0.1"
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        placeholder=""
        aria-label={ariaLabel}
        className={inputClassName}
      />
      {unit && <span className="jd-vital-factor-unit">{unit}</span>}
      {afterUnit}
    </label>
  );
}

function HeightFactorInput({ feet, inches, onFeetChange, onInchesChange, factorKey = 'height' }) {
  const isUnknown =
    (feet === undefined || feet === null || feet === '') &&
    (inches === undefined || inches === null || inches === '');
  return (
    <div
      className={`jd-vital-factor jd-height-factor ${isUnknown ? 'jd-vital-factor--unknown' : ''}`}
      data-jd-factor={factorKey}
    >
      <span className="jd-vital-factor-label">Height</span>
      <input
        type="number"
        min="0"
        max="8"
        step="1"
        value={feet ?? ''}
        onChange={(event) => onFeetChange(event.target.value)}
        placeholder=""
        aria-label="Patient height feet"
        className="jd-vital-factor-input--height-ft"
      />
      <span className="jd-vital-factor-unit">ft</span>
      <input
        type="number"
        min="0"
        max="11"
        step="1"
        value={inches ?? ''}
        onChange={(event) => onInchesChange(event.target.value)}
        placeholder=""
        aria-label="Patient height inches"
        className="jd-vital-factor-input--height-in"
      />
      <span className="jd-vital-factor-unit">in</span>
    </div>
  );
}

function parseOutcomeNumber(raw) {
  if (raw === undefined || raw === null || raw === '') return null;
  const text = String(raw).replace(/%/g, '').trim();
  if (!text || /^n\/?a$/i.test(text) || text === '-') return null;
  const n = parseFloat(text);
  return Number.isFinite(n) ? n : null;
}

function normalizeProfileOutcomes(profile) {
  const o = profile?.outcomes && typeof profile.outcomes === 'object' ? profile.outcomes : {};
  const merged = { ...EMPTY_OUTCOMES };
  Object.keys(EMPTY_OUTCOMES).forEach((k) => {
    merged[k] = parseOutcomeNumber(o[k]);
  });
  return merged;
}

function parsePct(raw) {
  if (raw === undefined || raw === null || raw === '') return null;
  const text = String(raw).replace(/%/g, '').trim();
  if (!text || /^n\/?a$/i.test(text) || text === '-') return null;
  const n = parseFloat(text);
  return Number.isFinite(n) ? n : null;
}

function normalizeBarPct(raw, matched = true) {
  // Unmatched / not-applicable stay null so labels render as N/A (not ~.01).
  if (!matched) return null;
  const p = parsePct(raw);
  return p != null && Number.isFinite(p) ? p : null;
}

/** Medical post-surgery rates must not fall below the shared control rate. */
function floorMedicalRiskPctAtControl(riskLabel, surgeryPct, controlPct) {
  if (!MEDICAL_RISK_LABEL_SET.has(riskLabel)) return surgeryPct;
  if (surgeryPct == null || !Number.isFinite(surgeryPct)) return surgeryPct;
  if (controlPct == null || !Number.isFinite(controlPct)) return surgeryPct;
  return Math.max(surgeryPct, controlPct);
}

function floorProfileMedicalRisksToControl(profile, controls) {
  if (!profile?.risks || !controls) return profile;
  const risks = { ...profile.risks };
  let changed = false;
  MEDICAL_RISK_LABELS.forEach((label) => {
    const surgery = parsePct(risks[label]);
    const control = parsePct(controls[label]);
    if (surgery == null || control == null || !(surgery < control)) return;
    risks[label] = controls[label];
    changed = true;
  });
  return changed ? { ...profile, risks } : profile;
}

function buildSlotsForRisk(riskLabel, spreadsheetControls, profiles, includeControl = true) {
  const controlPct = normalizeBarPct(spreadsheetControls?.[riskLabel], true);
  const slots = includeControl
    ? [
        {
          key: 'control',
          shortLabel: SPREADSHEET_CONTROL_LABEL,
          pct: controlPct,
          fill: '#6c757d',
          opacity: 0.85,
        },
      ]
    : [];

  profiles.forEach((profile) => {
    const raw = profile?.risks?.[riskLabel];
    const procedureKey = profileProcedureKey(profile);
    const levels = profile.levels ?? parseLevelSeriesKey(profile.surgery)?.levels ?? 0;
    const surgeryPct = normalizeBarPct(raw, profile?.matched !== false);
    slots.push({
      key: `sx__${profile.surgery}`,
      shortLabel: profile.label || formatLevelSeriesLabel(procedureKey, levels),
      pct: floorMedicalRiskPctAtControl(riskLabel, surgeryPct, controlPct),
      fill: getSpreadsheetSurgeryColor(procedureKey),
      procedureKey,
      striped: isMultilevel(levels),
      opacity: profile?.matched ? 1 : 0.35,
    });
  });

  return slots;
}

/** Inner bar keys shared by every complication group (skinny grouped bars). */
function buildX1Domain(profiles, includeControl = true) {
  return [...(includeControl ? ['control'] : []), ...profiles.map((p) => `sx__${p.surgery}`)];
}

function optionLabel(options, value, fallback = '') {
  const option = options.find((item) => String(item.value) === String(value));
  return option?.label || fallback || String(value ?? '');
}

function smokingStratumToOptionValue(value) {
  if (value === 1 || value === '1') return 'YES';
  if (value === 0 || value === '0') return 'NO';
  return value;
}

function modifiableActionLabel(field, fromValue, toValue) {
  switch (field) {
    case 'smoking':
      return `${optionLabel(SPREADSHEET_SMOKING_OPTIONS, fromValue)} -> ${optionLabel(SPREADSHEET_SMOKING_OPTIONS, toValue)}`;
    case 'bmi_bucket':
      return `${bmiBucketToVerbalLabel(fromValue) || 'current'} -> ${bmiBucketToVerbalLabel(toValue) || 'target'}`;
    case 'diabetes':
      return `${optionLabel(SPREADSHEET_DIABETES_OPTIONS, fromValue)} -> ${optionLabel(SPREADSHEET_DIABETES_OPTIONS, toValue)}`;
    case 'antithrombotic':
      return `${optionLabel(SPREADSHEET_ANTITHROMBOTIC_OPTIONS, fromValue)} -> ${optionLabel(SPREADSHEET_ANTITHROMBOTIC_OPTIONS, toValue)}`;
    default:
      return `${fromValue} -> ${toValue}`;
  }
}

function factorFilterChanged(fromValue, toValue) {
  const left = coerceFilterNumber(fromValue);
  const right = coerceFilterNumber(toValue);
  if (left != null || right != null) return left !== right;
  return fromValue !== toValue;
}

function buildCurrentFactorChangeActions(baselineFilters, currentFilters) {
  if (!baselineFilters || !currentFilters) return [];
  const actions = [];

  if (factorFilterChanged(baselineFilters.bmi_bucket, currentFilters.bmi_bucket)) {
    actions.push({
      key: 'current-bmi',
      field: 'bmi_bucket',
      target: 'bmi',
      chip: modifiableActionLabel('bmi_bucket', baselineFilters.bmi_bucket, currentFilters.bmi_bucket),
    });
  }
  if (factorFilterChanged(baselineFilters.smoking, currentFilters.smoking)) {
    actions.push({
      key: 'current-smoking',
      field: 'smoking',
      target: 'smoking',
      chip: modifiableActionLabel(
        'smoking',
        smokingStratumToOptionValue(baselineFilters.smoking),
        smokingStratumToOptionValue(currentFilters.smoking),
      ),
    });
  }
  if (factorFilterChanged(baselineFilters.diabetes, currentFilters.diabetes)) {
    actions.push({
      key: 'current-diabetes',
      field: 'diabetes',
      target: 'diabetes',
      chip: modifiableActionLabel('diabetes', baselineFilters.diabetes, currentFilters.diabetes),
    });
  }
  if (factorFilterChanged(baselineFilters.antithrombotic, currentFilters.antithrombotic)) {
    actions.push({
      key: 'current-antithrombotic',
      field: 'antithrombotic',
      target: 'antithrombotic',
      chip: modifiableActionLabel(
        'antithrombotic',
        baselineFilters.antithrombotic,
        currentFilters.antithrombotic,
      ),
    });
  }

  return actions;
}

function changeActionForTarget(actions, target) {
  return (actions || []).find((action) => action.target === target) || null;
}

const HEALTHY_BMI_BUCKET_INDEX = 1;
/** Range edges: increase lands at LOW, decrease lands at HIGH. */
const BMI_BUCKET_LOW = [16, 18.5, 25, 35];
const BMI_BUCKET_HIGH = [18.4, 24.9, 34.9, 40];

function representativeBmiForBucket(bucketIndex, fromIndex = null) {
  const idx = Number(bucketIndex);
  if (!Number.isInteger(idx) || idx < 0 || idx >= BMI_BUCKET_HIGH.length) return null;
  const from = Number(fromIndex);
  const decreasing = Number.isInteger(from) && idx < from;
  return decreasing ? BMI_BUCKET_HIGH[idx] : BMI_BUCKET_LOW[idx];
}

function actionPatchIsApplied(action, filters) {
  if (!action?.patch || !filters) return false;
  return Object.entries(action.patch).every(
    ([key, value]) => !factorFilterChanged(filters[key], value),
  );
}

function flattenModifiableActions(groups) {
  return (groups || []).flatMap((group) => group.actions || []);
}

function bmiActionTitle(fromIndex, targetIndex) {
  const verb = targetIndex < fromIndex ? 'Lower' : 'Raise';
  const target = (bmiBucketToVerbalLabel(targetIndex) || bmiBucketToLabel(targetIndex) || 'target').toLowerCase();
  return `${verb} BMI to ${target}`;
}

function buildModifiableActionGroups(filters, bmiOptions = []) {
  const groups = [];
  if (!filters) return groups;

  if (filters.smoking === 1) {
    groups.push({
      key: 'smoking',
      label: 'Smoking',
      actions: [
        {
          key: 'quit-smoking',
          title: 'Quit smoking',
          field: 'smoking',
          patch: { smoking: 0 },
          chip: modifiableActionLabel('smoking', 'YES', 'NO'),
        },
      ],
    });
  }

  const bmiIndex = Number.isInteger(filters.bmi_bucket) ? filters.bmi_bucket : null;
  const availableBmiLabels = new Set((bmiOptions || []).map((item) => item.value || item.label));
  if (bmiIndex != null && bmiIndex !== HEALTHY_BMI_BUCKET_INDEX) {
    const direction = bmiIndex > HEALTHY_BMI_BUCKET_INDEX ? -1 : 1;
    const bmiActions = [];
    for (
      let targetIndex = bmiIndex + direction;
      direction > 0 ? targetIndex <= HEALTHY_BMI_BUCKET_INDEX : targetIndex >= HEALTHY_BMI_BUCKET_INDEX;
      targetIndex += direction
    ) {
      const targetLabel = bmiBucketToLabel(targetIndex);
      if (availableBmiLabels.size && !availableBmiLabels.has(targetLabel)) continue;
      bmiActions.push({
        key: `bmi-${targetIndex}`,
        title: bmiActionTitle(bmiIndex, targetIndex),
        field: 'bmi_bucket',
        fromBucket: bmiIndex,
        patch: { bmi_bucket: targetIndex },
        chip: modifiableActionLabel('bmi_bucket', bmiIndex, targetIndex),
      });
    }
    if (bmiActions.length) {
      groups.push({
        key: 'bmi',
        label: 'BMI',
        actions: bmiActions,
      });
    }
  }

  if (filters.diabetes === 2 || filters.diabetes === 1) {
    groups.push({
      key: 'diabetes',
      label: 'Diabetes',
      actions: [
        {
          key: 'diabetes-to-none',
          title: filters.diabetes === 1 ? 'Resolve prediabetes' : 'Resolve diabetes',
          field: 'diabetes',
          patch: { diabetes: 0 },
          chip: modifiableActionLabel('diabetes', filters.diabetes, 0),
        },
      ],
    });
  }

  if (filters.antithrombotic === 2) {
    groups.push({
      key: 'antithrombotic',
      label: 'Antithrombotics',
      actions: [
        {
          key: 'manage-antithrombotics',
          title: 'Manage antithrombotics',
          field: 'antithrombotic',
          patch: { antithrombotic: 1 },
          chip: modifiableActionLabel('antithrombotic', filters.antithrombotic, 1),
        },
      ],
    });
  }

  return groups;
}

function buildSelectedProfilesFromBundle(regionBundle, selectedSurgeries, availableSurgeries) {
  const by = regionBundle?.profiles_by_surgery;
  if (!by || !selectedSurgeries?.length) return [];
  const controls = regionBundle?.spreadsheet_controls;
  return sortProfilesAlphabetically(
    selectedSurgeries.map((surgery) => {
      const row = by[surgery];
      const parsed = parseLevelSeriesKey(surgery);
      if (row) {
        const profile = {
          ...row,
          surgery,
          procedureKey: row.procedureKey || parsed?.procedureKey,
          levels: row.levels ?? parsed?.levels ?? 0,
          label:
            row.label ||
            formatLevelSeriesLabel(
              row.procedureKey || parsed?.procedureKey,
              row.levels ?? parsed?.levels ?? 0,
            ),
          outcomes: normalizeProfileOutcomes(row),
        };
        return floorProfileMedicalRisksToControl(profile, controls || row.controls);
      }
      const meta = (availableSurgeries || []).find((item) => item.surgery === surgery);
      const procedureKey = meta?.procedureKey || parsed?.procedureKey || surgery;
      const levels = meta?.levels ?? parsed?.levels ?? 0;
      return {
        surgery,
        procedureKey,
        levels,
        label: meta?.label || formatLevelSeriesLabel(procedureKey, levels),
        risks: {},
        outcomes: { ...EMPTY_OUTCOMES },
        matched: false,
      };
    }),
  );
}

const ALL_OUTCOMES_CHART_SIZE = {
  compact: {
    totalHeight: 340,
    margin: { top: 44, right: 20, bottom: 78, left: 56 },
    innerMinPerEntry: 120,
    axisFont: '14px',
    categoryAxisFont: '13px',
    pointVal: '14px',
    diffFont: '15px',
    tipRadius: 9,
    lineWidth: 2.5,
    yTicks: 5,
  },
  expanded: {
    totalHeight: 460,
    margin: { top: 48, right: 32, bottom: 88, left: 76 },
    innerMinPerEntry: 160,
    axisFont: '20px',
    categoryAxisFont: '14px',
    pointVal: '18px',
    diffFont: '20px',
    tipRadius: 12,
    lineWidth: 3.25,
    yTicks: 7,
  },
};

function fontPx(fontSize) {
  const px = parseFloat(fontSize);
  return Number.isFinite(px) ? px : 12;
}

function formatFontPx(px) {
  return `${px.toFixed(px % 1 === 0 ? 0 : 1)}px`;
}

function fitFontToSpace(baseFontSize, availablePx, minPx = 10, ratio = 0.72) {
  const basePx = fontPx(baseFontSize);
  const spacePx = Number(availablePx);
  if (!Number.isFinite(spacePx) || spacePx <= 0) return formatFontPx(basePx);
  return formatFontPx(Math.max(minPx, Math.min(basePx, spacePx * ratio)));
}

function fitFontToText(baseFontSize, availablePx, text, minPx = 8, charWidthRatio = 0.56) {
  const basePx = fontPx(baseFontSize);
  const spacePx = Number(availablePx);
  const charCount = String(text ?? '').length || 1;
  if (!Number.isFinite(spacePx) || spacePx <= 0) return formatFontPx(basePx);
  return formatFontPx(Math.max(minPx, Math.min(basePx, spacePx / (charCount * charWidthRatio))));
}

/**
 * Shrink value labels as digit count grows (e.g. 1.20 smaller than .12).
 * Length ≤ refDigits keeps base size; each extra digit multiplies by perDigit.
 */
function scaleFontByLabelDigits(
  baseFontSize,
  text,
  { minPx = 8, refDigits = 2, perDigit = 0.88 } = {},
) {
  const basePx = fontPx(baseFontSize);
  const digitCount = (String(text ?? '').match(/\d/g) || []).length || 1;
  if (!(basePx > 0) || digitCount <= refDigits) return formatFontPx(basePx);
  const scaled = basePx * perDigit ** (digitCount - refDigits);
  return formatFontPx(Math.max(minPx, scaled));
}

/** N/A caps read larger than digit labels at the same px — shrink slightly to match. */
function metricBarLabelFont(baseFontSize, text) {
  const basePx = fontPx(baseFontSize);
  if (!(basePx > 0)) return baseFontSize;
  const normalized = String(text ?? '').trim().toUpperCase();
  if (normalized === 'N/A' || normalized === 'NA') {
    return formatFontPx(basePx * 0.82);
  }
  return formatFontPx(basePx);
}

/** Stacked rate change label: current, down-arrow, new value (center-aligned). */
function appendVerticalRateArrowLabel(
  parent,
  {
    x,
    y,
    fromText,
    toText,
    fontSize,
    fill,
    align = 'center',
    className = 'bar-label jd-scenario-arrow-label',
  },
) {
  const sizePx = fontPx(fontSize);
  const lineHeight = sizePx * 1.12;
  const lines = [fromText, '↓', toText];
  const startY = align === 'bottom' ? y - 2 * lineHeight : y - lineHeight;
  const text = parent
    .append('text')
    .attr('class', className)
    .attr('x', x)
    .attr('y', startY)
    .attr('text-anchor', 'middle')
    .attr('font-size', fontSize)
    .attr('font-weight', 700);
  lines.forEach((line, index) => {
    text
      .append('tspan')
      .attr('x', x)
      .attr('dy', index === 0 ? 0 : lineHeight)
      .attr('text-anchor', 'middle')
      // Keep the arrow visually centered between the two values.
      .attr(
        'font-size',
        index === 1 ? formatFontPx(sizePx * 1.15) : metricBarLabelFont(fontSize, line),
      )
      .attr('fill', index === 0 ? JD_TEXT : fill)
      .text(line);
  });
  return text;
}

function buildOutcomesChartEntries(
  profiles,
  priorKey,
  afterKey,
  practiceChangeScale = null,
  outcomeMax = Infinity,
  practiceStats = null,
) {
  return profiles.map((profile) => {
    const o = normalizeProfileOutcomes(profile);
    const procedureKey = profileProcedureKey(profile);
    const levels = profile.levels ?? parseLevelSeriesKey(profile.surgery)?.levels ?? 0;
    const color = getSpreadsheetSurgeryColor(procedureKey);
    const researchPrior = normalizeOutcomeForChart(o[priorKey], priorKey);
    const researchAfter = normalizeOutcomeForChart(o[afterKey], afterKey);
    const practicePrior = practiceStats
      ? practiceBenefitMean(practiceStats, procedureKey, priorKey)
      : null;
    const practiceAfter = practiceStats
      ? practiceBenefitMean(practiceStats, procedureKey, afterKey)
      : null;
    const prior = practiceStats ? practicePrior : (researchPrior ?? 0);
    const scaledAfter =
      !practiceStats && practiceChangeScale != null
        ? scaleOutcomeChangeForPractice(researchPrior, researchAfter, practiceChangeScale, {
            min: 0,
            max: outcomeMax,
          })
        : null;
    const after = practiceStats
      ? practiceAfter
      : (scaledAfter != null ? scaledAfter : (researchAfter ?? 0));
    return {
      surgery: profile.surgery,
      name: profile.label || formatLevelSeriesLabel(procedureKey, levels),
      axisLabel: formatSurgeryChartAxisLabel(procedureKey, profile.label),
      procedureKey,
      striped: isMultilevel(levels),
      color,
      prior,
      after,
    };
  });
}

function formatOutcomeValue(value) {
  if (value == null || value === '') return 'N/A';
  const v = Number(value);
  if (!Number.isFinite(v)) return 'N/A';
  if (v === 0) return '~.01';
  return formatChartDecimal(v, v >= 100 ? 0 : 1);
}

function tipFillForEntry(svgSelection, entry) {
  return resolveBarFill(svgSelection, {
    fill: entry.color,
    striped: entry.striped,
    procedureKey: entry.procedureKey,
  });
}

/** Benefits-only: staggered Pre→Post line per surgery so segments do not overlap. */
function AllProceduresOutcomesLineChart({
  profiles,
  scenarioProfiles = [],
  priorKey,
  afterKey,
  priorAxisLabel = 'Pre',
  afterAxisLabel = 'Post',
  yHintMax,
  expanded = false,
  chartOrientation = 'vertical',
  fillAvailable = false,
  scenarioImprovementThreshold = null,
  practiceOutcomeChangeScale = null,
  practiceStats = null,
}) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const sizeKey = expanded ? 'expanded' : 'compact';
  const size = ALL_OUTCOMES_CHART_SIZE[sizeKey];

  const chartModel = useMemo(
    () => buildOutcomesChartEntries(
      profiles,
      priorKey,
      afterKey,
      practiceOutcomeChangeScale,
      yHintMax,
      practiceStats,
    ),
    [profiles, priorKey, afterKey, practiceOutcomeChangeScale, yHintMax, practiceStats],
  );
  const scenarioChartBySurgery = useMemo(
    () => new Map(
      buildOutcomesChartEntries(
        scenarioProfiles || [],
        priorKey,
        afterKey,
        practiceOutcomeChangeScale,
        yHintMax,
        practiceStats,
      ).map((entry) => [entry.surgery, entry]),
    ),
    [scenarioProfiles, priorKey, afterKey, practiceOutcomeChangeScale, yHintMax, practiceStats],
  );

  useEffect(() => {
    if (!chartModel.length || !profiles.length) {
      d3.select(svgRef.current).selectAll('*').remove();
      return undefined;
    }

    const draw = () => {
      const margin = { ...size.margin };
      const prePostLineCount = Math.max(
        1,
        splitRiskAxisLabelTokens(priorAxisLabel).length,
        splitRiskAxisLabelTokens(afterAxisLabel).length,
      );
      const prePostLineHeight = expanded ? 14 : 13;
      if (chartOrientation === 'horizontal') {
        margin.left += expanded ? 118 : 96;
      } else {
        margin.bottom += Math.max(0, prePostLineCount - 1) * prePostLineHeight;
      }

      const containerWidth = containerRef.current?.clientWidth || 720;
      const innerMin = chartModel.length * size.innerMinPerEntry + 80;
      const totalWidth = fillAvailable
        ? containerWidth
        : Math.max(containerWidth, innerMin + margin.left + margin.right);
      const containerHeight = containerRef.current?.clientHeight || 0;
      const totalHeight = fillAvailable ? (containerHeight || size.totalHeight) : size.totalHeight;
      const width = Math.max(1, totalWidth - margin.left - margin.right);
      const height = Math.max(1, totalHeight - margin.top - margin.bottom);

      const scenarioEntries = [...scenarioChartBySurgery.values()];
      const allVals = [
        ...chartModel.flatMap((entry) => [entry.prior ?? 0, entry.after ?? 0]),
        ...scenarioEntries.map((entry) => entry.after ?? 0),
      ];
      const dataMax = Math.max(d3.max(allVals) || 0, 0);
      const fixedYMax = typeof yHintMax === 'number' && yHintMax > 0 ? yHintMax : null;
      let yTop = fixedYMax;
      if (yTop == null) {
        yTop = dataMax <= 0 ? 1 : dataMax * 1.15;
      }

      d3.select(svgRef.current).selectAll('*').remove();

      const svg = d3
        .select(svgRef.current)
        .attr('viewBox', `0 0 ${totalWidth} ${totalHeight}`)
        .attr('width', fillAvailable ? '100%' : totalWidth)
        .attr('height', fillAvailable ? '100%' : totalHeight)
        .attr('preserveAspectRatio', fillAvailable ? 'none' : 'xMinYMid meet');

      const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);
      const valueTickFormat = (v) => {
        const n = Number(v);
        if (!Number.isFinite(n)) return '0';
        if (Number.isInteger(n)) return String(n);
        return formatChartDecimal(n, 1);
      };

      const appendDelta = (group, { prior, after, midX, midY, anchor = 'middle' }) => {
        const { absDiff, pctDiff } = computeOutcomeImprovement(prior, after);
        const diffText = formatDeltaAnnotation(absDiff, pctDiff, {
          absDecimals: after >= 100 || prior >= 100 ? 0 : 1,
          includePercentSign: true,
        });
        group
          .append('text')
          .attr('class', 'val-diff')
          .attr('x', midX)
          .attr('y', midY)
          .attr('text-anchor', anchor)
          .attr('dominant-baseline', 'auto')
          .attr('font-size', expanded ? '16px' : '12px')
          .attr('font-weight', 600)
          .attr('fill', JD_TEXT)
          .text(diffText);
      };

      const appendScenarioLifestyleLabel = (group, { x, y, value, prior, after }) => {
        const scenarioTipR = Math.max(4, size.tipRadius - 2);
        const labelX = x + scenarioTipR + 12;
        const valueText = formatOutcomeValue(value);
        const { absDiff, pctDiff } = computeOutcomeImprovement(prior, after);
        const deltaText = formatDeltaAnnotation(absDiff, pctDiff, {
          absDecimals: after >= 100 || prior >= 100 ? 0 : 1,
          includePercentSign: true,
        });
        const valueFont = scaleFontByLabelDigits(size.pointVal, valueText, {
          minPx: expanded ? 11 : 9,
        });

        group
          .append('circle')
          .attr('class', 'jd-scenario-outcome-tip jd-scenario-outcome-tip--scenario-post')
          .attr('cx', x)
          .attr('cy', y)
          .attr('r', scenarioTipR)
          .attr('fill', JD_MODIFICATION_COLOR)
          .attr('stroke', '#fff')
          .attr('stroke-width', 1.5);

        group
          .append('text')
          .attr('class', 'jd-scenario-overlay-label')
          .attr('x', labelX)
          .attr('y', y)
          .attr('text-anchor', 'start')
          .attr('dominant-baseline', 'middle')
          .attr('font-size', valueFont)
          .attr('font-weight', 700)
          .attr('fill', JD_MODIFICATION_COLOR)
          .text(valueText);

        group
          .append('text')
          .attr('class', 'jd-scenario-overlay-label jd-scenario-improvement-label')
          .attr('x', labelX)
          .attr('y', y + fontPx(valueFont) / 2 + 2)
          .attr('text-anchor', 'start')
          .attr('dominant-baseline', 'hanging')
          .attr('font-size', valueFont)
          .attr('font-weight', 700)
          .attr('fill', JD_MODIFICATION_COLOR)
          .text(deltaText);
      };

      if (chartOrientation === 'horizontal') {
        const y0 = d3
          .scaleBand()
          .domain(chartModel.map((d) => d.name))
          .range([0, height])
          .paddingInner(0.28)
          .paddingOuter(0.1);

        const x = d3.scaleLinear().domain([0, yTop]).range([0, width]);
        if (fixedYMax == null) x.nice();

        styleChartAxis(
          g.append('g').attr('transform', `translate(0,${height})`).call(
            d3.axisBottom(x).ticks(size.yTicks).tickFormat(valueTickFormat),
          ),
          size.axisFont,
        );

        // Pre / Post ticks beside each surgery band (staggered so lines never stack).
        const prePostTicks = chartModel.flatMap((entry) => {
          const bandY = y0(entry.name) ?? 0;
          const band = y0.bandwidth();
          const pad = band * 0.22;
          return [
            { label: priorAxisLabel, y: bandY + pad },
            { label: afterAxisLabel, y: bandY + band - pad },
          ];
        });
        const yAxis = g.append('g');
        yAxis
          .append('line')
          .attr('x1', 0)
          .attr('x2', 0)
          .attr('y1', 0)
          .attr('y2', height)
          .attr('stroke', '#333');
        const yTickGroups = yAxis
          .selectAll('g.jd-prepost-tick')
          .data(prePostTicks)
          .enter()
          .append('g')
          .attr('class', 'jd-prepost-tick')
          .attr('transform', (d) => `translate(0,${d.y})`);
        yTickGroups.append('line').attr('x2', -6).attr('stroke', '#333');
        yTickGroups.each(function placePrePostLabel(d) {
          const group = d3.select(this);
          const tokens = splitRiskAxisLabelTokens(d.label);
          const startY = -((tokens.length - 1) * prePostLineHeight) / 2;
          tokens.forEach((token, i) => {
            group
              .append('text')
              .attr('x', -10)
              .attr('y', startY + i * prePostLineHeight)
              .attr('text-anchor', 'end')
              .attr('dominant-baseline', 'middle')
              .attr('fill', JD_TEXT)
              .style('font-size', size.categoryAxisFont)
              .text(token);
          });
        });
        styleChartAxis(yAxis, size.categoryAxisFont);

        const groups = g
          .selectAll('.procedure-group')
          .data(chartModel)
          .enter()
          .append('g')
          .attr('class', 'procedure-group')
          .attr('transform', (d) => `translate(0,${y0(d.name)})`);

        groups.each(function renderLines(entry) {
          const group = d3.select(this);
          const scenarioEntry = scenarioChartBySurgery.get(entry.surgery);
          const band = y0.bandwidth();
          const pad = band * 0.22;
          const yPre = pad;
          const yPost = band - pad;
          const tip = tipFillForEntry(svg, entry);
          const hasPrior = isFiniteOutcomeValue(entry.prior);
          const hasAfter = isFiniteOutcomeValue(entry.after);
          if (!hasPrior && !hasAfter) {
            group
              .append('text')
              .attr('class', 'bar-label')
              .attr('x', width / 2)
              .attr('y', band / 2)
              .attr('text-anchor', 'middle')
              .attr('dominant-baseline', 'middle')
              .attr('font-size', size.pointVal)
              .attr('font-weight', 600)
              .attr('fill', JD_TEXT)
              .text('N/A');
            return;
          }
          const xPre = x(entry.prior ?? 0);
          const xPost = x(entry.after ?? 0);

          if (hasPrior && hasAfter) {
            group
              .append('line')
              .attr('class', 'jd-outcome-line')
              .attr('x1', xPre)
              .attr('y1', yPre)
              .attr('x2', xPost)
              .attr('y2', yPost)
              .attr('stroke', '#6c757d')
              .attr('stroke-width', size.lineWidth)
              .attr('stroke-linecap', 'round')
              .attr('stroke-dasharray', entry.striped ? '6 4' : null)
              .attr('opacity', 0.9);
          }

          [
            { key: 'pre', x: xPre, y: yPre, val: entry.prior, show: hasPrior },
            { key: 'post', x: xPost, y: yPost, val: entry.after, show: hasAfter },
          ].forEach((pt) => {
            if (!pt.show) return;
            group
              .append('circle')
              .attr('class', `jd-outcome-tip jd-outcome-tip--${pt.key}`)
              .attr('cx', pt.x)
              .attr('cy', pt.y)
              .attr('r', size.tipRadius)
              .attr('fill', tip)
              .attr('stroke', '#fff')
              .attr('stroke-width', 1.5);

            group
              .append('text')
              .attr('class', 'bar-label')
              .attr('x', pt.x)
              .attr('y', pt.y - size.tipRadius - 4)
              .attr('text-anchor', 'middle')
              .attr('dominant-baseline', 'auto')
              .attr('font-size', scaleFontByLabelDigits(size.pointVal, formatOutcomeValue(pt.val), {
                minPx: expanded ? 12 : 10,
              }))
              .attr('font-weight', 600)
              .attr('fill', JD_TEXT)
              .text(formatOutcomeValue(pt.val));
          });

          if (hasPrior && hasAfter) {
            appendDelta(group, {
              prior: entry.prior,
              after: entry.after,
              midX: xPost,
              midY: yPost - size.tipRadius - (expanded ? 38 : 30),
            });
          }
          if (scenarioEntry) {
            const hasMeaningfulScenarioImprovement =
              scenarioImprovementThreshold &&
              entry.prior - scenarioEntry.after >= scenarioImprovementThreshold.abs &&
              scenarioEntry.after < entry.after;
            if (!hasMeaningfulScenarioImprovement) return;
            const scenarioXPost = x(scenarioEntry.after ?? 0);
            group
              .append('line')
              .attr('class', 'jd-scenario-outcome-line')
              .attr('x1', xPre)
              .attr('y1', yPre)
              .attr('x2', scenarioXPost)
              .attr('y2', yPost)
              .attr('stroke', JD_MODIFICATION_COLOR)
              .attr('stroke-width', Math.max(2, size.lineWidth - 0.5))
              .attr('stroke-linecap', 'round')
              .attr('stroke-dasharray', '4 3')
              .attr('opacity', 0.9);
            appendScenarioLifestyleLabel(group, {
              x: scenarioXPost,
              y: yPost,
              value: scenarioEntry.after,
              prior: entry.prior,
              after: scenarioEntry.after,
            });
          }
        });
      } else {
        const x0 = d3
          .scaleBand()
          .domain(chartModel.map((d) => d.name))
          .range([0, width])
          .paddingInner(0.28)
          .paddingOuter(0.1);

        const y = d3.scaleLinear().domain([0, yTop]).range([height, 0]);
        if (fixedYMax == null) y.nice();

        styleChartAxis(
          g.append('g').call(d3.axisLeft(y).ticks(size.yTicks).tickFormat(valueTickFormat)),
          size.axisFont,
        );

        g.selectAll('.grid-line')
          .data(y.ticks(size.yTicks))
          .enter()
          .append('line')
          .attr('x1', 0)
          .attr('x2', width)
          .attr('y1', (d) => y(d))
          .attr('y2', (d) => y(d))
          .attr('stroke', '#e9ecef')
          .attr('stroke-dasharray', '3,3');

        // Pre / Post ticks under each surgery band, with surgery name as a second axis row.
        const prePostTicks = chartModel.flatMap((entry) => {
          const bandX = x0(entry.name) ?? 0;
          const band = x0.bandwidth();
          const pad = band * 0.22;
          return [
            { name: entry.name, label: priorAxisLabel, x: bandX + pad },
            { name: entry.name, label: afterAxisLabel, x: bandX + band - pad },
          ];
        });

        const xAxis = g.append('g').attr('transform', `translate(0,${height})`);
        xAxis
          .append('line')
          .attr('x1', 0)
          .attr('x2', width)
          .attr('y1', 0)
          .attr('y2', 0)
          .attr('stroke', '#333');
        const tickGroups = xAxis
          .selectAll('g.jd-prepost-tick')
          .data(prePostTicks)
          .enter()
          .append('g')
          .attr('class', 'jd-prepost-tick')
          .attr('transform', (d) => `translate(${d.x},0)`);
        tickGroups.append('line').attr('y2', 6).attr('stroke', '#333');
        tickGroups.each(function placePrePostLabel(d) {
          const group = d3.select(this);
          const tokens = splitRiskAxisLabelTokens(d.label);
          tokens.forEach((token, i) => {
            group
              .append('text')
              .attr('y', 18 + i * prePostLineHeight)
              .attr('text-anchor', 'middle')
              .attr('fill', JD_TEXT)
              .style('font-size', size.categoryAxisFont)
              .text(token);
          });
        });

        // Second axis row: surgery name centered under each Pre/Post pair.
        const surgeryLabelFont = expanded ? '12px' : '11px';
        const surgeryStartY = 18 + prePostLineCount * prePostLineHeight + 6;
        const surgeryLabels = xAxis
          .selectAll('g.jd-surgery-axis-label')
          .data(chartModel)
          .enter()
          .append('g')
          .attr('class', 'jd-surgery-axis-label')
          .attr('transform', (d) => {
            const bandX = x0(d.name) ?? 0;
            return `translate(${bandX + x0.bandwidth() / 2},0)`;
          });
        surgeryLabels.each(function placeSurgeryName(entry) {
          const labelGroup = d3.select(this);
          const words = String(entry.axisLabel || entry.name || '')
            .split(/\s+/)
            .filter(Boolean);
          const lineHeight = expanded ? 13 : 12;
          words.forEach((word, i) => {
            labelGroup
              .append('text')
              .attr('y', surgeryStartY + i * lineHeight)
              .attr('text-anchor', 'middle')
              .attr('fill', JD_TEXT)
              .style('font-size', surgeryLabelFont)
              .style('font-weight', 600)
              .text(word);
          });
        });
        styleChartAxis(xAxis, size.categoryAxisFont);

        const groups = g
          .selectAll('.procedure-group')
          .data(chartModel)
          .enter()
          .append('g')
          .attr('class', 'procedure-group')
          .attr('transform', (d) => `translate(${x0(d.name)},0)`);

        groups.each(function renderLines(entry) {
          const group = d3.select(this);
          const scenarioEntry = scenarioChartBySurgery.get(entry.surgery);
          const band = x0.bandwidth();
          const pad = band * 0.22;
          const xPre = pad;
          const xPost = band - pad;
          const tip = tipFillForEntry(svg, entry);
          const hasPrior = isFiniteOutcomeValue(entry.prior);
          const hasAfter = isFiniteOutcomeValue(entry.after);
          if (!hasPrior && !hasAfter) {
            group
              .append('text')
              .attr('class', 'bar-label')
              .attr('x', band / 2)
              .attr('y', height / 2)
              .attr('text-anchor', 'middle')
              .attr('dominant-baseline', 'middle')
              .attr('font-size', size.pointVal)
              .attr('font-weight', 600)
              .attr('fill', JD_TEXT)
              .text('N/A');
            return;
          }
          const yPre = y(entry.prior ?? 0);
          const yPost = y(entry.after ?? 0);

          if (hasPrior && hasAfter) {
            group
              .append('line')
              .attr('class', 'jd-outcome-line')
              .attr('x1', xPre)
              .attr('y1', yPre)
              .attr('x2', xPost)
              .attr('y2', yPost)
              .attr('stroke', '#6c757d')
              .attr('stroke-width', size.lineWidth)
              .attr('stroke-linecap', 'round')
              .attr('stroke-dasharray', entry.striped ? '6 4' : null)
              .attr('opacity', 0.9);
          }

          [
            { key: 'pre', x: xPre, y: yPre, val: entry.prior, show: hasPrior },
            { key: 'post', x: xPost, y: yPost, val: entry.after, show: hasAfter },
          ].forEach((pt) => {
            if (!pt.show) return;
            group
              .append('circle')
              .attr('class', `jd-outcome-tip jd-outcome-tip--${pt.key}`)
              .attr('cx', pt.x)
              .attr('cy', pt.y)
              .attr('r', size.tipRadius)
              .attr('fill', tip)
              .attr('stroke', '#fff')
              .attr('stroke-width', 1.5);

            group
              .append('text')
              .attr('class', 'bar-label')
              .attr('x', pt.x)
              .attr('y', pt.y - size.tipRadius - 5)
              .attr('text-anchor', 'middle')
              .attr('dominant-baseline', 'auto')
              .attr('font-size', scaleFontByLabelDigits(size.pointVal, formatOutcomeValue(pt.val), {
                minPx: expanded ? 12 : 10,
              }))
              .attr('font-weight', 600)
              .attr('fill', JD_TEXT)
              .text(formatOutcomeValue(pt.val));
          });

          if (hasPrior && hasAfter) {
            appendDelta(group, {
              prior: entry.prior,
              after: entry.after,
              midX: xPost,
              midY: Math.max(14, yPost - size.tipRadius - (expanded ? 40 : 30)),
            });
          }
          if (scenarioEntry) {
            const hasMeaningfulScenarioImprovement =
              scenarioImprovementThreshold &&
              entry.prior - scenarioEntry.after >= scenarioImprovementThreshold.abs &&
              scenarioEntry.after < entry.after;
            if (!hasMeaningfulScenarioImprovement) return;
            const scenarioYPost = y(scenarioEntry.after ?? 0);
            group
              .append('line')
              .attr('class', 'jd-scenario-outcome-line')
              .attr('x1', xPre)
              .attr('y1', yPre)
              .attr('x2', xPost)
              .attr('y2', scenarioYPost)
              .attr('stroke', JD_MODIFICATION_COLOR)
              .attr('stroke-width', Math.max(2, size.lineWidth - 0.5))
              .attr('stroke-linecap', 'round')
              .attr('stroke-dasharray', '4 3')
              .attr('opacity', 0.9);
            appendScenarioLifestyleLabel(group, {
              x: xPost,
              y: scenarioYPost,
              value: scenarioEntry.after,
              prior: entry.prior,
              after: scenarioEntry.after,
            });
          }
        });
      }
    };

    draw();
    return observeElementResize(containerRef.current, draw);
  }, [
    chartModel,
    scenarioChartBySurgery,
    profiles,
    expanded,
    sizeKey,
    chartOrientation,
    yHintMax,
    fillAvailable,
    scenarioImprovementThreshold,
    priorAxisLabel,
    afterAxisLabel,
  ]);

  if (!chartModel.length) return null;

  return (
    <div
      ref={containerRef}
      className="jd-chart-container"
      style={{ width: '100%', overflowX: fillAvailable ? 'hidden' : 'auto' }}
    >
      <svg
        ref={svgRef}
        style={{
          display: 'block',
          width: fillAvailable ? '100%' : undefined,
          height: fillAvailable ? '100%' : 'auto',
        }}
      />
    </div>
  );
}

function ProcedureOutcomeChartPanel({
  profiles,
  scenarioProfiles = [],
  chartOrientation = 'vertical',
  outcomeType = JD_CHART_TAB_KEYS.PAIN,
  fillAvailable = false,
  regionKey = null,
  thresholdFilterEnabled = true,
  showThresholdDetails = false,
  practiceOutcomeChangeScale = null,
  practiceStats = null,
}) {
  const isPain = outcomeType === JD_CHART_TAB_KEYS.PAIN;
  const threshold = getBenefitDeltaThreshold(outcomeType, regionKey);

  const sortedProfiles = useMemo(
    () => {
      const orderedProfiles = sortProfilesAlphabetically(profiles);
      if (practiceStats || !thresholdFilterEnabled || scenarioProfiles?.length) return orderedProfiles;
      return orderedProfiles.filter((profile) =>
        profileHasMeaningfulOutcomeDelta(profile, outcomeType, regionKey),
      );
    },
    [profiles, scenarioProfiles, outcomeType, regionKey, thresholdFilterEnabled, practiceStats],
  );

  if (!profiles?.length) return null;
  if (!sortedProfiles.length) {
    return (
      <div className="jd-threshold-empty-state">
        No pre-post improvement reaches the current meaningful-difference threshold.
      </div>
    );
  }

  return (
    <div className="jd-chart-panel">
      {showThresholdDetails && (
        <div className="jd-threshold-note">
          {isPain ? 'Pain delta' : regionKey === 'CERVICAL' ? 'NDI delta' : 'ODI delta'}:{' '}
          {formatThresholdSummary(threshold)}
        </div>
      )}
      <div className="jd-chart-panel-body">
        <AllProceduresOutcomesLineChart
          profiles={sortedProfiles}
          scenarioProfiles={scenarioProfiles}
          priorKey={isPain ? 'pain_prior' : 'function_prior'}
          afterKey={isPain ? 'pain_after' : 'function_after'}
          priorAxisLabel={isPain ? 'Pre (NRS)' : 'Pre (NDI/ODI)'}
          afterAxisLabel={isPain ? 'Post (NRS @ 6 Months)' : 'Post (NDI/ODI @ 6 Months)'}
          yHintMax={isPain ? PAIN_NRS_SCALE_MAX : OUTCOME_CHART_Y_MAX}
          expanded={fillAvailable}
          chartOrientation={chartOrientation}
          fillAvailable={fillAvailable}
          scenarioImprovementThreshold={threshold}
          practiceOutcomeChangeScale={practiceOutcomeChangeScale}
          practiceStats={practiceStats}
        />
      </div>
    </div>
  );
}

const RISK_CHART_SIZE = {
  compact: {
    totalHeight: 420,
    margin: { top: 34, right: 20, bottom: 72, left: 58 },
    innerMinPerEntry: 88,
    legendLead: '11px',
    legend: '10px',
    axisFont: '14px',
    /** Category labels (x in vertical charts) stay fixed; value axis may scale. */
    categoryAxisFont: '11px',
    barPct: '17px',
    legendRect: 9,
    legendItemGap: 22,
    legendCharWidth: 5.6,
    legendMaxLabelWidth: 180,
    legendPadAfterLead: 10,
    yTicks: 5,
  },
  expanded: {
    totalHeight: 600,
    margin: { top: 34, right: 32, bottom: 80, left: 82 },
    innerMinPerEntry: 120,
    legendLead: '16px',
    legend: '14px',
    axisFont: '22px',
    categoryAxisFont: '12px',
    barPct: '26px',
    legendRect: 14,
    legendItemGap: 40,
    legendCharWidth: 8.8,
    legendMaxLabelWidth: 240,
    legendPadAfterLead: 16,
    yTicks: 8,
  },
};

function scaleRiskBarPctFont(baseFontSize, selectedSurgeryCount) {
  const basePx = parseFloat(baseFontSize);
  if (!Number.isFinite(basePx) || selectedSurgeryCount < 4) return baseFontSize;

  // Aggressive shrink once 4+ bars share a risk group (~28% per step past 3).
  const steps = selectedSurgeryCount - 3;
  const scaledPx = Math.max(8, basePx * 0.72 ** steps);
  return `${scaledPx.toFixed(scaledPx % 1 === 0 ? 0 : 1)}px`;
}

function SpreadsheetRiskBarChart({
  riskLabels,
  profiles,
  spreadsheetControls,
  scenarioProfiles = [],
  scenarioSpreadsheetControls = null,
  practiceOverlayFactor = null,
  practiceStats = null,
  expanded = false,
  chartOrientation = 'vertical',
  fillAvailable = false,
  useDynamicScale = false,
  includeControl = true,
  onRiskLabelClick,
  valueKind = 'percent',
}) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const sizeKey = expanded ? 'expanded' : 'compact';
  const size = RISK_CHART_SIZE[sizeKey];
  const isAbsolute = valueKind === 'absolute';
  const effectiveDynamicScale = isAbsolute || useDynamicScale;

  const x1Domain = useMemo(() => buildX1Domain(profiles, includeControl), [profiles, includeControl]);

  const chartModel = useMemo(() => {
    const labels = sortRiskLabelsForChart(Array.isArray(riskLabels) ? [...riskLabels] : []);

    const entries = labels.map((name) => {
      const slots = buildSlotsForRisk(name, spreadsheetControls, profiles, includeControl);
      const scenarioSlots = scenarioProfiles?.length
        ? buildSlotsForRisk(
            name,
            scenarioSpreadsheetControls || spreadsheetControls,
            scenarioProfiles,
            includeControl,
          )
        : [];
      const slotByKey = Object.fromEntries(slots.map((s) => [s.key, s]));
      const scenarioSlotByKey = Object.fromEntries(scenarioSlots.map((s) => [s.key, s]));
      const bars = x1Domain.map((key) => {
        const slot = slotByKey[key];
        const scenarioSlot = scenarioSlotByKey[key];
        const surgeryId = key.startsWith('sx__') ? key.slice(5) : null;
        const parsed = surgeryId ? parseLevelSeriesKey(surgeryId) : null;
        const procedureKey = slot?.procedureKey || parsed?.procedureKey || null;
        const defaultSurgicalFill = procedureKey
          ? getSpreadsheetSurgeryColor(procedureKey)
          : '#5c6368';
        const researchPct = slot ? slot.pct : null;
        const researchScenarioPct = scenarioSlot ? scenarioSlot.pct : null;
        const livePracticePct =
          practiceStats != null && key !== 'control'
            ? practiceRateForRisk(practiceStats, procedureKey, name)
            : null;
        const pct = resolvePracticeAwareRiskPct({
          key,
          researchPct,
          livePracticePct,
          practiceStats,
          practiceOverlayFactor,
        });
        const scenarioPct = resolvePracticeAwareRiskPct({
          key,
          researchPct: researchScenarioPct,
          livePracticePct,
          practiceStats,
          practiceOverlayFactor,
        });
        return {
          key,
          // Preserve null so not-applicable rates label as N/A (0 still means ~.01).
          pct,
          scenarioPct,
          // Keep research values for the axis so Research | This practice does not rescale.
          axisPct: researchPct,
          axisScenarioPct: researchScenarioPct,
          fill: slot?.fill ?? (key === 'control' ? '#6c757d' : defaultSurgicalFill),
          procedureKey,
          striped: Boolean(slot?.striped),
          opacity: slot?.opacity ?? (key.startsWith('sx__') ? 0.35 : 0.2),
        };
      });
      const hasNoScenarioImprovement =
        Boolean(scenarioProfiles?.length) &&
        !riskLabelHasMeaningfulScenarioImprovement({ riskLabel: name, profiles, scenarioProfiles });
      return { name, bars, hasNoScenarioImprovement };
    });

    return { entries, x1Domain };
  }, [
    riskLabels,
    profiles,
    spreadsheetControls,
    scenarioProfiles,
    scenarioSpreadsheetControls,
    practiceOverlayFactor,
    practiceStats,
    x1Domain,
    includeControl,
  ]);

  useEffect(() => {
    const { entries } = chartModel;
    if (!entries.length || !profiles.length) {
      d3.select(svgRef.current).selectAll('*').remove();
      return;
    }

    const draw = () => {
      const formatBarValue = (value) => formatMetricBarValue(value, valueKind);
      const margin = { ...size.margin };
      const hasRateOverlay = entries.some((entry) =>
        entry.bars.some((bar) => Boolean(resolveRateOverlay(entry, bar, formatBarValue))),
      );
      if (chartOrientation === 'horizontal') {
        // Room for wrapped left-axis labels (one word per line).
        margin.left += expanded ? 72 : 56;
        if (hasRateOverlay) {
          // Extra room for stacked current | new labels at bar ends.
          margin.right = Math.max(margin.right, expanded ? 64 : 52);
        }
      } else {
        // Size bottom margin to the tallest wrapped label — no spare dead space.
        margin.bottom = riskChartBottomMargin(
          entries.map((entry) => entry.name),
          size.categoryAxisFont,
        );
      }
      const containerWidth = containerRef.current?.clientWidth || 720;
      // Scenario overlays share a surgery's bar slot (overlap), so density stays 1 slot per bar.
      const barsPerEntry = Math.max(
        1,
        d3.max(entries.map((entry) => entry.bars.length)) || x1Domain.length,
      );
      const barDensityScale = Math.max(1, barsPerEntry / 4);
      const innerMin = entries.length * size.innerMinPerEntry * barDensityScale + 80;
      const totalWidth = fillAvailable
        ? containerWidth
        : Math.max(containerWidth, innerMin + margin.left + margin.right);
      const containerHeight = containerRef.current?.clientHeight || 0;
      const totalHeight = fillAvailable ? (containerHeight || size.totalHeight) : size.totalHeight;
      const width = Math.max(1, totalWidth - margin.left - margin.right);
      const height = Math.max(1, totalHeight - margin.top - margin.bottom);
      const barPctFont = scaleRiskBarPctFont(size.barPct, barsPerEntry);

      const allPct = [];
      entries.forEach((e) => {
        e.bars.forEach((b) => {
          allPct.push(b.axisPct ?? b.pct ?? 0);
          if (b.axisScenarioPct != null) allPct.push(b.axisScenarioPct);
          else if (b.scenarioPct != null) allPct.push(b.scenarioPct);
        });
      });
      const dynamicYMax = Math.max((d3.max(allPct) || 0) * 1.28, isAbsolute ? 1 : 0.01);
      const yMax = effectiveDynamicScale ? dynamicYMax : 100;

      d3.select(svgRef.current).selectAll('*').remove();

      const svg = d3
        .select(svgRef.current)
        .attr('viewBox', `0 0 ${totalWidth} ${totalHeight}`)
        .attr('width', fillAvailable ? '100%' : totalWidth)
        .attr('height', fillAvailable ? '100%' : totalHeight)
        .attr('preserveAspectRatio', fillAvailable ? 'none' : 'xMinYMid meet');

      const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

      if (chartOrientation === 'horizontal') {
        const y0 = d3
          .scaleBand()
          .domain(entries.map((d) => d.name))
          .range([0, height])
          .paddingInner(0.2)
          .paddingOuter(0.06);

        const x = d3
          .scaleLinear()
          .domain([0, yMax])
          .range([0, width]);
        if (effectiveDynamicScale) x.nice();
        const valueAxisTickFormat = makeMetricAxisTickFormat(x, size.yTicks, valueKind);

        const yAxis = g.append('g').call(d3.axisLeft(y0).tickFormat((d) => formatRiskLabelForChart(String(d))));
        yAxis.selectAll('.tick text').each(function addRiskTooltip(d) {
          const tip = riskLabelTooltip(String(d));
          if (!tip) return;
          d3.select(this).append('title').text(tip);
        });
        yAxis
          .selectAll('.tick text')
          .style('cursor', onRiskLabelClick ? 'zoom-in' : null)
          .on('click', (event, d) => {
            if (!onRiskLabelClick) return;
            event.stopPropagation();
            onRiskLabelClick(String(d));
          });
        styleChartAxis(yAxis, size.categoryAxisFont);
        wrapRiskAxisTickWords(yAxis.selectAll('.tick text'), {
          anchor: 'end',
          stack: 'center',
        });

        const useLocalAbsoluteScales = isAbsolute && entries.length > 1;

        if (!useLocalAbsoluteScales) {
          styleChartAxis(
            g.append('g').attr('transform', `translate(0,${height})`).call(
              d3.axisBottom(x).ticks(size.yTicks).tickFormat(valueAxisTickFormat),
            ),
            size.axisFont,
          );

          g.selectAll('.grid-line')
            .data(x.ticks(size.yTicks))
            .enter()
            .append('line')
            .attr('x1', (d) => x(d))
            .attr('x2', (d) => x(d))
            .attr('y1', 0)
            .attr('y2', height)
            .attr('stroke', '#e9ecef')
            .attr('stroke-dasharray', '3,3');
        }

        // Outer slot keeps SVG translate; inner group gets CSS hover scale.
        const groups = g
          .selectAll('.complication-slot')
          .data(entries)
          .enter()
          .append('g')
          .attr('class', 'complication-slot')
          .attr('transform', (d) => `translate(0,${y0(d.name)})`)
          .append('g')
          .attr('class', 'complication-group')
          .style('cursor', onRiskLabelClick ? 'zoom-in' : null)
          .on('click', (event, d) => {
            if (!onRiskLabelClick) return;
            event.stopPropagation();
            onRiskLabelClick(d.name);
          });

        const zeroBarWidth = 3;

        groups.each(function renderBars(entry) {
          const group = d3.select(this);
          group
            .insert('rect', ':first-child')
            .attr('class', 'jd-complication-hit-area')
            .attr('x', 0)
            .attr('y', 0)
            .attr('width', width)
            .attr('height', y0.bandwidth())
            .attr('fill', 'transparent')
            .attr('pointer-events', 'all');
          const displayBars = entry.bars;
          const y1 = d3
            .scaleBand()
            .domain(displayBars.map((b) => b.key))
            .range([0, y0.bandwidth()])
            .padding(0.12);
          const valueLabelFont = fitFontToSpace(barPctFont, y1.bandwidth(), expanded ? 16 : 12, 0.72);
          const crowdedBars = displayBars.length >= 4;
          const labelMinPx = crowdedBars ? (expanded ? 9 : 7) : expanded ? 12 : 10;
          // Shared plain/arrow size: densify with surgery count + longest value, then 75%.
          const longestLabel = displayBars.reduce((best, bar) => {
            const candidates = [formatBarValue(bar.pct)];
            const overlay = resolveRateOverlay(entry, bar, formatBarValue);
            if (overlay) candidates.push(formatBarValue(overlay.pct));
            const label = candidates.reduce((a, b) => (b.length > a.length ? b : a), '');
            return label.length > best.length ? label : best;
          }, '');
          const densifiedLabelFont = scaleFontByLabelDigits(valueLabelFont, longestLabel, {
            minPx: labelMinPx,
            perDigit: crowdedBars ? 0.68 : 0.82,
          });
          const sharedBarLabelFont = formatFontPx(
            Math.max(labelMinPx * 0.75, fontPx(densifiedLabelFont) * 0.75),
          );
          const localMax = useLocalAbsoluteScales
            ? Math.max(
                (d3.max(
                  entry.bars.flatMap((b) => [
                    b.axisPct ?? b.pct ?? 0,
                    b.axisScenarioPct ?? b.scenarioPct ?? 0,
                  ]),
                ) || 0) * 1.15,
                1,
              )
            : yMax;
          const xScale = useLocalAbsoluteScales
            ? d3.scaleLinear().domain([0, localMax]).range([0, width])
            : x;

          displayBars.forEach((bar) => {
            const barGroup = group.append('g').attr('class', 'jd-chart-figure');
            const bandY = y1(bar.key) ?? 0;
            const bandH = y1.bandwidth();
            const baselinePct = bar.pct;
            const baselineDraw = metricBarDrawValue(baselinePct);
            const overlay = resolveRateOverlay(entry, bar, formatBarValue);
            const overlayPct = overlay ? overlay.pct : null;
            const overlayDraw = metricBarDrawValue(overlayPct);
            const baselineWidth =
              baselineDraw === 0 ? zeroBarWidth : Math.max(0, xScale(baselineDraw));
            const overlayWidth =
              overlayPct == null
                ? 0
                : overlayDraw === 0
                  ? zeroBarWidth
                  : Math.max(0, xScale(overlayDraw));

            if (overlay) {
              const fill = resolveBarFill(svg, bar);
              const solidOpacity = bar.opacity ?? 1;
              const lowDraw = Math.min(baselineDraw, overlayDraw);
              const highDraw = Math.max(baselineDraw, overlayDraw);
              const highWidth =
                highDraw === 0 ? zeroBarWidth : Math.max(0, xScale(highDraw));
              const lowWidth =
                lowDraw === 0 ? zeroBarWidth : Math.max(0, xScale(lowDraw));
              barGroup
                .append('rect')
                .attr('class', `${overlay.className} ${overlay.className}--baseline`)
                .attr('x', 0)
                .attr('y', bandY)
                .attr('height', bandH)
                .attr('width', highWidth)
                .attr('rx', 2)
                .attr('fill', fill)
                .attr('opacity', solidOpacity * JD_SCENARIO_FADE_OPACITY);
              barGroup
                .append('rect')
                .attr('class', `${overlay.className} ${overlay.className}--overlay`)
                .attr('x', 0)
                .attr('y', bandY)
                .attr('height', bandH)
                .attr('width', lowWidth)
                .attr('fill', fill)
                .attr('opacity', solidOpacity);
              appendVerticalRateArrowLabel(barGroup, {
                x: Math.max(baselineWidth, overlayWidth) + 6 + fontPx(sharedBarLabelFont) * 1.35,
                y: bandY + bandH / 2,
                fromText: formatBarValue(baselinePct),
                toText: formatBarValue(overlayPct),
                fontSize: sharedBarLabelFont,
                fill: overlay.fill,
                align: 'center',
              });
            } else {
              barGroup
                .append('rect')
                .attr('x', 0)
                .attr('y', bandY)
                .attr('height', bandH)
                .attr('width', baselineWidth)
                .attr('rx', 2)
                .attr('fill', resolveBarFill(svg, bar))
                .attr('opacity', bar.opacity);
              barGroup
                .append('text')
                .attr('class', 'bar-label')
                .attr('x', baselineWidth + 4)
                .attr('y', bandY + bandH / 2 + 4)
                .attr('text-anchor', 'start')
                .attr('font-size', metricBarLabelFont(sharedBarLabelFont, formatBarValue(baselinePct)))
                .attr('font-weight', 700)
                .attr('fill', JD_TEXT)
                .text(formatBarValue(baselinePct));
            }
          });
        });
      } else {
        const x0 = d3
          .scaleBand()
          .domain(entries.map((d) => d.name))
          .range([0, width])
          .paddingInner(0.2)
          .paddingOuter(0.06);

        const y = d3
          .scaleLinear()
          .domain([0, yMax])
          .range([height, 0]);
        if (effectiveDynamicScale) y.nice();
        const valueAxisTickFormat = makeMetricAxisTickFormat(y, size.yTicks, valueKind);

        const xAxis = g
          .append('g')
          .attr('transform', `translate(0,${height})`)
          .call(d3.axisBottom(x0).tickFormat((d) => formatRiskLabelForChart(String(d))));
        xAxis.selectAll('.tick text').each(function addRiskTooltip(d) {
          const tip = riskLabelTooltip(String(d));
          if (!tip) return;
          d3.select(this).append('title').text(tip);
        });
        xAxis
          .selectAll('.tick text')
          .style('cursor', onRiskLabelClick ? 'zoom-in' : null)
          .on('click', (event, d) => {
            if (!onRiskLabelClick) return;
            event.stopPropagation();
            onRiskLabelClick(String(d));
          });
        xAxis
          .selectAll('text')
          .attr('transform', null)
          .attr('dx', null)
          .attr('dy', null)
          .style('text-anchor', 'middle');
        styleChartAxis(xAxis, size.categoryAxisFont);
        wrapRiskAxisTickWords(xAxis.selectAll('.tick text'), {
          anchor: 'middle',
          stack: 'down',
        });

        const useLocalAbsoluteScales = isAbsolute && entries.length > 1;

        if (!useLocalAbsoluteScales) {
          styleChartAxis(
            g.append('g').call(d3.axisLeft(y).ticks(size.yTicks).tickFormat(valueAxisTickFormat)),
            size.axisFont,
          );

          g.selectAll('.grid-line')
            .data(y.ticks(size.yTicks))
            .enter()
            .append('line')
            .attr('x1', 0)
            .attr('x2', width)
            .attr('y1', (d) => y(d))
            .attr('y2', (d) => y(d))
            .attr('stroke', '#e9ecef')
            .attr('stroke-dasharray', '3,3');
        }

        // Outer slot keeps SVG translate; inner group gets CSS hover scale.
        const groups = g
          .selectAll('.complication-slot')
          .data(entries)
          .enter()
          .append('g')
          .attr('class', 'complication-slot')
          .attr('transform', (d) => `translate(${x0(d.name)},0)`)
          .append('g')
          .attr('class', 'complication-group')
          .style('cursor', onRiskLabelClick ? 'zoom-in' : null)
          .on('click', (event, d) => {
            if (!onRiskLabelClick) return;
            event.stopPropagation();
            onRiskLabelClick(d.name);
          });

        const zeroBarHeight = 3;

        groups.each(function renderBars(entry) {
          const group = d3.select(this);
          group
            .insert('rect', ':first-child')
            .attr('class', 'jd-complication-hit-area')
            .attr('x', 0)
            .attr('y', 0)
            .attr('width', x0.bandwidth())
            .attr('height', height)
            .attr('fill', 'transparent')
            .attr('pointer-events', 'all');
          const displayBars = entry.bars;
          const x1 = d3
            .scaleBand()
            .domain(displayBars.map((b) => b.key))
            .range([0, x0.bandwidth()])
            .padding(0.12);
          const valueLabelFont = fitFontToSpace(barPctFont, x1.bandwidth(), expanded ? 16 : 12, 0.74);
          const crowdedBars = displayBars.length >= 4;
          const labelMinPx = crowdedBars ? (expanded ? 9 : 7) : expanded ? 12 : 10;
          // Shared plain/arrow size: densify with surgery count + longest value, then 75%.
          const longestLabel = displayBars.reduce((best, bar) => {
            const candidates = [formatBarValue(bar.pct)];
            const overlay = resolveRateOverlay(entry, bar, formatBarValue);
            if (overlay) candidates.push(formatBarValue(overlay.pct));
            const label = candidates.reduce((a, b) => (b.length > a.length ? b : a), '');
            return label.length > best.length ? label : best;
          }, '');
          const densifiedLabelFont = scaleFontByLabelDigits(valueLabelFont, longestLabel, {
            minPx: labelMinPx,
            perDigit: crowdedBars ? 0.68 : 0.82,
          });
          const sharedBarLabelFont = formatFontPx(
            Math.max(labelMinPx * 0.75, fontPx(densifiedLabelFont) * 0.75),
          );
          const localMax = useLocalAbsoluteScales
            ? Math.max(
                (d3.max(
                  entry.bars.flatMap((b) => [
                    b.axisPct ?? b.pct ?? 0,
                    b.axisScenarioPct ?? b.scenarioPct ?? 0,
                  ]),
                ) || 0) * 1.15,
                1,
              )
            : yMax;
          const yScale = useLocalAbsoluteScales
            ? d3.scaleLinear().domain([0, localMax]).range([height, 0])
            : y;

          displayBars.forEach((bar) => {
            const barGroup = group.append('g').attr('class', 'jd-chart-figure');
            const bandX = x1(bar.key) ?? 0;
            const bandW = x1.bandwidth();
            const baselinePct = bar.pct;
            const baselineDraw = metricBarDrawValue(baselinePct);
            const overlay = resolveRateOverlay(entry, bar, formatBarValue);
            const overlayPct = overlay ? overlay.pct : null;
            const overlayDraw = metricBarDrawValue(overlayPct);
            const baselineTop =
              baselineDraw === 0 ? height - zeroBarHeight : yScale(baselineDraw);
            const baselineHeight =
              baselineDraw === 0 ? zeroBarHeight : Math.max(0, height - yScale(baselineDraw));
            const overlayTop =
              overlayPct == null
                ? height
                : overlayDraw === 0
                  ? height - zeroBarHeight
                  : yScale(overlayDraw);

            if (overlay) {
              const fill = resolveBarFill(svg, bar);
              const solidOpacity = bar.opacity ?? 1;
              const lowDraw = Math.min(baselineDraw, overlayDraw);
              const highDraw = Math.max(baselineDraw, overlayDraw);
              const highTop =
                highDraw === 0 ? height - zeroBarHeight : yScale(highDraw);
              const highHeight =
                highDraw === 0 ? zeroBarHeight : Math.max(0, height - yScale(highDraw));
              const lowTop =
                lowDraw === 0 ? height - zeroBarHeight : yScale(lowDraw);
              const lowHeight =
                lowDraw === 0 ? zeroBarHeight : Math.max(0, height - yScale(lowDraw));
              barGroup
                .append('rect')
                .attr('class', `${overlay.className} ${overlay.className}--baseline`)
                .attr('x', bandX)
                .attr('y', highTop)
                .attr('width', bandW)
                .attr('height', highHeight)
                .attr('rx', 2)
                .attr('fill', fill)
                .attr('opacity', solidOpacity * JD_SCENARIO_FADE_OPACITY);
              barGroup
                .append('rect')
                .attr('class', `${overlay.className} ${overlay.className}--overlay`)
                .attr('x', bandX)
                .attr('y', lowTop)
                .attr('width', bandW)
                .attr('height', lowHeight)
                .attr('fill', fill)
                .attr('opacity', solidOpacity);
              appendVerticalRateArrowLabel(barGroup, {
                x: bandX + bandW / 2,
                y: Math.min(baselineTop, overlayTop) - 4,
                fromText: formatBarValue(baselinePct),
                toText: formatBarValue(overlayPct),
                fontSize: sharedBarLabelFont,
                fill: overlay.fill,
                align: 'bottom',
              });
            } else {
              barGroup
                .append('rect')
                .attr('x', bandX)
                .attr('y', baselineTop)
                .attr('width', bandW)
                .attr('height', baselineHeight)
                .attr('rx', 2)
                .attr('fill', resolveBarFill(svg, bar))
                .attr('opacity', bar.opacity);
              barGroup
                .append('text')
                .attr('class', 'bar-label')
                .attr('x', bandX + bandW / 2)
                .attr('y', baselineTop - 3)
                .attr('text-anchor', 'middle')
                .attr('font-size', metricBarLabelFont(sharedBarLabelFont, formatBarValue(baselinePct)))
                .attr('font-weight', 700)
                .attr('fill', JD_TEXT)
                .text(formatBarValue(baselinePct));
            }
          });
        });
      }
    };

    draw();
    return observeElementResize(containerRef.current, draw);
  }, [
    chartModel,
    profiles,
    expanded,
    sizeKey,
    chartOrientation,
    fillAvailable,
    useDynamicScale,
    valueKind,
    isAbsolute,
    effectiveDynamicScale,
    x1Domain.length,
    onRiskLabelClick,
  ]);

  if (!chartModel.entries.length) {
    return <p className="text-muted small mb-0">No chart data for the current selection.</p>;
  }

  return (
    <div
      ref={containerRef}
      className="jd-chart-container"
      style={{ width: '100%', overflowX: fillAvailable ? 'hidden' : 'auto' }}
    >
      <svg
        ref={svgRef}
        style={{
          display: 'block',
          width: fillAvailable ? '100%' : undefined,
          height: fillAvailable ? '100%' : 'auto',
        }}
      />
    </div>
  );
}

function ComplicationRiskChartPanel({
  riskLabels,
  profiles,
  spreadsheetControls,
  scenarioProfiles = [],
  scenarioSpreadsheetControls = null,
  practiceOverlayFactor = null,
  practiceStats = null,
  chartOrientation = 'vertical',
  fillAvailable = false,
  useDynamicScale = false,
  onToggleScale,
  includeControl = true,
  valueKind = 'percent',
  thresholdFilterEnabled = true,
  showThresholdDetails = false,
}) {
  const [expandedRiskLabel, setExpandedRiskLabel] = useState(null);
  const visibleRiskLabels = useMemo(
    () => {
      const labels = Array.isArray(riskLabels) ? [...riskLabels] : [];
      // Always keep every complication on the chart; only optionally gate on
      // meaningful between-surgery differences (base view, no scenario overlay).
      if (practiceStats || !thresholdFilterEnabled || scenarioProfiles?.length) return labels;
      return labels.filter((riskLabel) =>
        riskLabelHasMeaningfulDifference({
          riskLabel,
          profiles,
          spreadsheetControls,
          includeControl,
          scenarioProfiles,
          scenarioSpreadsheetControls,
        }),
      );
    },
    [
      thresholdFilterEnabled,
      riskLabels,
      profiles,
      spreadsheetControls,
      includeControl,
      scenarioProfiles,
      scenarioSpreadsheetControls,
      practiceStats,
    ],
  );
  const expandedRiskIndex = expandedRiskLabel ? visibleRiskLabels.indexOf(expandedRiskLabel) : -1;
  const canNavigateFocusedRisk = expandedRiskIndex >= 0 && visibleRiskLabels.length > 1;
  const focusedRiskPosition = expandedRiskIndex >= 0 ? expandedRiskIndex + 1 : 0;
  const showScaleToggle = valueKind === 'percent' && typeof onToggleScale === 'function';

  const navigateFocusedRisk = (direction) => {
    if (!canNavigateFocusedRisk) return;
    const nextIndex = (expandedRiskIndex + direction + visibleRiskLabels.length) % visibleRiskLabels.length;
    setExpandedRiskLabel(visibleRiskLabels[nextIndex]);
  };

  if (!riskLabels?.length) return null;
  if (!visibleRiskLabels.length) {
    return (
      <div className="jd-chart-panel">
        <div className="jd-threshold-empty-state">
          No post-surgery statistic reaches the current meaningful-difference threshold.
        </div>
      </div>
    );
  }

  return (
    <div className="jd-chart-panel">
      {showScaleToggle && (
        <div className="jd-risk-chart-overlay-actions">
          <Button
            variant={useDynamicScale ? 'primary' : 'light'}
            size="sm"
            className="border shadow-sm jd-icon-button"
            onClick={onToggleScale}
            aria-label={useDynamicScale ? 'Use 100 percent complication scale' : 'Use data-fit complication scale'}
            title={useDynamicScale ? 'Use 0-100% scale' : 'Zoom to current data scale'}
          >
            <Search size={15} aria-hidden />
          </Button>
        </div>
      )}
      {showThresholdDetails && (
        <div className="jd-threshold-note">
          Showing outcomes with meaningful differences. Open a focused chart to see the threshold for that statistic.
        </div>
      )}
      <div className="jd-chart-panel-body">
        <SpreadsheetRiskBarChart
          riskLabels={visibleRiskLabels}
          profiles={profiles}
          spreadsheetControls={spreadsheetControls}
          scenarioProfiles={scenarioProfiles}
          scenarioSpreadsheetControls={scenarioSpreadsheetControls}
          practiceOverlayFactor={practiceOverlayFactor}
          practiceStats={practiceStats}
          expanded={fillAvailable}
          chartOrientation={chartOrientation}
          fillAvailable={fillAvailable}
          useDynamicScale={useDynamicScale}
          includeControl={includeControl}
          onRiskLabelClick={setExpandedRiskLabel}
          valueKind={valueKind}
        />
      </div>
      {expandedRiskLabel && expandedRiskIndex >= 0 && (
        <div
          className="jd-risk-focus-backdrop"
          role="presentation"
          onClick={() => setExpandedRiskLabel(null)}
        >
          <div
            className="jd-risk-focus-card"
            role="dialog"
            aria-modal="true"
            aria-label={`${formatRiskLabelForChart(expandedRiskLabel)} focused complication chart`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="jd-risk-focus-header">
              <div className="jd-risk-focus-title">
                <h6 className="mb-0">{formatRiskLabelForChart(expandedRiskLabel)}</h6>
                {canNavigateFocusedRisk && (
                  <span className="jd-risk-focus-count">
                    {focusedRiskPosition} of {visibleRiskLabels.length}
                  </span>
                )}
              </div>
              {canNavigateFocusedRisk && (
                <div className="jd-risk-focus-carousel" aria-label="Navigate complication charts">
                  <Button
                    variant="light"
                    size="sm"
                    className="border shadow-sm"
                    onClick={() => navigateFocusedRisk(-1)}
                    aria-label="Previous complication chart"
                  >
                    Previous
                  </Button>
                  <Button
                    variant="light"
                    size="sm"
                    className="border shadow-sm"
                    onClick={() => navigateFocusedRisk(1)}
                    aria-label="Next complication chart"
                  >
                    Next
                  </Button>
                </div>
              )}
              <div className="jd-risk-focus-header-actions">
                {showScaleToggle && (
                  <Button
                    variant={useDynamicScale ? 'primary' : 'light'}
                    size="sm"
                    className="border shadow-sm jd-icon-button"
                    onClick={onToggleScale}
                    aria-label={useDynamicScale ? 'Use 100 percent complication scale' : 'Use data-fit complication scale'}
                    title={useDynamicScale ? 'Use 0-100% scale' : 'Zoom to current data scale'}
                  >
                    <Search size={15} aria-hidden />
                  </Button>
                )}
                <Button
                  variant="light"
                  size="sm"
                  className="border shadow-sm"
                  onClick={() => setExpandedRiskLabel(null)}
                  aria-label="Close focused complication chart"
                >
                  Close
                </Button>
              </div>
            </div>
            <div className="jd-risk-focus-chart">
              {showThresholdDetails && MEANINGFUL_DIFFERENCE_THRESHOLDS[expandedRiskLabel] && (
                <div className="jd-threshold-note jd-threshold-note--focus">
                  {formatThresholdSummary(MEANINGFUL_DIFFERENCE_THRESHOLDS[expandedRiskLabel])}
                </div>
              )}
              <SpreadsheetRiskBarChart
                riskLabels={[expandedRiskLabel]}
                profiles={profiles}
                spreadsheetControls={spreadsheetControls}
                scenarioProfiles={scenarioProfiles}
                scenarioSpreadsheetControls={scenarioSpreadsheetControls}
                practiceOverlayFactor={practiceOverlayFactor}
                practiceStats={practiceStats}
                expanded
                chartOrientation={chartOrientation}
                fillAvailable
                useDynamicScale={useDynamicScale}
                includeControl={includeControl}
                valueKind={valueKind}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function chooseValueAxisTicks(yMax) {
  const maxIntervals = 5;
  if (!(yMax > 0)) {
    return { ticks: [0, 1], top: 1 };
  }
  const rawStep = yMax / maxIntervals;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  let step = magnitude;
  let top = Math.ceil(yMax / step) * step;
  for (const multiplier of [1, 2, 5, 10, 20, 50]) {
    const candidate = multiplier * magnitude;
    const candidateTop = Math.ceil(yMax / candidate) * candidate;
    const intervals = Math.round(candidateTop / candidate);
    if (intervals <= maxIntervals) {
      step = candidate;
      top = candidateTop;
      break;
    }
  }
  const ticks = [];
  for (let value = 0; value <= top + step / 1e9; value += step) {
    ticks.push(Number(value.toPrecision(12)));
  }
  return { ticks, top };
}

function appendValueRangeDollarLabels(group, {
  x,
  yHigh,
  high,
  central,
  low,
  fontSize,
}) {
  const lines = [
    { key: 'high', text: `High ${formatUsd(high)}` },
    { key: 'central', text: `Central ${formatUsd(central)}` },
    { key: 'low', text: `Low ${formatUsd(low)}` },
  ];
  const sizePx = fontPx(fontSize);
  const lineHeight = sizePx * 1.18;
  const startY = yHigh - (lines.length - 1) * lineHeight - 4;
  const text = group
    .append('text')
    .attr('class', 'bar-label jd-value-range-labels')
    .attr('x', x)
    .attr('y', startY)
    .attr('text-anchor', 'middle')
    .attr('font-weight', 700);
  lines.forEach((line, index) => {
    text
      .append('tspan')
      .attr('x', x)
      .attr('dy', index === 0 ? 0 : lineHeight)
      .attr('text-anchor', 'middle')
      .attr('font-size', metricBarLabelFont(fontSize, line.text))
      .attr('fill', JD_TEXT)
      .text(line.text);
  });
  return text;
}

function appendOutlinedMetricBox(group, {
  x,
  y,
  text,
  fontSize,
  title,
  anchor = 'bottom',
  align = 'center',
  color = JD_TEXT,
  fill = '#fff',
}) {
  const label = text || 'N/A';
  const font = metricBarLabelFont(fontSize, label);
  const measure = group
    .append('text')
    .attr('font-size', font)
    .attr('font-weight', 700)
    .attr('opacity', 0)
    .text(label);
  const bbox = measure.node().getBBox();
  measure.remove();

  const padX = 6;
  const padY = 3;
  const boxWidth = bbox.width + padX * 2;
  const boxHeight = bbox.height + padY * 2;
  const boxX = align === 'left' ? x : x - boxWidth / 2;
  const boxY = anchor === 'top' ? y : y - boxHeight;
  const textX = boxX + boxWidth / 2;

  group
    .append('rect')
    .attr('class', 'jd-value-cpp-box')
    .attr('x', boxX)
    .attr('y', boxY)
    .attr('width', boxWidth)
    .attr('height', boxHeight)
    .attr('rx', 3)
    .attr('fill', fill)
    .attr('stroke', color)
    .attr('stroke-width', 1.5);

  group
    .append('text')
    .attr('class', 'bar-label jd-value-cpp-label')
    .attr('x', textX)
    .attr('y', boxY + padY - bbox.y)
    .attr('text-anchor', 'middle')
    .attr('font-size', font)
    .attr('font-weight', 700)
    .attr('fill', JD_TEXT)
    .text(label);

  if (title) {
    group.append('title').text(title);
  }

  return { boxY, boxHeight, boxWidth };
}

function formatCostPerPointCaption(value, kind) {
  const amount = formatUsd(value);
  if (amount === 'N/A') return null;
  return kind === 'pain' ? `${amount} / pain point` : `${amount} / function point`;
}

function valueChartCostPerPointCount(entry) {
  if (!entry?.payable) return 0;
  let count = 0;
  if (entry.costPerPainPoint != null && Number.isFinite(entry.costPerPainPoint)) count += 1;
  if (entry.costPerDysfunctionPoint != null && Number.isFinite(entry.costPerDysfunctionPoint)) count += 1;
  return count;
}

function appendCostPerPointBarLabels(group, { entry, fontSize, centerX, startY }) {
  const items = [
    {
      text: formatCostPerPointCaption(entry.costPerPainPoint, 'pain'),
      title: 'Cost per 1 point reduction in pain',
      color: JD_TEXT,
      fill: '#fff',
    },
    {
      text: formatCostPerPointCaption(entry.costPerDysfunctionPoint, 'dysfunction'),
      title: 'Cost per 1 point reduction in dysfunction',
      color: JD_TEXT,
      fill: '#ced4da',
    },
  ].filter((item) => item.text);
  if (!items.length) return null;

  const chipGroup = group.append('g').attr('class', 'jd-value-cpp-row');
  let y = 0;
  items.forEach((item) => {
    const drawn = appendOutlinedMetricBox(chipGroup, {
      x: 0,
      y,
      text: item.text,
      fontSize,
      title: item.title,
      anchor: 'top',
      align: 'center',
      color: item.color,
      fill: item.fill,
    });
    y += (drawn?.boxHeight || fontPx(fontSize) + 8) + 4;
  });
  const bbox = chipGroup.node()?.getBBox?.();
  if (bbox) {
    chipGroup.attr(
      'transform',
      `translate(${centerX - bbox.x - bbox.width / 2},${startY - bbox.y})`,
    );
  }
  return bbox;
}

function valueChartScaleValues(entries) {
  return (entries || []).flatMap((entry) => {
    if (!entry?.payable) return [];
    return [entry.central, entry.high, entry.low, entry.base];
  }).filter((value) => Number.isFinite(value));
}

function maxValueChartScale(entriesBySetting) {
  const values = Object.values(entriesBySetting || {}).flatMap(valueChartScaleValues);
  return Math.max(0, ...values);
}

function buildExpectedValueChartEntries({
  profiles,
  valuePayload,
  valueSetting,
}) {
  return sortProfilesAlphabetically(profiles || []).map((profile) => {
    const procedureKey = profileProcedureKey(profile);
    const levels = profile.levels ?? parseLevelSeriesKey(profile.surgery)?.levels ?? 0;
    const expected = computeProbabilisticExpectedValue({
      payload: valuePayload,
      procedureKey,
      levels,
      setting: valueSetting,
      risks: profile.risks,
    });
    const outcomes = normalizeProfileOutcomes(profile);
    const {
      painReduction,
      dysfunctionReduction,
      costPerPainPoint,
      costPerDysfunctionPoint,
    } = computeCostPerPointMetrics(expected, outcomes);
    return {
      surgery: profile.surgery,
      name: profile.label || formatLevelSeriesLabel(procedureKey, levels),
      axisLabel: formatSurgeryChartAxisLabel(procedureKey, profile.label),
      procedureKey,
      levels,
      striped: isMultilevel(levels),
      fill: getSpreadsheetSurgeryColor(procedureKey),
      painReduction,
      dysfunctionReduction,
      costPerPainPoint,
      costPerDysfunctionPoint,
      ...expected,
    };
  });
}

const EMPTY_VALUE_CHART = [];

function ValueChartPanel({
  chartModel: chartModelProp,
  showRange = false,
  fillAvailable = false,
  sharedScaleMax = 0,
}) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const expanded = fillAvailable;
  const sizeKey = expanded ? 'expanded' : 'compact';
  const size = RISK_CHART_SIZE[sizeKey];
  const chartModel = chartModelProp || EMPTY_VALUE_CHART;
  const hasRange = Boolean(showRange);

  useEffect(() => {
    if (!chartModel.length) {
      d3.select(svgRef.current).selectAll('*').remove();
      return undefined;
    }

    const draw = () => {
      const rangeLabelFont = scaleRiskBarPctFont(expanded ? '15px' : '12px', chartModel.length);
      const rangeLineHeight = fontPx(rangeLabelFont) * 1.18;
      const extraTop = hasRange ? rangeLineHeight * 3 + 10 : 8;
      const cppChipHeight = fontPx(rangeLabelFont) + 10;
      const cppLineCount = chartModel.reduce(
        (max, entry) => Math.max(max, valueChartCostPerPointCount(entry)),
        0,
      );
      const cppStackHeight = cppLineCount > 0 ? cppLineCount * (cppChipHeight + 4) - 4 : 0;
      const cppLift = rangeLineHeight * 1.5;
      const margin = { ...size.margin, top: size.margin.top + extraTop };
      margin.bottom = riskChartBottomMargin(
        chartModel.map((entry) => entry.axisLabel || entry.name),
        size.categoryAxisFont,
      );
      const containerWidth = containerRef.current?.clientWidth || 720;
      const innerMin = chartModel.length * size.innerMinPerEntry + 80;
      const totalWidth = fillAvailable
        ? containerWidth
        : Math.max(containerWidth, innerMin + margin.left + margin.right);
      const containerHeight = containerRef.current?.clientHeight || 0;
      const totalHeight = fillAvailable ? containerHeight || size.totalHeight : size.totalHeight;
      const width = Math.max(1, totalWidth - margin.left - margin.right);
      const height = Math.max(1, totalHeight - margin.top - margin.bottom);

      const values = valueChartScaleValues(chartModel);
      const dataMax = sharedScaleMax > 0
        ? sharedScaleMax
        : Math.max(0, ...values);
      const chipHeadroomPx = cppLineCount > 0 ? cppStackHeight + cppLift + 8 : 0;
      let paddedMax = dataMax > 0 ? dataMax * 1.12 : 1;
      if (dataMax > 0 && chipHeadroomPx > 0 && height > chipHeadroomPx + 8) {
        paddedMax = Math.max(paddedMax, dataMax * (height / (height - chipHeadroomPx)));
      }
      const { ticks: yTicks, top: yMax } = chooseValueAxisTicks(paddedMax);

      d3.select(svgRef.current).selectAll('*').remove();
      const svg = d3
        .select(svgRef.current)
        .attr('viewBox', `0 0 ${totalWidth} ${totalHeight}`)
        .attr('width', fillAvailable ? '100%' : totalWidth)
        .attr('height', fillAvailable ? '100%' : totalHeight)
        .attr('preserveAspectRatio', fillAvailable ? 'none' : 'xMinYMid meet')
        .style('overflow', 'visible');

      const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);
      const x = d3
        .scaleBand()
        .domain(chartModel.map((entry) => entry.name))
        .range([0, width])
        .paddingInner(0.28)
        .paddingOuter(0.08);
      const y = d3.scaleLinear().domain([0, yMax]).range([height, 0]);

      const xAxis = g
        .append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x));
      xAxis.selectAll('text').style('text-anchor', 'middle');
      styleChartAxis(xAxis, size.categoryAxisFont);
      wrapRiskAxisTickWords(xAxis.selectAll('.tick text'), {
        anchor: 'middle',
        stack: 'down',
        getLabel: (d) => {
          const entry = chartModel.find((item) => item.name === d);
          return entry?.axisLabel || d;
        },
      });

      styleChartAxis(
        g.append('g').call(d3.axisLeft(y).tickValues(yTicks).tickFormat(formatUsdAxisTick)),
        size.axisFont,
      );
      g.selectAll('.grid-line')
        .data(yTicks)
        .enter()
        .append('line')
        .attr('x1', 0)
        .attr('x2', width)
        .attr('y1', (d) => y(d))
        .attr('y2', (d) => y(d))
        .attr('stroke', '#e9ecef')
        .attr('stroke-dasharray', '3,3');

      const barWidth = x.bandwidth();
      const labelFont = scaleRiskBarPctFont(size.barPct, chartModel.length);

      chartModel.forEach((entry) => {
        const group = g
          .append('g')
          .attr('class', 'jd-value-bar')
          .attr('transform', `translate(${x(entry.name)},0)`);
        const fill = resolveBarFill(svg, entry);
        if (!entry.payable || entry.central == null) {
          group
            .append('rect')
            .attr('x', 0)
            .attr('y', height - 3)
            .attr('width', barWidth)
            .attr('height', 3)
            .attr('rx', 2)
            .attr('fill', fill)
            .attr('opacity', 0.35);
          group
            .append('text')
            .attr('class', 'bar-label')
            .attr('x', barWidth / 2)
            .attr('y', height - 10)
            .attr('text-anchor', 'middle')
            .attr('font-size', metricBarLabelFont(labelFont, 'N/A'))
            .attr('font-weight', 700)
            .attr('fill', JD_TEXT)
            .text('N/A');
          if (entry.naReason) {
            group.append('title').text(entry.naReason);
          }
          return;
        }

        const baseHeight = Math.max(0, y(0) - y(entry.base));
        const centralY = y(entry.central);
        group
          .append('rect')
          .attr('x', 0)
          .attr('y', y(entry.base))
          .attr('width', barWidth)
          .attr('height', baseHeight)
          .attr('rx', 2)
          .attr('fill', fill)
          .attr('opacity', 0.92);
        if (entry.incrementalCentral > 0) {
          group
            .append('rect')
            .attr('x', 0)
            .attr('y', centralY)
            .attr('width', barWidth)
            .attr('height', Math.max(0, y(entry.base) - centralY))
            .attr('rx', 2)
            .attr('fill', fill)
            .attr('opacity', 0.45);
        }

        if (hasRange && entry.high != null && entry.low != null && entry.high !== entry.low) {
          const midX = barWidth / 2;
          group
            .append('line')
            .attr('x1', midX)
            .attr('x2', midX)
            .attr('y1', y(entry.high))
            .attr('y2', y(entry.low))
            .attr('stroke', JD_TEXT)
            .attr('stroke-width', 1.5);
          [entry.high, entry.low].forEach((value) => {
            group
              .append('line')
              .attr('x1', midX - Math.min(8, barWidth * 0.22))
              .attr('x2', midX + Math.min(8, barWidth * 0.22))
              .attr('y1', y(value))
              .attr('y2', y(value))
              .attr('stroke', JD_TEXT)
              .attr('stroke-width', 1.5);
          });
        }

        const barPeakY = hasRange && entry.high != null
          ? Math.min(centralY, y(entry.high))
          : centralY;
        const longestRangeLabel = [
          `High ${formatUsd(entry.high)}`,
          `Central ${formatUsd(entry.central)}`,
          `Low ${formatUsd(entry.low)}`,
        ].reduce((best, text) => (text.length > best.length ? text : best), '');
        const hasCppLabels = valueChartCostPerPointCount(entry) > 0;
        const valueFont = fitFontToText(
          hasRange ? rangeLabelFont : scaleRiskBarPctFont(size.barPct, chartModel.length),
          barWidth,
          hasRange ? longestRangeLabel : formatUsd(entry.central),
          expanded ? 9 : 7,
        );

        const metricFont = fitFontToText(
          rangeLabelFont,
          Math.max(72, barWidth * 0.9),
          [
            formatCostPerPointCaption(entry.costPerPainPoint, 'pain'),
            formatCostPerPointCaption(entry.costPerDysfunctionPoint, 'dysfunction'),
          ].filter(Boolean).reduce((best, text) => (text.length > best.length ? text : best), 'W'),
          expanded ? 8 : 7,
        );
        let valueLabelNode = null;
        if (hasRange && entry.low != null && entry.high != null) {
          valueLabelNode = appendValueRangeDollarLabels(group, {
            x: barWidth / 2,
            yHigh: barPeakY,
            high: entry.high,
            central: entry.central,
            low: entry.low,
            fontSize: valueFont,
          });
        } else {
          const label = formatUsd(entry.central);
          valueLabelNode = group
            .append('text')
            .attr('class', 'bar-label')
            .attr('x', barWidth / 2)
            .attr('y', centralY - 6)
            .attr('text-anchor', 'middle')
            .attr('font-size', metricBarLabelFont(valueFont, label))
            .attr('font-weight', 700)
            .attr('fill', JD_TEXT)
            .text(label);
        }
        const labelBox = valueLabelNode?.node?.()?.getBBox?.();
        if (labelBox && hasCppLabels) {
          appendCostPerPointBarLabels(group, {
            entry,
            fontSize: metricFont,
            centerX: barWidth / 2,
            startY: labelBox.y - cppLift - cppStackHeight,
          });
        }
      });
    };

    draw();
    return observeElementResize(containerRef.current, draw);
  }, [chartModel, fillAvailable, hasRange, sharedScaleMax, size, sizeKey]);

  if (!chartModel.length) {
    return <p className="text-muted small mb-0">Select a surgery to compare reimbursement.</p>;
  }

  return (
    <div
      ref={containerRef}
      className="jd-chart-container"
      style={{
        width: '100%',
        overflowX: fillAvailable ? 'hidden' : 'auto',
        overflowY: 'visible',
      }}
    >
      <svg
        ref={svgRef}
        style={{
          display: 'block',
          width: fillAvailable ? '100%' : undefined,
          height: fillAvailable ? '100%' : 'auto',
          overflow: 'visible',
        }}
      />
    </div>
  );
}

function ExpectedValueBreakdownPanel({ chartModel }) {
  const payableEntries = useMemo(
    () => (chartModel || []).filter((entry) => entry.payable && entry.base != null),
    [chartModel],
  );
  const [selectedSurgery, setSelectedSurgery] = useState(null);

  useEffect(() => {
    if (!payableEntries.length) {
      setSelectedSurgery(null);
      return;
    }
    if (!payableEntries.some((entry) => entry.surgery === selectedSurgery)) {
      setSelectedSurgery(payableEntries[0].surgery);
    }
  }, [payableEntries, selectedSurgery]);

  const entry = payableEntries.find((item) => item.surgery === selectedSurgery) || payableEntries[0];
  const breakdown = useMemo(
    () => [...(entry?.breakdown || [])].sort(
      (a, b) => (b.contributionCentral || 0) - (a.contributionCentral || 0),
    ),
    [entry],
  );

  if (!entry) {
    return (
      <aside className="jd-expected-value-breakdown" aria-label="Value calculation">
        <div className="jd-expected-value-breakdown-title">Calculation</div>
        <p className="text-muted small mb-0">No payable value for this setting.</p>
      </aside>
    );
  }

  return (
    <aside className="jd-expected-value-breakdown" aria-label="Value calculation">
      <div className="jd-expected-value-breakdown-title">Calculation</div>
      {payableEntries.length > 1 && (
        <Form.Select
          size="sm"
          className="jd-expected-value-surgery-select mb-2"
          value={entry.surgery}
          onChange={(event) => setSelectedSurgery(event.target.value)}
          aria-label="Surgery for value breakdown"
        >
          {payableEntries.map((item) => (
            <option key={item.surgery} value={item.surgery}>
              {item.name}
            </option>
          ))}
        </Form.Select>
      )}
      <div className="jd-ev-breakdown-section">
        <div className="jd-ev-breakdown-row jd-ev-breakdown-row--total">
          <div className="jd-ev-breakdown-label">
            <span className="jd-ev-breakdown-name">Cost per 1 point reduction in pain</span>
            <span className="jd-ev-breakdown-formula">
              {entry.painReduction > 0
                ? `Expected total ÷ ${formatPointReduction(entry.painReduction)} pain reduction`
                : 'No pain-point reduction for this procedure'}
            </span>
          </div>
          <span className="jd-ev-breakdown-amount">{formatUsd(entry.costPerPainPoint)}</span>
        </div>
        <div className="jd-ev-breakdown-row jd-ev-breakdown-row--total">
          <div className="jd-ev-breakdown-label">
            <span className="jd-ev-breakdown-name">Cost per 1 point reduction in dysfunction</span>
            <span className="jd-ev-breakdown-formula">
              {entry.dysfunctionReduction > 0
                ? `Expected total ÷ ${formatPointReduction(entry.dysfunctionReduction)} dysfunction reduction`
                : 'No dysfunction-point reduction for this procedure'}
            </span>
          </div>
          <span className="jd-ev-breakdown-amount">{formatUsd(entry.costPerDysfunctionPoint)}</span>
        </div>
      </div>
      <div className="jd-ev-breakdown-section">
        <div className="jd-ev-breakdown-row jd-ev-breakdown-row--base">
          <span>Base reimbursement</span>
          <span className="jd-ev-breakdown-amount">{formatUsd(entry.base)}</span>
        </div>
      </div>
      <div className="jd-ev-breakdown-section">
        <div className="jd-ev-breakdown-heading">Complications</div>
        <p className="jd-ev-breakdown-note text-muted small">
          Each row is event probability × minor/major share × midpoint reimbursement.
        </p>
        {breakdown.length === 0 ? (
          <p className="text-muted small mb-0">No complication costs mapped for this procedure.</p>
        ) : (
          breakdown.map((row) => (
            <div
              key={row.key}
              className={`jd-ev-breakdown-row${row.contributionCentral > 0 ? '' : ' jd-ev-breakdown-row--zero'}`}
            >
              <div className="jd-ev-breakdown-label">
                <span className="jd-ev-breakdown-name">{row.label}</span>
                <span className="jd-ev-breakdown-formula">
                  {formatExpectedValueFormula(row)}
                </span>
              </div>
              <span className="jd-ev-breakdown-amount">{formatUsd(row.contributionCentral)}</span>
            </div>
          ))
        )}
      </div>
      <div className="jd-ev-breakdown-section jd-ev-breakdown-total">
        <div className="jd-ev-breakdown-row">
          <span>Complication add-on</span>
          <span className="jd-ev-breakdown-amount">{formatUsd(entry.incrementalCentral)}</span>
        </div>
        <div className="jd-ev-breakdown-row jd-ev-breakdown-row--total">
          <span>Expected total</span>
          <span className="jd-ev-breakdown-amount">{formatUsd(entry.central)}</span>
        </div>
      </div>
    </aside>
  );
}

function RateSourceToggle({ value, onChange }) {
  return (
    <div className="jd-rate-source-toggle" role="group" aria-label="Research or this practice">
      <button
        type="button"
        className={value === 'research' ? 'is-selected' : undefined}
        aria-pressed={value === 'research'}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onChange('research');
        }}
      >
        Research
      </button>
      <button
        type="button"
        className={value === 'practice' ? 'is-selected' : undefined}
        aria-pressed={value === 'practice'}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onChange('practice');
        }}
      >
        This practice
      </button>
    </div>
  );
}

function ChartLevelLegend({
  activeChartTab,
}) {
  if (activeChartTab === JD_CHART_TAB_KEYS.VALUE) {
    return (
      <div className="jd-chart-level-legend jd-chart-level-legend--value" aria-label="Chart legend">
        <span className="jd-chart-level-legend-row">
          <span className="jd-procedure-level-legend-item">
            <span className="jd-value-legend-swatch jd-value-legend-swatch--base" aria-hidden />
            Base reimbursement
          </span>
          <span className="jd-procedure-level-legend-item">
            <span className="jd-value-legend-swatch jd-value-legend-swatch--add" aria-hidden />
            Estimated complication cost
          </span>
        </span>
        <span className="jd-procedure-level-legend-item">
          <span className="jd-value-legend-swatch jd-value-legend-swatch--outline" aria-hidden />
          Cost per 1 point reduction in pain
        </span>
        <span className="jd-procedure-level-legend-item">
          <span className="jd-value-legend-swatch jd-value-legend-swatch--fill-muted" aria-hidden />
          Cost per 1 point reduction in dysfunction
        </span>
      </div>
    );
  }

  return null;
}

function ExpectedValueTabPanel({
  profiles,
  valuePayload,
  valueStatus,
  valueSetting,
  onValueSettingChange,
  fillAvailable = false,
}) {
  const chartModelsBySetting = useMemo(() => {
    const bySetting = {};
    VALUE_SETTINGS.forEach((setting) => {
      bySetting[setting.key] = buildExpectedValueChartEntries({
        profiles,
        valuePayload,
        valueSetting: setting.key,
      });
    });
    return bySetting;
  }, [profiles, valuePayload]);
  const chartModel = chartModelsBySetting[valueSetting] || EMPTY_VALUE_CHART;
  const sharedScaleMax = useMemo(
    () => maxValueChartScale(chartModelsBySetting),
    [chartModelsBySetting],
  );

  if (valueStatus === 'loading' || valueStatus === 'idle') {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" />
        <span className="ms-2 text-muted">Loading value data…</span>
      </div>
    );
  }
  if (valueStatus === 'error' || !valuePayload) {
    return (
      <Alert variant="warning" className="py-2 mb-0">
        Value is unavailable.
      </Alert>
    );
  }

  return (
    <div className="jd-expected-value-panel">
      <div className="jd-expected-value-chart-stage">
        <div className="jd-value-toolbar">
          <Nav
            variant="pills"
            activeKey={valueSetting}
            onSelect={(key) => {
              if (!key) return;
              onValueSettingChange(key);
            }}
            className="jd-benefits-subtabs"
            aria-label="Care setting"
          >
            {VALUE_SETTINGS.map((setting) => (
              <Nav.Item key={setting.key}>
                <Nav.Link eventKey={setting.key} title={setting.title}>
                  {setting.label}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </div>
        <ValueChartPanel
          chartModel={chartModel}
          showRange={false}
          fillAvailable={fillAvailable}
          sharedScaleMax={sharedScaleMax}
        />
      </div>
      <ExpectedValueBreakdownPanel chartModel={chartModel} />
    </div>
  );
}

function JointDecisionsChartTabs({
  chartTabs,
  activeChartTab,
  onActiveChartTabChange,
  profiles,
  scenarioProfiles = [],
  riskLabelsByType,
  spreadsheetControls,
  scenarioSpreadsheetControls = null,
  practiceOverlayFactor = null,
  practiceStats = null,
  practiceOutcomeChangeScale = null,
  chartOrientation,
  fillAvailable = false,
  useDynamicRiskScale,
  onToggleRiskScale,
  includeRiskControl = true,
  selectedRegion,
  thresholdFilterEnabled = true,
  showThresholdDetails = false,
  tabNotifications = {},
  valuePayload = null,
  valueStatus = 'idle',
  valueSetting = 'inpatient',
  onValueSettingChange,
}) {
  const [benefitsSubTab, setBenefitsSubTab] = useState(JD_CHART_TAB_KEYS.PAIN);
  const activeTab = chartTabs.find((tab) => tab.key === activeChartTab) ?? chartTabs[0];
  if (!activeTab) return null;

  const renderRiskPanel = (labels, { includeControl = false, valueKind = 'percent' } = {}) => (
    <ComplicationRiskChartPanel
      riskLabels={labels}
      profiles={profiles}
      scenarioProfiles={scenarioProfiles}
      spreadsheetControls={spreadsheetControls}
      scenarioSpreadsheetControls={scenarioSpreadsheetControls}
      practiceOverlayFactor={practiceOverlayFactor}
      practiceStats={practiceStats}
      chartOrientation={chartOrientation}
      fillAvailable={fillAvailable}
      useDynamicScale={useDynamicRiskScale}
      onToggleScale={onToggleRiskScale}
      includeControl={includeControl}
      valueKind={valueKind}
      thresholdFilterEnabled={thresholdFilterEnabled}
      showThresholdDetails={showThresholdDetails}
    />
  );

  const renderActiveChart = () => {
    switch (activeTab.key) {
      case JD_CHART_TAB_KEYS.BENEFITS:
        return (
          <div className={`jd-benefits-panel ${fillAvailable ? 'jd-benefits-panel--fill' : ''}`}>
            <Nav
              variant="pills"
              activeKey={benefitsSubTab}
              onSelect={(key) => {
                if (!key) return;
                setBenefitsSubTab(key);
              }}
              className="jd-benefits-subtabs flex-shrink-0"
              aria-label="Benefits metrics"
            >
              <Nav.Item>
                <Nav.Link eventKey={JD_CHART_TAB_KEYS.PAIN}>
                  Pain
                  {tabNotifications.pain > 0 && (
                    <Badge bg="success" pill className="jd-tab-notification-badge">
                      {tabNotifications.pain}
                    </Badge>
                  )}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey={JD_CHART_TAB_KEYS.DISABILITY}>
                  Dysfunction
                  {tabNotifications.function > 0 && (
                    <Badge bg="success" pill className="jd-tab-notification-badge">
                      {tabNotifications.function}
                    </Badge>
                  )}
                </Nav.Link>
              </Nav.Item>
            </Nav>
            <div className="jd-benefits-chart">
              <ProcedureOutcomeChartPanel
                profiles={profiles}
                scenarioProfiles={scenarioProfiles}
                chartOrientation={chartOrientation}
                outcomeType={benefitsSubTab}
                fillAvailable={fillAvailable}
                regionKey={selectedRegion}
                thresholdFilterEnabled={thresholdFilterEnabled}
                showThresholdDetails={showThresholdDetails}
                practiceOutcomeChangeScale={practiceOutcomeChangeScale}
                practiceStats={practiceStats}
              />
            </div>
          </div>
        );
      case JD_CHART_TAB_KEYS.MEDICAL_RISKS:
        return renderRiskPanel(riskLabelsByType.medical, { includeControl: includeRiskControl });
      case JD_CHART_TAB_KEYS.PROCEDURE_RISKS:
        return renderRiskPanel(riskLabelsByType.procedure, { includeControl: false });
      case JD_CHART_TAB_KEYS.RECOVERY:
        return renderRiskPanel(riskLabelsByType.recovery, {
          includeControl: false,
          valueKind: 'absolute',
        });
      case JD_CHART_TAB_KEYS.LONG_TERM:
        return renderRiskPanel(riskLabelsByType.longTerm, { includeControl: false });
      case JD_CHART_TAB_KEYS.VALUE:
        return (
          <ExpectedValueTabPanel
            profiles={profiles}
            valuePayload={valuePayload}
            valueStatus={valueStatus}
            valueSetting={valueSetting}
            onValueSettingChange={onValueSettingChange}
            fillAvailable={fillAvailable}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={`jd-chart-tabs ${fillAvailable ? 'jd-chart-tabs--fill' : ''}`}>
      <div className="jd-chart-tab-content">{renderActiveChart()}</div>
    </div>
  );
}

const PROCEDURE_CONFIG_REGIONS = [
  { value: 'CERVICAL', label: 'Cervical' },
  { value: 'THORACIC', label: 'Thoracic' },
  { value: 'LUMBAR', label: 'Lumbar' },
];

function normalizeProcedureCatalog(entries) {
  return (entries || [])
    .map((item) => ({
      ...item,
      label: getProcedureDisplayLabel(item.key),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

function buildProcedureCatalogFromLabels() {
  return normalizeProcedureCatalog(
    Object.entries(SPREADSHEET_SURGERY_DISPLAY_LABELS)
      .filter(([key]) => !key.includes(' ') && key === key.toLowerCase())
      .map(([key]) => ({
        key,
        region: procedureRegion(key),
        region_label: PROCEDURE_CONFIG_REGIONS.find((r) => r.value === procedureRegion(key))?.label
          || procedureRegion(key),
      })),
  );
}

function ProcedureConfigModal({
  show,
  onHide,
  catalog,
  hiddenProcedures,
  onSave,
  saving,
  error,
}) {
  const [visibleDraft, setVisibleDraft] = useState(new Set());

  useEffect(() => {
    if (!show) return;
    const hidden = new Set(hiddenProcedures);
    setVisibleDraft(new Set(catalog.map((item) => item.key).filter((key) => !hidden.has(key))));
  }, [show, catalog, hiddenProcedures]);

  const groupedCatalog = useMemo(() => {
    const groups = PROCEDURE_CONFIG_REGIONS.map((region) => ({
      ...region,
      procedures: catalog.filter((item) => item.region === region.value),
    }));
    return groups.filter((group) => group.procedures.length > 0);
  }, [catalog]);

  const handleToggle = (procedureKey, checked) => {
    setVisibleDraft((prev) => {
      const next = new Set(prev);
      if (checked) next.add(procedureKey);
      else next.delete(procedureKey);
      return next;
    });
  };

  const handleRegionToggle = (regionValue, checked) => {
    const regionKeys = catalog
      .filter((item) => item.region === regionValue)
      .map((item) => item.key);
    setVisibleDraft((prev) => {
      const next = new Set(prev);
      regionKeys.forEach((key) => {
        if (checked) next.add(key);
        else next.delete(key);
      });
      return next;
    });
  };

  const handleSave = async () => {
    const hidden = catalog
      .map((item) => item.key)
      .filter((key) => !visibleDraft.has(key));
    await onSave(hidden);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Procedure visibility</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3">
          Hide procedures your practice does not offer. These settings apply to everyone in your
          organization.
        </p>
        {error && (
          <Alert variant="danger" className="py-2">
            {error}
          </Alert>
        )}
        {groupedCatalog.map((group) => {
          const regionKeys = group.procedures.map((item) => item.key);
          const visibleCount = regionKeys.filter((key) => visibleDraft.has(key)).length;
          const allVisible = visibleCount === regionKeys.length;
          const noneVisible = visibleCount === 0;
          return (
            <div key={group.value} className="jd-procedure-config-region">
              <div className="jd-procedure-config-region-header">
                <h6 className="mb-0">{group.label}</h6>
                <div className="jd-procedure-config-region-actions">
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0"
                    onClick={() => handleRegionToggle(group.value, true)}
                    disabled={allVisible}
                  >
                    Show all
                  </Button>
                  <span aria-hidden>·</span>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0"
                    onClick={() => handleRegionToggle(group.value, false)}
                    disabled={noneVisible}
                  >
                    Hide all
                  </Button>
                </div>
              </div>
              <div className="jd-procedure-config-options">
                {group.procedures.map((item) => (
                  <Form.Check
                    key={item.key}
                    type="checkbox"
                    id={`jd-procedure-config-${item.key}`}
                    className="jd-procedure-config-check"
                    label={getProcedureDisplayLabel(item.key)}
                    checked={visibleDraft.has(item.key)}
                    onChange={(e) => handleToggle(item.key, e.target.checked)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function JdTitleTabsCarousel({ children, activeKey }) {
  const scrollerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;

    const updateOverflow = () => {
      const maxScroll = el.scrollWidth - el.clientWidth;
      setCanScrollLeft(el.scrollLeft > 2);
      setCanScrollRight(maxScroll - el.scrollLeft > 2);
    };

    updateOverflow();
    el.addEventListener('scroll', updateOverflow, { passive: true });
    const stopResize = observeElementResize(el, updateOverflow);
    const mutations = new MutationObserver(updateOverflow);
    mutations.observe(el, { childList: true, subtree: true, characterData: true });
    return () => {
      el.removeEventListener('scroll', updateOverflow);
      stopResize();
      mutations.disconnect();
    };
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const active = el.querySelector('.nav-link.active');
    if (!active) return;
    const rowRect = el.getBoundingClientRect();
    const tabRect = active.getBoundingClientRect();
    if (tabRect.left < rowRect.left + 8) {
      el.scrollBy({ left: tabRect.left - rowRect.left - 12, behavior: 'smooth' });
    } else if (tabRect.right > rowRect.right - 8) {
      el.scrollBy({ left: tabRect.right - rowRect.right + 12, behavior: 'smooth' });
    }
  }, [activeKey]);

  const scrollByPage = (direction, event) => {
    event.preventDefault();
    event.stopPropagation();
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction * Math.max(160, el.clientWidth * 0.7),
      behavior: 'smooth',
    });
  };

  return (
    <div
      className={`jd-title-tabs-carousel${canScrollLeft ? ' is-overflow-left' : ''}${
        canScrollRight ? ' is-overflow-right' : ''
      }`}
    >
      {canScrollLeft && (
        <button
          type="button"
          className="jd-title-tabs-arrow jd-title-tabs-arrow--left"
          aria-label="Show earlier tabs"
          onClick={(event) => scrollByPage(-1, event)}
        >
          <ChevronLeft size={16} />
        </button>
      )}
      <div className="jd-title-tabs-row" ref={scrollerRef}>
        {children}
      </div>
      {canScrollRight && (
        <button
          type="button"
          className="jd-title-tabs-arrow jd-title-tabs-arrow--right"
          aria-label="Show more tabs"
          onClick={(event) => scrollByPage(1, event)}
        >
          <ChevronRight size={16} />
        </button>
      )}
    </div>
  );
}

function JointDecisionsDemoReferences({ references }) {
  if (!references?.length) return null;
  return (
    <section className="jd-demo-references" aria-label="References">
      <h2 className="jd-demo-references-title">References</h2>
      <ol className="jd-demo-references-list">
        {references.map((citation) => (
          <li key={citation}>{citation}</li>
        ))}
      </ol>
    </section>
  );
}

/**
 * Spreadsheet-backed joint decision risks.
 * 1) Fast-load patient characteristics for filter dropdowns.
 * 2) Score charts from OLS regression_models.json (client-side).
 * CDN sex×age strata slices are gated by LOAD_STRATA_CDN_SLICE (off for now).
 */
function formatDecisionTimestamp(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function mapDecisionCharacteristicsToProfile(chars) {
  if (!chars || typeof chars !== 'object') return {};
  return {
    height: chars.height_inches,
    weight: chars.weight_lbs,
    bmi: chars.bmi,
    bmi_bucket: chars.bmi_bucket_label,
    smoking: chars.smoking,
    diabetes_bucket: chars.diabetes,
    antithrombotic: chars.antithrombotic,
    insurance: chars.insurance,
    revision_surgery: chars.revision_surgery,
  };
}

function JointDecisionsSpreadsheet({
  patient,
  recommendation,
  appointmentId,
  onEngage,
  layoutAnchor = 'primary',
  onDecideSubmit,
  onStageReady,
  enrollmentSurgeryPrompt = 0,
}) {
  const cardRef = useRef(null);
  const headerFiltersRef = useRef(null);
  const prevIncompleteSetupRef = useRef(null);
  const setupScrollTimerRef = useRef(null);
  const strataSliceLoadedKeyRef = useRef(null);
  const pendingDecisionHydrationRef = useRef(null);
  const [preload, setPreload] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedBmi, setSelectedBmi] = useState('');
  const [heightFeetInput, setHeightFeetInput] = useState('');
  const [heightInchesInput, setHeightInchesInput] = useState('');
  const [weightInput, setWeightInput] = useState('');
  const [baselineWeightLbs, setBaselineWeightLbs] = useState(null);
  const [baselineBmiValue, setBaselineBmiValue] = useState(null);
  const [bmiInput, setBmiInput] = useState('');
  const [selectedSmoking, setSelectedSmoking] = useState('NO');
  const [selectedDiabetes, setSelectedDiabetes] = useState('0');
  const [selectedAntithrombotic, setSelectedAntithrombotic] = useState('');
  const [selectedInsurance, setSelectedInsurance] = useState('');
  const [selectedRevisionSurgery, setSelectedRevisionSurgery] = useState('0');
  const [selectedPickerLevels, setSelectedPickerLevels] = useState('0');
  const [selectedSurgeries, setSelectedSurgeries] = useState([]);
  const [baselineStratumFilters, setBaselineStratumFilters] = useState(null);
  const [stableFactorsExpanded, setStableFactorsExpanded] = useState(false);
  const [isLoadingCharacteristics, setIsLoadingCharacteristics] = useState(false);
  const [isLoadingStrata, setIsLoadingStrata] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeChartTab, setActiveChartTab] = useState(JD_CHART_TAB_KEYS.BENEFITS);
  // Per-tab magnifier: defaults off (0–100); retains last setting when revisiting a tab.
  const [dynamicRiskScaleByTab, setDynamicRiskScaleByTab] = useState({});
  const [includeRiskControl, setIncludeRiskControl] = useState(true);
  const [showModificationOverlay, setShowModificationOverlay] = useState(true);
  const [rateSource, setRateSource] = useState('research');
  const [practiceStats, setPracticeStats] = useState(null);
  // Default to OLS regression; strata CDN path kept as fallback / future toggle.
  const [riskSourceMode, setRiskSourceMode] = useState('regression'); // 'strata' | 'regression'
  const prevRiskSourceModeRef = useRef('regression');
  const [regressionModels, setRegressionModels] = useState(null);
  const [regressionModelsStatus, setRegressionModelsStatus] = useState('idle'); // idle|loading|ready|error
  const [hasEngagedJointDecisions, setHasEngagedJointDecisions] = useState(false);
  const [normalPanelHeight, setNormalPanelHeight] = useState(null);
  const [hiddenProcedures, setHiddenProcedures] = useState([]);
  const [procedureCatalog, setProcedureCatalog] = useState(buildProcedureCatalogFromLabels);
  const [showProcedureConfig, setShowProcedureConfig] = useState(false);
  const [procedureConfigSaving, setProcedureConfigSaving] = useState(false);
  const [procedureConfigError, setProcedureConfigError] = useState(null);
  const [valuePayload, setValuePayload] = useState(null);
  const [valueStatus, setValueStatus] = useState('idle');
  const [valueSetting, setValueSetting] = useState('inpatient');
  const [showDecideModal, setShowDecideModal] = useState(false);
  const [enrollmentSurgeryNotice, setEnrollmentSurgeryNotice] = useState(false);
  const enrollmentSurgeryPromptSeen = useRef(0);
  const [decideSurgeryKey, setDecideSurgeryKey] = useState('');
  const [decideSurgeryDate, setDecideSurgeryDate] = useState(defaultDecideSurgeryDate);
  const [decideMedications, setDecideMedications] = useState(() => [newDecideMedRow()]);
  const [decideError, setDecideError] = useState(null);
  const [reviewedSurgeryKeys, setReviewedSurgeryKeys] = useState([]);
  const [viewedChartTabs, setViewedChartTabs] = useState([JD_CHART_TAB_KEYS.BENEFITS]);
  const [decideModalTab, setDecideModalTab] = useState('procedure');
  const [encounterNoteType, setEncounterNoteType] = useState(ENCOUNTER_NOTE_TYPE.SURGERY_DECISION);
  const [encounterNotes, setEncounterNotes] = useState(emptyEncounterNoteDrafts);
  const [encounterNotesEdited, setEncounterNotesEdited] = useState(emptyEncounterNoteEdited);
  const [procedureLevelsText, setProcedureLevelsText] = useState('');
  const [encounterDecision, setEncounterDecision] = useState(ENCOUNTER_DECISION.SURGERY);
  const [approvedDecision, setApprovedDecision] = useState(null);
  const [showApprovedDecisionModal, setShowApprovedDecisionModal] = useState(false);
  const [approvedDraftNoteType, setApprovedDraftNoteType] = useState(ENCOUNTER_NOTE_TYPE.SURGERY_DECISION);
  const [approvedDraftNotes, setApprovedDraftNotes] = useState(emptyEncounterNoteDrafts);
  const [approvedDraftDecision, setApprovedDraftDecision] = useState(ENCOUNTER_DECISION.SURGERY);
  const [approvedDraftSurgeryKey, setApprovedDraftSurgeryKey] = useState('');
  const [approvedDraftSurgeryDate, setApprovedDraftSurgeryDate] = useState('');
  const [approvedSaveError, setApprovedSaveError] = useState(null);
  const [approvedSaving, setApprovedSaving] = useState(false);
  const [demoReferences, setDemoReferences] = useState([]);
  const chartOrientation = 'vertical';
  const chartAntithrombotics = useMemo(
    () => chartAntithromboticsFromRecommendation(recommendation),
    [recommendation],
  );

  useEffect(() => {
    if (!enrollmentSurgeryPrompt || enrollmentSurgeryPromptSeen.current === enrollmentSurgeryPrompt) return;
    enrollmentSurgeryPromptSeen.current = enrollmentSurgeryPrompt;
    setDecideSurgeryKey(leftoverMedManagementSurgeryToken(selectedSurgeries));
    setDecideSurgeryDate(defaultDecideSurgeryDate());
    setDecideMedications(seedDecideMedicationRows(chartAntithrombotics));
    setDecideError(null);
    setDecideModalTab('procedure');
    setEncounterNotesEdited(emptyEncounterNoteEdited());
    setEnrollmentSurgeryNotice(true);
    setShowDecideModal(true);
  }, [chartAntithrombotics, enrollmentSurgeryPrompt, selectedSurgeries]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await fetchOrganizationJointDecisionsProcedureConfig();
      if (cancelled || !data) return;
      setHiddenProcedures(Array.isArray(data.hidden_procedures) ? data.hidden_procedures : []);
      if (Array.isArray(data.all_procedures) && data.all_procedures.length > 0) {
        setProcedureCatalog(normalizeProcedureCatalog(data.all_procedures));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setValueStatus('loading');
    (async () => {
      const data = await fetchJointDecisionsValue();
      if (cancelled) return;
      if (data?.rates) {
        setValuePayload(data);
        setValueStatus('ready');
      } else {
        setValuePayload(null);
        setValueStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const regionOptions = useMemo(() => {
    if (Array.isArray(preload?.regions) && preload.regions.length > 0) {
      return preload.regions.map((r) => ({
        value: r.region,
        label: r.label || r.region,
      }));
    }
    return SPREADSHEET_REGION_OPTIONS;
  }, [preload?.regions]);

  const heightInchesTotal = useMemo(
    () => totalInchesFromFeetInches(heightFeetInput, heightInchesInput),
    [heightFeetInput, heightInchesInput],
  );

  const resolvedBmiValue = useMemo(
    () => parsePositiveNumber(bmiInput) ?? calculateBmiFromVitals(heightInchesTotal, weightInput),
    [bmiInput, heightInchesTotal, weightInput],
  );

  const resolvedBmiBucket = useMemo(
    () => selectedBmi || calculateBmiBucketFromBmi(resolvedBmiValue),
    [selectedBmi, resolvedBmiValue],
  );

  const stratumFilters = useMemo(
    () =>
      resolveStratumFilters({
        selectedBmi,
        resolvedBmiValue,
        selectedSmoking,
        selectedDiabetes,
        selectedAntithrombotic,
        selectedInsurance,
        selectedRevisionSurgery,
      }),
    [
      selectedBmi,
      resolvedBmiValue,
      selectedSmoking,
      selectedDiabetes,
      selectedAntithrombotic,
      selectedInsurance,
      selectedRevisionSurgery,
    ],
  );
  const baselineChartFilters = baselineStratumFilters || stratumFilters;

  const bmiOptions = useMemo(() => {
    if (Array.isArray(preload?.stratum_rows) && preload.stratum_rows.length > 0) {
      const buckets = uniqueBmiOptionsFromSpannerRows(preload.stratum_rows, stratumFilters);
      if (buckets.length) {
        return buckets.map((value) => ({ value, label: bmiRangeToVerbalLabel(value) }));
      }
    }
    const buckets =
      Array.isArray(preload?.bmi_options) && preload.bmi_options.length
        ? preload.bmi_options
        : SPREADSHEET_BMI_ORDER;
    return buckets.map((value) => ({ value, label: bmiRangeToVerbalLabel(value) }));
  }, [preload?.stratum_rows, preload?.bmi_options, stratumFilters]);

  const smokingOptions = SPREADSHEET_SMOKING_OPTIONS;
  const diabetesOptions = SPREADSHEET_DIABETES_OPTIONS;
  const antithromboticOptions = SPREADSHEET_ANTITHROMBOTIC_OPTIONS;
  const insuranceOptions = SPREADSHEET_INSURANCE_OPTIONS;
  const revisionSurgeryOptions = SPREADSHEET_REVISION_OPTIONS;
  const levelsOptions = SPREADSHEET_LEVELS_OPTIONS;
  const pickerLevelsValue = encodeBucketFilter(selectedPickerLevels) ?? 0;
  const pickerIsMultilevel = isMultilevel(pickerLevelsValue);

  const activeRegionBundle = useMemo(() => {
    if (!selectedRegion) return null;

    if (
      riskSourceMode === 'regression' &&
      regressionModels?.outcomes &&
      preload?.sex_code != null &&
      preload?.age_bucket != null
    ) {
      return buildRegionBundleFromRegression(
        regressionModels,
        selectedRegion,
        baselineChartFilters,
        {
          sexCode: preload.sex_code,
          ageBucket: preload.age_bucket,
          allowedProcedureKeys: resolveOrgEnabledProcedureKeys(
            selectedRegion,
            procedureCatalog,
            hiddenProcedures,
          ),
        },
      );
    }

    if (Array.isArray(preload?.stratum_rows) && preload.stratum_rows.length > 0) {
      return buildRegionBundleFromSpannerStrata(preload.stratum_rows, selectedRegion, baselineChartFilters);
    }

    if (!preload) return null;

    const allProfiles = preload.profiles_by_surgery ?? {};
    const profilesBySurgery = {};
    const availableSurgeriesList = [];
    let spreadsheetControls = {};

    Object.entries(allProfiles).forEach(([key, profile]) => {
      if (procedureRegion(key) !== selectedRegion) return;
      // Fallback path has no level grid — map as 1–2 levels only.
      const seriesKey = makeLevelSeriesKey(key, 0);
      profilesBySurgery[seriesKey] = {
        ...profile,
        surgery: seriesKey,
        procedureKey: key,
        levels: 0,
        label: formatLevelSeriesLabel(key, 0),
      };
      if (profile?.matched !== false) {
        availableSurgeriesList.push({
          surgery: seriesKey,
          procedureKey: key,
          levels: 0,
          label: formatLevelSeriesLabel(key, 0),
          shortLabel: '1–2',
          procedureLabel: getProcedureDisplayLabel(key),
        });
        if (!Object.keys(spreadsheetControls).length && profile.controls) {
          spreadsheetControls = profile.controls;
        }
      }
    });

    availableSurgeriesList.sort((a, b) => compareProcedureKeys(a.procedureKey, b.procedureKey));

    if (!Object.keys(spreadsheetControls).length) {
      spreadsheetControls = preload.spreadsheet_controls ?? {};
    }

    return {
      available_surgeries: availableSurgeriesList,
      profiles_by_surgery: profilesBySurgery,
      spreadsheet_controls: spreadsheetControls,
      region_label:
        preload.regions?.find((r) => r.region === selectedRegion)?.label ??
        preload.region_label,
    };
  }, [
    preload,
    selectedRegion,
    baselineChartFilters,
    riskSourceMode,
    regressionModels,
    procedureCatalog,
    hiddenProcedures,
  ]);

  const availableSurgeries = activeRegionBundle?.available_surgeries ?? [];
  const hiddenProcedureSet = useMemo(() => new Set(hiddenProcedures), [hiddenProcedures]);
  const visibleAvailableSurgeries = useMemo(
    () =>
      availableSurgeries
        .filter((item) => !hiddenProcedureSet.has(item.procedureKey || parseLevelSeriesKey(item.surgery)?.procedureKey))
        .filter((item) => Number(item.levels ?? 0) === Number(pickerLevelsValue))
        .sort((a, b) =>
          compareProcedureKeys(
            a.procedureKey || parseLevelSeriesKey(a.surgery)?.procedureKey,
            b.procedureKey || parseLevelSeriesKey(b.surgery)?.procedureKey,
          ),
        ),
    [availableSurgeries, hiddenProcedureSet, pickerLevelsValue],
  );

  useEffect(() => {
    if (!patient?.id || preload?.sex_code == null || preload?.age_bucket == null) {
      return undefined;
    }
    let cancelled = false;
    const procedureKeys = [
      ...new Set(
        (availableSurgeries || [])
          .map((item) => item.procedureKey || parseLevelSeriesKey(item.surgery)?.procedureKey)
          .filter(Boolean),
      ),
    ];
    const timer = window.setTimeout(() => {
      fetchJointDecisionsPracticeStats(patient.id, {
        selectors: {
          sex: preload.sex_code,
          age_bucket: preload.age_bucket,
          bmi_bucket: stratumFilters.bmi_bucket,
          smoking: stratumFilters.smoking,
          diabetes: stratumFilters.diabetes,
          antithrombotic: stratumFilters.antithrombotic,
          workers_comp: stratumFilters.workers_comp,
          revision_surgery: stratumFilters.revision_surgery,
          levels: pickerLevelsValue,
        },
        procedureKeys,
      }).then((data) => {
        if (!cancelled) setPracticeStats(data);
      });
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    patient?.id,
    preload?.sex_code,
    preload?.age_bucket,
    stratumFilters,
    pickerLevelsValue,
    availableSurgeries,
    selectedRegion,
  ]);

  const spreadsheetControls = activeRegionBundle?.spreadsheet_controls ?? {};
  const hasCompleteVitals = (
    heightInchesTotal !== null &&
    parsePositiveNumber(weightInput) !== null
  );
  const hasExactBmi = parsePositiveNumber(bmiInput) !== null;
  const missingRequiredFactors = useMemo(
    () =>
      collectMissingRequiredFactors({
        hasExactBmi,
        hasCompleteVitals,
        selectedBmi,
        selectedAntithrombotic,
        selectedInsurance,
      }),
    [
      hasExactBmi,
      hasCompleteVitals,
      selectedBmi,
      selectedAntithrombotic,
      selectedInsurance,
    ],
  );
  const patientChartLoaded = preload != null;
  const readingPatientChart =
    Boolean(patient?.id) && !patientChartLoaded && isLoadingCharacteristics;
  const needsRequiredInput = patientChartLoaded && missingRequiredFactors.length > 0;
  const missingRequiredFactorLabels = missingRequiredFactors.map(
    (key) => REQUIRED_FACTOR_LABELS[key] || key,
  );
  const shouldShowBmiMissingState = patientChartLoaded && missingRequiredFactors.includes('bmi');
  const incompleteSetupFactors = useMemo(
    () =>
      collectIncompleteSetupFactors({
        heightFeetInput,
        heightInchesInput,
        weightInput,
        hasBmi:
          hasExactBmi ||
          hasCompleteVitals ||
          !isBlankFilterValue(selectedBmi),
        selectedInsurance,
        selectedAntithrombotic,
      }),
    [
      heightFeetInput,
      heightInchesInput,
      weightInput,
      hasExactBmi,
      hasCompleteVitals,
      selectedBmi,
      selectedInsurance,
      selectedAntithrombotic,
    ],
  );

  useEffect(() => {
    if (!needsRequiredInput) {
      prevIncompleteSetupRef.current = null;
      if (setupScrollTimerRef.current) {
        window.clearTimeout(setupScrollTimerRef.current);
        setupScrollTimerRef.current = null;
      }
      return undefined;
    }

    const incomplete = incompleteSetupFactors;
    const prev = prevIncompleteSetupRef.current;
    prevIncompleteSetupRef.current = incomplete;

    if (!prev) return undefined;

    const justCompleted = prev.filter((key) => !incomplete.includes(key));
    if (!justCompleted.length) return undefined;

    const completedIndex = Math.min(
      ...justCompleted.map((key) => SETUP_FILL_ORDER.indexOf(key)).filter((index) => index >= 0),
    );
    if (!Number.isFinite(completedIndex)) return undefined;

    const nextKey = SETUP_FILL_ORDER.slice(completedIndex + 1).find((key) => incomplete.includes(key));
    if (!nextKey) return undefined;

    const nudge = () => nudgeScrollerToFactor(headerFiltersRef.current, nextKey);
    if (setupScrollTimerRef.current) {
      window.clearTimeout(setupScrollTimerRef.current);
      setupScrollTimerRef.current = null;
    }
    if (justCompleted.some((key) => SETUP_SELECT_FACTORS.has(key))) {
      nudge();
      return undefined;
    }
    setupScrollTimerRef.current = window.setTimeout(nudge, 400);
    return () => {
      if (setupScrollTimerRef.current) {
        window.clearTimeout(setupScrollTimerRef.current);
        setupScrollTimerRef.current = null;
      }
    };
  }, [needsRequiredInput, incompleteSetupFactors]);
  const hasStrataData = Boolean(
    (Array.isArray(preload?.stratum_rows) && preload.stratum_rows.length > 0) ||
      (preload?.profiles_by_surgery && Object.keys(preload.profiles_by_surgery).length > 0) ||
      (riskSourceMode === 'regression' &&
        regressionModels?.outcomes &&
        preload?.sex_code != null &&
        preload?.age_bucket != null),
  );
  const regionLabel =
    activeRegionBundle?.region_label ??
    regionOptions.find((r) => r.value === selectedRegion)?.label ??
    selectedRegion;

  const profiles = useMemo(() => {
    return buildSelectedProfilesFromBundle(activeRegionBundle, selectedSurgeries, visibleAvailableSurgeries);
  }, [activeRegionBundle, selectedSurgeries, visibleAvailableSurgeries]);

  const modifiableActionGroups = useMemo(
    () => buildModifiableActionGroups(baselineChartFilters, bmiOptions),
    [baselineChartFilters, bmiOptions],
  );

  const riskLabelsByType = useMemo(
    () => ({
      medical: MEDICAL_RISK_LABELS,
      procedure: PROCEDURE_RISK_LABELS,
      recovery: RECOVERY_METRIC_LABELS,
      longTerm: LONG_TERM_RISK_LABELS,
    }),
    [],
  );

  const chartTabs = useMemo(
    () => [
      { key: JD_CHART_TAB_KEYS.BENEFITS, label: 'Benefits' },
      { key: JD_CHART_TAB_KEYS.MEDICAL_RISKS, label: 'Medical Risks' },
      { key: JD_CHART_TAB_KEYS.PROCEDURE_RISKS, label: 'Procedural Risks' },
      { key: JD_CHART_TAB_KEYS.RECOVERY, label: 'Recovery' },
      { key: JD_CHART_TAB_KEYS.LONG_TERM, label: 'Long-Term' },
      { key: JD_CHART_TAB_KEYS.VALUE, label: 'Value' },
    ],
    [],
  );

  useEffect(() => {
    if (!patient?.id) {
      setPreload(null);
      setSelectedRegion(null);
      setSelectedBmi('');
      setHeightFeetInput('');
      setHeightInchesInput('');
      setWeightInput('');
      setBaselineWeightLbs(null);
      setBaselineBmiValue(null);
      setBmiInput('');
      setSelectedSmoking('NO');
      setSelectedDiabetes('0');
      setSelectedAntithrombotic('');
      setSelectedInsurance('');
      setSelectedRevisionSurgery('0');
      setSelectedPickerLevels('0');
      setSelectedSurgeries([]);
      setReviewedSurgeryKeys([]);
      setViewedChartTabs([JD_CHART_TAB_KEYS.BENEFITS]);
      setDecideModalTab('procedure');
      setEncounterNoteType(ENCOUNTER_NOTE_TYPE.SURGERY_DECISION);
      setEncounterNotes(emptyEncounterNoteDrafts());
      setEncounterNotesEdited(emptyEncounterNoteEdited());
      setProcedureLevelsText('');
      setEncounterDecision(ENCOUNTER_DECISION.SURGERY);
      setApprovedDecision(null);
      setShowApprovedDecisionModal(false);
      setApprovedDraftNoteType(ENCOUNTER_NOTE_TYPE.SURGERY_DECISION);
      setApprovedDraftNotes(emptyEncounterNoteDrafts());
      setApprovedDraftDecision(ENCOUNTER_DECISION.SURGERY);
      setApprovedDraftSurgeryKey('');
      setApprovedDraftSurgeryDate('');
      setApprovedSaveError(null);
      setApprovedSaving(false);
      setBaselineStratumFilters(null);
      setStableFactorsExpanded(false);
      setLoadError(null);
      setIsLoadingCharacteristics(false);
      setIsLoadingStrata(false);
      setDynamicRiskScaleByTab({});
      setIncludeRiskControl(true);
      setRiskSourceMode('regression');
      setRegressionModels(null);
      setRegressionModelsStatus('idle');
      strataSliceLoadedKeyRef.current = null;
      pendingDecisionHydrationRef.current = null;
      return;
    }

    setPreload(null);
    setSelectedRegion(null);
    setSelectedBmi('');
    setHeightFeetInput('');
    setHeightInchesInput('');
    setWeightInput('');
    setBaselineWeightLbs(null);
    setBaselineBmiValue(null);
    setBmiInput('');
    setSelectedSmoking('NO');
    setSelectedDiabetes('0');
    setSelectedAntithrombotic('');
    setSelectedInsurance('');
    setSelectedRevisionSurgery('0');
    setSelectedPickerLevels('0');
    setSelectedSurgeries([]);
    setReviewedSurgeryKeys([]);
    setViewedChartTabs([JD_CHART_TAB_KEYS.BENEFITS]);
    setDecideModalTab('procedure');
    setEncounterNoteType(ENCOUNTER_NOTE_TYPE.SURGERY_DECISION);
    setEncounterNotes(emptyEncounterNoteDrafts());
    setEncounterNotesEdited(emptyEncounterNoteEdited());
    setProcedureLevelsText('');
    setEncounterDecision(ENCOUNTER_DECISION.SURGERY);
    setApprovedDecision(null);
    setShowApprovedDecisionModal(false);
    setApprovedDraftNoteType(ENCOUNTER_NOTE_TYPE.SURGERY_DECISION);
    setApprovedDraftNotes(emptyEncounterNoteDrafts());
    setApprovedDraftDecision(ENCOUNTER_DECISION.SURGERY);
    setApprovedDraftSurgeryKey('');
    setApprovedDraftSurgeryDate('');
    setApprovedSaveError(null);
    setApprovedSaving(false);
    setBaselineStratumFilters(null);
    setStableFactorsExpanded(false);
    setLoadError(null);
    setDynamicRiskScaleByTab({});
    setIncludeRiskControl(true);
    setRiskSourceMode('regression');
    setPracticeStats(null);
    setRateSource('research');
    setRegressionModels(null);
    setRegressionModelsStatus('idle');
    setIsLoadingCharacteristics(true);
    setIsLoadingStrata(false);
    strataSliceLoadedKeyRef.current = null;
    pendingDecisionHydrationRef.current = null;
  }, [patient?.id]);

  // OLS coefficient payload for default regression scoring (API, then static public fallback).
  // On load failure we fall back to strata CDN slices when LOAD_STRATA_CDN_SLICE is on.
  useEffect(() => {
    if (!patient?.id) return undefined;
    let cancelled = false;
    setRegressionModelsStatus('loading');
    (async () => {
      const models = await fetchJointDecisionsRegressionModels(patient.id);
      if (cancelled) return;
      if (models?.outcomes) {
        setRegressionModels(models);
        setRegressionModelsStatus('ready');
      } else {
        setRegressionModels(null);
        setRegressionModelsStatus('error');
        if (LOAD_STRATA_CDN_SLICE) {
          setRiskSourceMode('strata');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patient?.id]);

  // Fast path: patient characteristics only (show dropdowns).
  useEffect(() => {
    if (!patient?.id) return undefined;

    let cancelled = false;
    setLoadError(null);
    setIsLoadingCharacteristics(true);

    (async () => {
      try {
        const [result, savedDecision] = await Promise.all([
          fetchJointDecisionsSpreadsheetCharacteristics(patient.id),
          fetchJointDecisionsDecision(patient.id),
        ]);
        if (cancelled) return;
        if (!result) {
          setLoadError('Unable to load patient characteristics');
          return;
        }
        if (result.error) {
          setLoadError(String(result.error));
          return;
        }

        const patientRegion = result.region || 'LUMBAR';
        const patientProfile = result.patient_profile || null;
        const bmiChoices = Array.isArray(result.bmi_options) ? result.bmi_options : [];
        const isReady = result.status !== 'needs_input';

        setPreload({
          status: result.status || (isReady ? 'ready' : 'needs_input'),
          missing: Array.isArray(result.missing) ? result.missing : [],
          stratum_rows: [],
          strata_slice_key: null,
          profiles_by_surgery: {},
          available_surgeries: [],
          risk_labels: Array.isArray(result.risk_labels) ? result.risk_labels : [],
          spreadsheet_controls: {},
          patient_profile: patientProfile,
          region: patientRegion,
          region_label: result.region_label || null,
          regions: Array.isArray(result.regions) ? result.regions : [],
          bmi_options: bmiChoices,
          suggested_surgeries: Array.isArray(result.suggested_surgeries)
            ? result.suggested_surgeries
            : [],
          clinical: result.clinical || null,
          sex_code: result.sex_code ?? null,
          age_bucket: result.age_bucket ?? null,
          strata_slice_file: result.strata_slice_file || null,
        });

        setSelectedRegion(savedDecision?.region || patientRegion);

        const factorProfile = savedDecision
          ? { ...patientProfile, ...mapDecisionCharacteristicsToProfile(savedDecision.characteristics) }
          : patientProfile;

        const initialHeightParts = splitInchesToFeetInches(factorProfile?.height);
        const initialHeightInches = totalInchesFromFeetInches(
          initialHeightParts.feet,
          initialHeightParts.inches,
        );
        const chartWeight = formatVitalValue(factorProfile?.weight);
        const initialExactBmi =
          parsePositiveNumber(factorProfile?.bmi) ??
          calculateBmiFromVitals(initialHeightInches, chartWeight);
        const derivedWeight =
          chartWeight ||
          (initialExactBmi != null
            ? formatCalculatedWeight(calculateWeightFromBmi(initialHeightInches, initialExactBmi))
            : '');
        const initialBmiBucket = calculateBmiBucketFromBmi(initialExactBmi);

        setHeightFeetInput(initialHeightParts.feet);
        setHeightInchesInput(initialHeightParts.inches);
        setWeightInput(derivedWeight);
        setBaselineWeightLbs(parsePositiveNumber(derivedWeight));
        setBaselineBmiValue(parsePositiveNumber(initialExactBmi));
        setBmiInput(formatCalculatedBmi(initialExactBmi));
        setSelectedSmoking(resolveInitialSmoking(factorProfile));
        setSelectedDiabetes(resolveInitialDiabetes(factorProfile));
        setSelectedAntithrombotic(resolveInitialAntithrombotic(factorProfile));
        setSelectedInsurance(resolveInitialInsurance(factorProfile));
        setSelectedRevisionSurgery(resolveInitialRevisionSurgery(factorProfile));

        const nextBucket =
          (initialBmiBucket && bmiChoices.includes(initialBmiBucket) ? initialBmiBucket : null) ||
          resolveInitialBmi(factorProfile, bmiChoices);
        if (nextBucket) {
          setSelectedBmi(nextBucket);
        }

        if (savedDecision) {
          setApprovedDecision(savedDecision);
          if (savedDecision.picker_levels != null && savedDecision.picker_levels !== '') {
            setSelectedPickerLevels(String(savedDecision.picker_levels));
          }
          const ui = savedDecision.chart_ui || {};
          if (ui.include_risk_control != null) {
            setIncludeRiskControl(Boolean(ui.include_risk_control));
          }
          if (ui.value_setting) setValueSetting(ui.value_setting);
          if (ui.show_modification_overlay != null) {
            setShowModificationOverlay(Boolean(ui.show_modification_overlay));
          }
          if (ui.active_chart_tab) setActiveChartTab(ui.active_chart_tab);
          if (ui.risk_source_mode) setRiskSourceMode(ui.risk_source_mode);
          if (savedDecision.encounter_decision) {
            setEncounterDecision(savedDecision.encounter_decision);
          }
          const restoredNote = encounterNoteUiFromChart(ui, savedDecision.encounter_note || '');
          setEncounterNoteType(restoredNote.noteType);
          setEncounterNotes(restoredNote.notes);
          setEncounterNotesEdited(restoredNote.edited);
          setProcedureLevelsText(restoredNote.procedureLevelsText);
          const viewed = Array.isArray(savedDecision.viewed_chart_tabs)
            ? savedDecision.viewed_chart_tabs.filter(Boolean)
            : [];
          if (viewed.length) setViewedChartTabs(viewed);
          pendingDecisionHydrationRef.current = {
            selected: seriesKeysFromSurgeries(savedDecision.considered_surgeries),
            reviewed: seriesKeysFromSurgeries(
              savedDecision.reviewed_surgeries?.length
                ? savedDecision.reviewed_surgeries
                : savedDecision.considered_surgeries,
            ),
          };
        } else {
          setApprovedDecision(null);
        }
      } catch (e) {
        if (!cancelled) {
          setLoadError(e?.message || 'Failed to load patient characteristics');
        }
      } finally {
        if (!cancelled) setIsLoadingCharacteristics(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [patient?.id]);

  // One slim sex×age CDN slice. Factor toggles stay local — no Spanner strata fallback.
  // Gated off: charts currently score from regression models only.
  useEffect(() => {
    if (!LOAD_STRATA_CDN_SLICE) {
      setIsLoadingStrata(false);
      return undefined;
    }

    if (!patient?.id || !patientChartLoaded) {
      setIsLoadingStrata(false);
      return undefined;
    }

    const sexCode = preload?.sex_code;
    const ageBucket = preload?.age_bucket;
    if (sexCode == null || ageBucket == null) {
      setIsLoadingStrata(false);
      return undefined;
    }

    const sliceKey = `${sexCode}:${ageBucket}`;
    if (strataSliceLoadedKeyRef.current === sliceKey) {
      setIsLoadingStrata(false);
      return undefined;
    }

    let cancelled = false;
    setIsLoadingStrata(true);
    setLoadError(null);

    (async () => {
      try {
        const slice = await fetchJointDecisionsStrataSlice(patient.id, {
          sexCode,
          ageBucket,
        });
        if (cancelled) return;

        if (!slice?.rows?.length) {
          setLoadError('Unable to load strata slice for this patient');
          strataSliceLoadedKeyRef.current = sliceKey;
          return;
        }

        strataSliceLoadedKeyRef.current = sliceKey;
        setPreload((prev) =>
          prev
            ? {
                ...prev,
                stratum_rows: slice.rows,
                strata_slice_key: sliceKey,
                profiles_by_surgery: {},
                available_surgeries: [],
                spreadsheet_controls: {},
              }
            : prev,
        );
      } catch (e) {
        if (!cancelled) {
          setLoadError(e?.message || 'Failed to load strata slice');
          strataSliceLoadedKeyRef.current = sliceKey;
        }
      } finally {
        if (!cancelled) setIsLoadingStrata(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    patient?.id,
    patientChartLoaded,
    preload?.sex_code,
    preload?.age_bucket,
  ]);

  const pruneSelectedSurgeries = (bundle) => {
    // Keep selections across level-picker toggles; only drop keys no longer available at all.
    const availableKeys = new Set((bundle?.available_surgeries ?? []).map((item) => item.surgery));
    setSelectedSurgeries((prev) => prev.filter((s) => availableKeys.has(s)));
  };

  useEffect(() => {
    if (!availableSurgeries.length) return;
    const availableKeys = new Set(availableSurgeries.map((item) => item.surgery));
    const pending = pendingDecisionHydrationRef.current;
    if (pending) {
      pendingDecisionHydrationRef.current = null;
      const selected = (pending.selected || []).filter((key) => availableKeys.has(key));
      const reviewed = (pending.reviewed || []).filter((key) => availableKeys.has(key));
      setSelectedSurgeries(selected);
      if (reviewed.length) {
        setReviewedSurgeryKeys(reviewed);
      } else if (selected.length) {
        setReviewedSurgeryKeys(selected);
      }
      return;
    }
    pruneSelectedSurgeries({ available_surgeries: availableSurgeries });
  }, [availableSurgeries]);

  const handleRegionChange = (regionKey) => {
    setHasEngagedJointDecisions(true);
    setSelectedRegion(regionKey);
    setSelectedSurgeries([]);
  };

  const updateBmiBucketFromExactBmi = (nextBmi) => {
    const nextBucket = calculateBmiBucketFromBmi(nextBmi);
    if (!nextBucket) return;
    setSelectedBmi(nextBucket);
  };

  const updateBaselineFilters = (patch) => {
    setBaselineStratumFilters((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const updateBmiBucketFromExactBmiForBaseline = (nextBmi) => {
    const nextBucket = calculateBmiBucketFromBmi(nextBmi);
    if (!nextBucket) return;
    updateBaselineFilters({ bmi_bucket: bmiLabelToBucket(nextBucket) });
  };

  const updateBmiFromVitals = (nextHeight, nextWeight, { updateBaseline = false } = {}) => {
    const nextBmi = calculateBmiFromVitals(nextHeight, nextWeight);
    if (nextBmi == null) return;

    setBmiInput(formatCalculatedBmi(nextBmi));
    updateBmiBucketFromExactBmi(nextBmi);
    if (updateBaseline) updateBmiBucketFromExactBmiForBaseline(nextBmi);
  };

  const handleHeightFeetChange = (nextFeet) => {
    setHasEngagedJointDecisions(true);
    setHeightFeetInput(nextFeet);
    updateBmiFromVitals(
      totalInchesFromFeetInches(nextFeet, heightInchesInput),
      weightInput,
      { updateBaseline: stableFactorsExpanded },
    );
  };

  const handleHeightInchesChange = (nextInches) => {
    setHasEngagedJointDecisions(true);
    setHeightInchesInput(nextInches);
    updateBmiFromVitals(
      totalInchesFromFeetInches(heightFeetInput, nextInches),
      weightInput,
      { updateBaseline: stableFactorsExpanded },
    );
  };

  const commitBaselineVitals = (nextWeight, nextBmi) => {
    const weightLbs = parsePositiveNumber(nextWeight);
    const bmiValue = parsePositiveNumber(nextBmi);
    if (weightLbs != null) setBaselineWeightLbs(weightLbs);
    if (bmiValue != null) setBaselineBmiValue(bmiValue);
  };

  // While required factors are still being entered, vitals edits are baseline — not scenario mods.
  const treatVitalsAsBaseline = stableFactorsExpanded || needsRequiredInput;

  const handleWeightChange = (nextWeight) => {
    setHasEngagedJointDecisions(true);
    setWeightInput(nextWeight);
    const nextBmi = calculateBmiFromVitals(heightInchesTotal, nextWeight);
    if (treatVitalsAsBaseline) {
      commitBaselineVitals(nextWeight, nextBmi ?? bmiInput);
    }
    updateBmiFromVitals(heightInchesTotal, nextWeight, {
      updateBaseline: stableFactorsExpanded,
    });
  };

  const handleBmiChange = (nextBmi) => {
    setHasEngagedJointDecisions(true);
    setBmiInput(nextBmi);
    updateBmiBucketFromExactBmi(nextBmi);
    if (stableFactorsExpanded) updateBmiBucketFromExactBmiForBaseline(nextBmi);

    const nextWeight =
      calculateWeightFromBmi(heightInchesTotal, nextBmi) ??
      scaleWeightForBmiChange(baselineWeightLbs ?? weightInput, baselineBmiValue, nextBmi);
    if (nextWeight != null) {
      setWeightInput(formatCalculatedWeight(nextWeight));
      if (treatVitalsAsBaseline) {
        commitBaselineVitals(nextWeight, nextBmi);
      }
    } else if (treatVitalsAsBaseline) {
      commitBaselineVitals(weightInput, nextBmi);
    }
  };

  const handleSmokingChange = (smokingKey) => {
    setHasEngagedJointDecisions(true);
    setSelectedSmoking(smokingKey);
    if (stableFactorsExpanded) {
      updateBaselineFilters({ smoking: encodeSmokingForStratum(smokingKey) });
    }
  };

  const handleDiabetesChange = (diabetesKey) => {
    setHasEngagedJointDecisions(true);
    setSelectedDiabetes(diabetesKey);
    if (stableFactorsExpanded) {
      updateBaselineFilters({ diabetes: encodeBucketFilter(diabetesKey) });
    }
  };

  const handleAntithromboticChange = (antithromboticKey) => {
    setHasEngagedJointDecisions(true);
    setSelectedAntithrombotic(antithromboticKey);
    if (stableFactorsExpanded) {
      updateBaselineFilters({ antithrombotic: encodeBucketFilter(antithromboticKey) });
    }
  };

  const handleInsuranceChange = (insuranceKey) => {
    setHasEngagedJointDecisions(true);
    setSelectedInsurance(insuranceKey);
    if (stableFactorsExpanded) {
      updateBaselineFilters({ workers_comp: encodeInsuranceForStratum(insuranceKey) });
    }
  };

  const handleRevisionSurgeryChange = (revisionKey) => {
    setHasEngagedJointDecisions(true);
    setSelectedRevisionSurgery(revisionKey);
    if (stableFactorsExpanded) {
      updateBaselineFilters({ revision_surgery: encodeBucketFilter(revisionKey) ?? 0 });
    }
  };

  const handlePickerLevelsChange = (levelsKey) => {
    setHasEngagedJointDecisions(true);
    // Only switch the procedure list filter — keep already-selected series on the
    // chart so the same procedure can be compared across 1–2 and 3+ levels.
    setSelectedPickerLevels(levelsKey);
  };

  const handleSurgeryCheckboxChange = (surgeryKey, checked) => {
    setHasEngagedJointDecisions(true);
    // Auto-enable Control only when the graph first gains a surgery.
    if (checked && selectedSurgeries.length === 0) {
      setIncludeRiskControl(true);
    }
    if (checked) {
      setReviewedSurgeryKeys((prev) => (
        prev.includes(surgeryKey) ? prev : [...prev, surgeryKey]
      ));
    }
    setSelectedSurgeries((prev) => {
      if (checked) {
        return prev.includes(surgeryKey) ? prev : [...prev, surgeryKey];
      }
      return prev.filter((s) => s !== surgeryKey);
    });
  };

  const handleChartTabSelect = (key) => {
    if (!key) return;
    setHasEngagedJointDecisions(true);
    setActiveChartTab(key);
    setViewedChartTabs((prev) => (prev.includes(key) ? prev : [...prev, key]));
  };

  const handleOpenProcedureConfig = () => {
    setProcedureConfigError(null);
    setShowProcedureConfig(true);
  };

  const handleOpenDecideModal = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDecideSurgeryKey(leftoverMedManagementSurgeryToken(selectedSurgeries));
    setDecideSurgeryDate(defaultDecideSurgeryDate());
    setDecideMedications(seedDecideMedicationRows(chartAntithrombotics));
    setDecideError(null);
    setDecideModalTab('procedure');
    setEncounterNotesEdited(emptyEncounterNoteEdited());
    setEnrollmentSurgeryNotice(false);
    setShowDecideModal(true);
  };

  const handleOpenApprovedDecision = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!approvedDecision) return;
    const restoredNote = encounterNoteUiFromChart(
      approvedDecision.chart_ui || {},
      approvedDecision.encounter_note || '',
    );
    setApprovedDraftNoteType(restoredNote.noteType);
    setApprovedDraftNotes(restoredNote.notes);
    setApprovedDraftDecision(
      approvedDecision.encounter_decision || ENCOUNTER_DECISION.SURGERY,
    );
    setApprovedDraftSurgeryKey(approvedDecision.decided_procedure_key || '');
    setApprovedDraftSurgeryDate(
      approvedDecision.decided_surgery_date || defaultDecideSurgeryDate(),
    );
    setApprovedSaveError(null);
    setShowApprovedDecisionModal(true);
  };

  const approvedSurgeryChanged = Boolean(
    approvedDecision && (
      approvedDraftSurgeryKey !== (approvedDecision.decided_procedure_key || '')
      || approvedDraftSurgeryDate !== (approvedDecision.decided_surgery_date || '')
    ),
  );

  const savedApprovedNoteUi = useMemo(
    () => (approvedDecision
      ? encounterNoteUiFromChart(
        approvedDecision.chart_ui || {},
        approvedDecision.encounter_note || '',
      )
      : null),
    [approvedDecision],
  );

  const approvedDecisionDirty = Boolean(
    approvedDecision && savedApprovedNoteUi && (
      approvedDraftNoteType !== savedApprovedNoteUi.noteType
      || (approvedDraftNotes[approvedDraftNoteType] || '') !== (
        savedApprovedNoteUi.notes[approvedDraftNoteType] || ''
      )
      || approvedDraftDecision !== (approvedDecision.encounter_decision || ENCOUNTER_DECISION.SURGERY)
      || approvedSurgeryChanged
    ),
  );

  const approvedSurgeryOptions = useMemo(() => {
    const selected = String(approvedDraftSurgeryKey || '').trim();
    if (selected && !MED_MANAGEMENT_SURGERIES.includes(selected)) {
      return [selected, ...MED_MANAGEMENT_SURGERIES];
    }
    return MED_MANAGEMENT_SURGERIES;
  }, [approvedDraftSurgeryKey]);

  const handleSaveApprovedDecision = async () => {
    if (!approvedDecision || !approvedDecisionDirty) return;
    if (!approvedDraftSurgeryKey) {
      setApprovedSaveError('Select a surgery.');
      return;
    }
    if (!approvedDraftSurgeryDate) {
      setApprovedSaveError('Choose a surgery date.');
      return;
    }
    const consultAppointmentId = [approvedDecision.appointment_id, patient?.appointment_id, appointmentId]
      .map((id) => (id == null || id === '' ? '' : String(id)))
      .find((id) => id && id !== '0') || appointmentId;
    if (!consultAppointmentId) {
      setApprovedSaveError('Unable to save Joint Decisions without an appointment.');
      return;
    }
    if (approvedSurgeryChanged && !onDecideSubmit) {
      setApprovedSaveError('Unable to continue to Med Management.');
      return;
    }
    setApprovedSaveError(null);
    setApprovedSaving(true);
    let decisionSaved = false;
    try {
      const noteToSave = (approvedDraftNotes[approvedDraftNoteType] || '')
        || generatedNotes[approvedDraftNoteType]
        || '';
      const nextChartUi = chartUiWithEncounterNote(approvedDecision.chart_ui || {}, {
        noteType: approvedDraftNoteType,
        notes: {
          ...emptyEncounterNoteDrafts(),
          ...approvedDraftNotes,
          [approvedDraftNoteType]: noteToSave,
        },
        procedureLevelsText,
      });
      const saved = await saveJointDecisionsDecision({
        patient_id: approvedDecision.patient_id || patient?.id,
        appointment_id: consultAppointmentId,
        encounter_decision: approvedDraftDecision,
        decided_procedure_key: approvedDraftSurgeryKey,
        decided_surgery_date: approvedDraftSurgeryDate,
        decided_procedure_label: formatMedManagementSurgeryLabel(approvedDraftSurgeryKey),
        region: approvedDecision.region || selectedRegion,
        picker_levels: approvedDecision.picker_levels,
        characteristics: approvedDecision.characteristics || {},
        considered_surgeries: approvedDecision.considered_surgeries || [],
        reviewed_surgeries: approvedDecision.reviewed_surgeries || [],
        viewed_chart_tabs: approvedDecision.viewed_chart_tabs || [],
        chart_ui: nextChartUi,
        encounter_note: noteToSave,
        encounter_note_edited: true,
        periop_appointment_id: approvedDecision.periop_appointment_id || '0',
      });
      const nextDecision = saved?.decision || {
        ...approvedDecision,
        appointment_id: consultAppointmentId,
        encounter_decision: approvedDraftDecision,
        decided_procedure_key: approvedDraftSurgeryKey,
        decided_surgery_date: approvedDraftSurgeryDate,
        decided_procedure_label: formatMedManagementSurgeryLabel(approvedDraftSurgeryKey),
        chart_ui: nextChartUi,
        encounter_note: noteToSave,
        encounter_note_edited: true,
        approval_status: 'approved',
        approval_time: new Date().toISOString(),
      };
      setApprovedDecision(nextDecision);
      setEncounterDecision(approvedDraftDecision);
      setEncounterNoteType(approvedDraftNoteType);
      setEncounterNotes({
        ...emptyEncounterNoteDrafts(),
        ...approvedDraftNotes,
        [approvedDraftNoteType]: noteToSave,
      });
      setEncounterNotesEdited({
        ...emptyEncounterNoteEdited(),
        [approvedDraftNoteType]: true,
      });
      decisionSaved = true;
      setShowApprovedDecisionModal(false);
      if (approvedSurgeryChanged) {
        await onDecideSubmit({
          periopToken: approvedDraftSurgeryKey,
          date: approvedDraftSurgeryDate,
          label: formatMedManagementSurgeryLabel(approvedDraftSurgeryKey),
          characteristics: approvedDecision.characteristics || {},
          replaceSynthetic: true,
          sourceAppointmentId: consultAppointmentId === '0' ? undefined : consultAppointmentId,
        });
      }
    } catch (err) {
      if (!decisionSaved) {
        setApprovedSaveError(err?.message || 'Failed to save Joint Decisions.');
      }
      // Med Management errors are shown on that tab; keep the popup closed.
    } finally {
      setApprovedSaving(false);
    }
  };

  const recClinical = useMemo(
    () => clinicalFactsFromRecommendation(recommendation),
    [recommendation],
  );

  const selectedProcedureKeys = useMemo(
    () => selectedSurgeries
      .map((key) => parseSeriesKey(key)?.procedureKey)
      .filter(Boolean),
    [selectedSurgeries],
  );
  const reviewedProcedureKeys = useMemo(() => {
    const keys = reviewedSurgeryKeys.length > 0 ? reviewedSurgeryKeys : selectedSurgeries;
    return keys.map((key) => parseSeriesKey(key)?.procedureKey).filter(Boolean);
  }, [reviewedSurgeryKeys, selectedSurgeries]);

  useEffect(() => {
    const defaultLevels = defaultProcedureLevelsText({
      procedureKeys: selectedProcedureKeys,
      procedureKey: decideSurgeryKey,
    });
    if (!defaultLevels) return;
    setProcedureLevelsText((prev) => (String(prev || '').trim() ? prev : defaultLevels));
  }, [decideSurgeryKey, selectedProcedureKeys]);

  const encounterNoteInput = useMemo(() => {
    const clinical = preload?.clinical || {};
    const diagnoses = [...(clinical.diagnoses || []), ...(recClinical.diagnoses || [])];
    const reviewedKeys = reviewedSurgeryKeys.length > 0 ? reviewedSurgeryKeys : selectedSurgeries;
    return {
      dateOfService: new Date(),
      appointmentType: patient?.appointment_type,
      diagnoses,
      regionLabel,
      chiefComplaint: clinical.chief_complaint || recClinical.chiefComplaint,
      previousSpineSurgeries:
        clinical.previous_spine_surgeries || recClinical.previousSpineSurgeries,
      revisionSurgery: selectedRevisionSurgery,
      reviewedProcedureLabels: reviewedKeys.map(surgeryKeyToNoteLabel),
      selectedProcedureLabels: selectedSurgeries.map(surgeryKeyToNoteLabel),
      reviewedProcedureKeys,
      selectedProcedureKeys,
      viewedChartLabels: viewedChartTabs.map(chartTabNoteLabel),
      age: preload?.patient_profile?.age,
      bmi: resolvedBmiValue,
      bmiBucketLabel: selectedBmi,
      smoking: selectedSmoking,
      diabetes: selectedDiabetes,
      antithrombotic: selectedAntithrombotic,
      antithromboticMedications: chartAntithrombotics.map((med) =>
        String(med.token || '')
          .replace(/_/g, ' ')
          .toLowerCase()
          .replace(/\b\w/g, (char) => char.toUpperCase()),
      ),
      decision: encounterDecision,
      sex: preload?.patient_profile?.sex,
      procedureLevelsText,
      valueSetting,
    };
  }, [
    chartAntithrombotics,
    encounterDecision,
    patient?.appointment_type,
    preload?.clinical,
    preload?.patient_profile?.age,
    preload?.patient_profile?.sex,
    procedureLevelsText,
    recClinical,
    regionLabel,
    resolvedBmiValue,
    reviewedProcedureKeys,
    reviewedSurgeryKeys,
    selectedAntithrombotic,
    selectedBmi,
    selectedDiabetes,
    selectedProcedureKeys,
    selectedRevisionSurgery,
    selectedSmoking,
    selectedSurgeries,
    valueSetting,
    viewedChartTabs,
  ]);

  const generatedNotes = useMemo(() => ({
    [ENCOUNTER_NOTE_TYPE.EM]: buildEncounterNote(ENCOUNTER_NOTE_TYPE.EM, encounterNoteInput),
    [ENCOUNTER_NOTE_TYPE.SURGERY_DECISION]: buildEncounterNote(
      ENCOUNTER_NOTE_TYPE.SURGERY_DECISION,
      encounterNoteInput,
    ),
  }), [encounterNoteInput]);

  const displayedEncounterNote = encounterNotesEdited[encounterNoteType]
    ? encounterNotes[encounterNoteType]
    : generatedNotes[encounterNoteType];
  const displayedApprovedNote = (approvedDraftNotes[approvedDraftNoteType] || '')
    || generatedNotes[approvedDraftNoteType]
    || '';

  const handleDecideSubmit = async () => {
    if (!decideSurgeryKey) {
      setDecideError('Select a surgery.');
      return;
    }
    if (!decideSurgeryDate) {
      setDecideError('Choose a surgery date.');
      return;
    }
    if (!onDecideSubmit) {
      setDecideError('Unable to continue to Med Management.');
      return;
    }
    const consultAppointmentId = appointmentId || patient?.appointment_id;
    if (!consultAppointmentId) {
      setDecideError('Unable to save Joint Decisions without an appointment.');
      return;
    }
    setDecideError(null);
    if (typeof document !== 'undefined' && document.activeElement?.blur) {
      document.activeElement.blur();
    }
    let decisionSaved = false;
    try {
      const clinical = preload?.clinical || {};
      const diagnoses = [...(clinical.diagnoses || []), ...(recClinical.diagnoses || [])];
      const payload = buildJointDecisionsDecisionPayload({
        patientId: patient.id,
        appointmentId: consultAppointmentId,
        encounterDecision,
        decidedProcedureKey: decideSurgeryKey,
        decidedSurgeryDate: decideSurgeryDate,
        decidedProcedureLabel: formatMedManagementSurgeryLabel(decideSurgeryKey),
        region: selectedRegion,
        pickerLevels: selectedPickerLevels,
        characteristics: {
          age: preload?.patient_profile?.age,
          sex: preload?.patient_profile?.sex,
          sex_code: preload?.sex_code,
          age_bucket: preload?.age_bucket,
          bmi: resolvedBmiValue,
          bmi_bucket_label: selectedBmi,
          height_inches: heightInchesTotal,
          weight_lbs: parsePositiveNumber(weightInput),
          smoking: selectedSmoking,
          diabetes: selectedDiabetes,
          antithrombotic: selectedAntithrombotic,
          insurance: selectedInsurance,
          revision_surgery: selectedRevisionSurgery,
          diagnoses,
          chief_complaint: clinical.chief_complaint || recClinical.chiefComplaint,
          previous_spine_surgeries:
            clinical.previous_spine_surgeries || recClinical.previousSpineSurgeries,
        },
        selectedSurgeries,
        reviewedSurgeryKeys,
        viewedChartTabs,
        chartUi: chartUiWithEncounterNote({
          include_risk_control: includeRiskControl,
          value_setting: valueSetting,
          show_modification_overlay: showModificationOverlay,
          active_chart_tab: activeChartTab,
          risk_source_mode: riskSourceMode,
        }, {
          noteType: encounterNoteType,
          notes: {
            ...emptyEncounterNoteDrafts(),
            ...encounterNotes,
            [encounterNoteType]: displayedEncounterNote,
          },
          procedureLevelsText,
        }),
        encounterNote: displayedEncounterNote,
        encounterNoteEdited: Boolean(encounterNotesEdited[encounterNoteType]),
        periopAppointmentId: '0',
        surgeryLabelForKey: surgeryKeyToNoteLabel,
      });
      const saved = await saveJointDecisionsDecision(payload);
      setApprovedDecision(saved?.decision || {
        ...payload,
        approval_status: 'approved',
        approval_time: new Date().toISOString(),
      });
      decisionSaved = true;
      const goToEnrollment = enrollmentSurgeryNotice;
      setShowDecideModal(false);
      setEnrollmentSurgeryNotice(false);
      await onDecideSubmit({
        periopToken: decideSurgeryKey,
        date: decideSurgeryDate,
        label: formatMedManagementSurgeryLabel(decideSurgeryKey),
        characteristics: payload.characteristics,
        goToEnrollment,
        ...decideMedicationNoteChanges(chartAntithrombotics, decideMedications),
      });
    } catch (err) {
      if (!decisionSaved) {
        setDecideError(err?.message || 'Failed to save Joint Decisions.');
        return;
      }
      // Error is shown on Med Management; keep the popup closed.
    }
  };

  const handleSaveProcedureConfig = async (nextHiddenProcedures) => {
    setProcedureConfigSaving(true);
    setProcedureConfigError(null);
    try {
      const result = await saveOrganizationJointDecisionsProcedureConfig(nextHiddenProcedures);
      setHiddenProcedures(
        Array.isArray(result?.hidden_procedures) ? result.hidden_procedures : nextHiddenProcedures,
      );
      if (Array.isArray(result?.all_procedures) && result.all_procedures.length > 0) {
        setProcedureCatalog(normalizeProcedureCatalog(result.all_procedures));
      }
      setShowProcedureConfig(false);
    } catch (e) {
      setProcedureConfigError(e?.message || 'Failed to save procedure settings');
    } finally {
      setProcedureConfigSaving(false);
    }
  };

  useEffect(() => {
    if (!isExpanded) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsExpanded(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isExpanded]);

  const showSpinner = readingPatientChart;
  const waitingForCharts = LOAD_STRATA_CDN_SLICE
    ? isLoadingStrata
    : regressionModelsStatus === 'idle' || regressionModelsStatus === 'loading';
  const dataReady =
    patientChartLoaded &&
    !loadError &&
    !needsRequiredInput &&
    hasStrataData;
  const chartReady = dataReady && selectedSurgeries.length > 0;
  const [chartsRevealed, setChartsRevealed] = useState(false);

  useEffect(() => {
    if (!dataReady || waitingForCharts) {
      setChartsRevealed(false);
      return undefined;
    }
    let frame2 = 0;
    const frame1 = window.requestAnimationFrame(() => {
      frame2 = window.requestAnimationFrame(() => {
        setChartsRevealed(true);
      });
    });
    return () => {
      window.cancelAnimationFrame(frame1);
      if (frame2) window.cancelAnimationFrame(frame2);
    };
  }, [dataReady, waitingForCharts, patient?.id]);

  useEffect(() => {
    if (!isDemoOrDevApp() || !chartsRevealed || !patient?.id) {
      setDemoReferences([]);
      return undefined;
    }
    let cancelled = false;
    const cancelPaint = runAfterPaint(() => {
      import('../data/demoJointDecisionsReferences')
        .then((mod) => {
          if (cancelled) return;
          setDemoReferences(mod.getDemoJointDecisionsReferences(patient.id));
        })
        .catch(() => {
          if (!cancelled) setDemoReferences([]);
        });
    });
    return () => {
      cancelled = true;
      cancelPaint();
    };
  }, [chartsRevealed, patient?.id]);

  const onStageReadyRef = useRef(onStageReady);
  onStageReadyRef.current = onStageReady;
  useEffect(() => {
    const holdingMainStage = waitingForCharts || (dataReady && !chartsRevealed);
    const ready = !holdingMainStage && (patientChartLoaded || Boolean(loadError));
    onStageReadyRef.current?.(ready);
    return () => onStageReadyRef.current?.(false);
  }, [waitingForCharts, dataReady, chartsRevealed, patientChartLoaded, loadError]);

  useEffect(() => {
    if (!patientChartLoaded || needsRequiredInput || baselineStratumFilters) return;
    setBaselineStratumFilters(stratumFilters);
  }, [patientChartLoaded, needsRequiredInput, baselineStratumFilters, stratumFilters]);

  // Entering regression mode: adopt current factors as the new baseline so
  // meaningful-difference thresholds / lifestyle overlays stay off until the
  // user changes a modifiable factor again.
  useEffect(() => {
    const prev = prevRiskSourceModeRef.current;
    prevRiskSourceModeRef.current = riskSourceMode;
    if (prev === riskSourceMode) return;
    if (riskSourceMode !== 'regression') return;
    if (!stratumFilters) return;
    setBaselineStratumFilters({ ...stratumFilters });
    setShowModificationOverlay(true);
  }, [riskSourceMode, stratumFilters]);

  const currentFactorChangeActions = useMemo(
    () => (stableFactorsExpanded ? [] : buildCurrentFactorChangeActions(baselineStratumFilters, stratumFilters)),
    [baselineStratumFilters, stratumFilters, stableFactorsExpanded],
  );
  const hasCurrentFactorChanges = currentFactorChangeActions.length > 0;
  const currentBmiChangeAction = changeActionForTarget(currentFactorChangeActions, 'bmi');
  const currentSmokingChangeAction = changeActionForTarget(currentFactorChangeActions, 'smoking');
  const currentDiabetesChangeAction = changeActionForTarget(currentFactorChangeActions, 'diabetes');
  const currentAntithromboticChangeAction = changeActionForTarget(currentFactorChangeActions, 'antithrombotic');
  const currentWeightChangeAction = useMemo(() => {
    if (stableFactorsExpanded) return null;
    const baselineLbs = parsePositiveNumber(baselineWeightLbs);
    const currentLbs = parsePositiveNumber(weightInput);
    let deltaLbs = null;
    if (baselineLbs != null && currentLbs != null) {
      deltaLbs = currentLbs - baselineLbs;
    } else {
      const baselineBmi = parsePositiveNumber(baselineBmiValue);
      const currentBmi = parsePositiveNumber(bmiInput);
      const scaled = scaleWeightForBmiChange(baselineLbs, baselineBmi, currentBmi);
      if (baselineLbs != null && scaled != null) {
        deltaLbs = scaled - baselineLbs;
      }
    }
    const chip = formatWeightDeltaChip(deltaLbs);
    if (!chip) return null;
    return {
      key: 'current-weight',
      target: 'weight',
      chip,
    };
  }, [baselineBmiValue, baselineWeightLbs, bmiInput, stableFactorsExpanded, weightInput]);

  const scenarioRegionBundleForCurrentFactors = useMemo(() => {
    if (!hasCurrentFactorChanges || !selectedRegion) {
      return null;
    }
    if (
      riskSourceMode === 'regression' &&
      regressionModels?.outcomes &&
      preload?.sex_code != null &&
      preload?.age_bucket != null
    ) {
      return buildRegionBundleFromRegression(
        regressionModels,
        selectedRegion,
        stratumFilters,
        {
          sexCode: preload.sex_code,
          ageBucket: preload.age_bucket,
          allowedProcedureKeys: resolveOrgEnabledProcedureKeys(
            selectedRegion,
            procedureCatalog,
            hiddenProcedures,
          ),
        },
      );
    }
    if (!Array.isArray(preload?.stratum_rows) || !preload.stratum_rows.length) {
      return null;
    }
    return buildRegionBundleFromSpannerStrata(preload.stratum_rows, selectedRegion, stratumFilters);
  }, [
    hasCurrentFactorChanges,
    preload?.stratum_rows,
    preload?.sex_code,
    preload?.age_bucket,
    selectedRegion,
    stratumFilters,
    riskSourceMode,
    regressionModels,
    procedureCatalog,
    hiddenProcedures,
  ]);

  const scenarioProfilesForCurrentFactors = useMemo(
    () =>
      scenarioRegionBundleForCurrentFactors
        ? buildSelectedProfilesFromBundle(
            scenarioRegionBundleForCurrentFactors,
            selectedSurgeries,
            visibleAvailableSurgeries,
          )
        : [],
    [scenarioRegionBundleForCurrentFactors, selectedSurgeries, visibleAvailableSurgeries],
  );
  const scenarioSpreadsheetControlsForCurrentFactors =
    scenarioRegionBundleForCurrentFactors?.spreadsheet_controls ?? null;
  const hasPickedModification = hasCurrentFactorChanges;
  const showActiveModificationOverlay = hasPickedModification && showModificationOverlay;
  const visibleScenarioProfilesForCurrentFactors = showActiveModificationOverlay
    ? scenarioProfilesForCurrentFactors
    : [];
  const visibleScenarioSpreadsheetControlsForCurrentFactors = showActiveModificationOverlay
    ? scenarioSpreadsheetControlsForCurrentFactors
    : null;
  // Only gate charts on meaningful-difference thresholds once a lifestyle
  // modification is actively overlaid — never for a bare regression/strata base view.
  const effectiveThresholdFilterEnabled = showActiveModificationOverlay;

  useEffect(() => {
    if (!hasPickedModification) {
      setShowModificationOverlay(true);
    }
  }, [hasPickedModification]);

  const tabNotifications = useMemo(() => {
    if (!showActiveModificationOverlay) return {};
    const pain = countMeaningfulScenarioOutcomeProfiles(
      profiles,
      visibleScenarioProfilesForCurrentFactors,
      JD_CHART_TAB_KEYS.PAIN,
      selectedRegion,
    );
    const functionCount = countMeaningfulScenarioOutcomeProfiles(
      profiles,
      visibleScenarioProfilesForCurrentFactors,
      JD_CHART_TAB_KEYS.DISABILITY,
      selectedRegion,
    );
    const riskArgs = {
      profiles,
      spreadsheetControls,
      scenarioProfiles: visibleScenarioProfilesForCurrentFactors,
      scenarioSpreadsheetControls: visibleScenarioSpreadsheetControlsForCurrentFactors,
    };
    return {
      [JD_CHART_TAB_KEYS.BENEFITS]: pain + functionCount,
      pain,
      function: functionCount,
      [JD_CHART_TAB_KEYS.MEDICAL_RISKS]: countMeaningfulRiskLabels({
        ...riskArgs,
        riskLabels: MEDICAL_RISK_LABELS,
        includeControl: includeRiskControl,
      }),
      [JD_CHART_TAB_KEYS.PROCEDURE_RISKS]: countMeaningfulRiskLabels({
        ...riskArgs,
        riskLabels: PROCEDURE_RISK_LABELS,
        includeControl: false,
      }),
      [JD_CHART_TAB_KEYS.RECOVERY]: countMeaningfulRiskLabels({
        ...riskArgs,
        riskLabels: RECOVERY_METRIC_LABELS,
        includeControl: false,
      }),
      [JD_CHART_TAB_KEYS.LONG_TERM]: countMeaningfulRiskLabels({
        ...riskArgs,
        riskLabels: LONG_TERM_RISK_LABELS,
        includeControl: false,
      }),
    };
  }, [
    profiles,
    selectedRegion,
    spreadsheetControls,
    visibleScenarioProfilesForCurrentFactors,
    visibleScenarioSpreadsheetControlsForCurrentFactors,
    includeRiskControl,
    showActiveModificationOverlay,
  ]);
  const shouldCollapseStableFactors = patientChartLoaded && !needsRequiredInput;
  const isExploreChangesMode = shouldCollapseStableFactors && !stableFactorsExpanded;
  const showStableFactorControls = !shouldCollapseStableFactors || stableFactorsExpanded;
  const improvableFactorKeys = new Set(modifiableActionGroups.map((group) => group.key));
  const showBmiFactor =
    showStableFactorControls ||
    improvableFactorKeys.has('bmi') ||
    Boolean(currentBmiChangeAction) ||
    Boolean(currentWeightChangeAction);
  const showSmokingFactor =
    showStableFactorControls || improvableFactorKeys.has('smoking') || Boolean(currentSmokingChangeAction);
  const showDiabetesFactor =
    showStableFactorControls || improvableFactorKeys.has('diabetes') || Boolean(currentDiabetesChangeAction);
  const showAntithromboticFactor =
    showStableFactorControls || improvableFactorKeys.has('antithrombotic') || Boolean(currentAntithromboticChangeAction);
  const exploreSuggestedActions = isExploreChangesMode
    ? flattenModifiableActions(modifiableActionGroups)
    : [];

  const applyModifiableAction = (action) => {
    if (!action?.patch || stableFactorsExpanded) return;
    if (actionPatchIsApplied(action, stratumFilters)) {
      revertModifiableField(action.field);
      return;
    }
    if (action.field === 'smoking' && action.patch.smoking != null) {
      handleSmokingChange(action.patch.smoking === 1 ? 'YES' : 'NO');
      return;
    }
    if (action.field === 'diabetes' && action.patch.diabetes != null) {
      handleDiabetesChange(String(action.patch.diabetes));
      return;
    }
    if (action.field === 'antithrombotic' && action.patch.antithrombotic != null) {
      handleAntithromboticChange(String(action.patch.antithrombotic));
      return;
    }
    if (action.field === 'bmi_bucket' && action.patch.bmi_bucket != null) {
      const nextBmi = representativeBmiForBucket(
        action.patch.bmi_bucket,
        action.fromBucket ?? baselineStratumFilters?.bmi_bucket,
      );
      if (nextBmi != null) handleBmiChange(formatCalculatedBmi(nextBmi));
    }
  };

  const restoreBaselineVitals = () => {
    const baselineBmi = parsePositiveNumber(baselineBmiValue);
    if (baselineBmi != null) {
      handleBmiChange(formatCalculatedBmi(baselineBmi));
      return;
    }
    const baselineLbs = parsePositiveNumber(baselineWeightLbs);
    if (baselineLbs != null) {
      handleWeightChange(formatCalculatedWeight(baselineLbs));
      return;
    }
    const baselineBucket = baselineStratumFilters?.bmi_bucket;
    const label = bmiBucketToLabel(baselineBucket);
    if (label) setSelectedBmi(label);
  };

  const revertModifiableField = (field) => {
    if (stableFactorsExpanded) return;
    if (field === 'smoking') {
      handleSmokingChange(smokingStratumToOptionValue(baselineStratumFilters?.smoking) || 'NO');
      return;
    }
    if (field === 'diabetes') {
      const baseline = baselineStratumFilters?.diabetes;
      handleDiabetesChange(baseline == null || baseline === '' ? '0' : String(baseline));
      return;
    }
    if (field === 'antithrombotic') {
      const baseline = baselineStratumFilters?.antithrombotic;
      handleAntithromboticChange(baseline == null || baseline === '' ? '' : String(baseline));
      return;
    }
    if (field === 'bmi_bucket' || field === 'bmi' || field === 'weight') {
      restoreBaselineVitals();
    }
  };

  const revertFactorChange = (action) => {
    revertModifiableField(action?.field || action?.target);
  };

  const renderSuggestedImprovementChips = () => {
    if (!isExploreChangesMode || !exploreSuggestedActions.length) return null;
    return (
      <span className="jd-modifiable-action-chips" aria-label="Suggested improvements">
        {exploreSuggestedActions.map((action) => (
          <ModifiableActionChip
            key={action.key}
            action={action}
            applied={actionPatchIsApplied(action, stratumFilters)}
            onClick={applyModifiableAction}
          />
        ))}
      </span>
    );
  };

  useEffect(() => {
    if (!dataReady || chartTabs.length === 0) return;
    if (!chartTabs.some((tab) => tab.key === activeChartTab)) {
      setActiveChartTab(chartTabs[0].key);
    }
  }, [activeChartTab, chartTabs, dataReady]);

  const useDynamicRiskScale = Boolean(dynamicRiskScaleByTab[activeChartTab]);

  useEffect(() => {
    if (isExpanded) return undefined;

    const updatePanelHeight = () => {
      const card = cardRef.current;
      if (!card) return;
      const { top } = card.getBoundingClientRect();
      const bottomGap = 8;
      const availableHeight = window.innerHeight - top - bottomGap;
      setNormalPanelHeight(Math.max(280, Math.floor(availableHeight)));
    };

    const raf = window.requestAnimationFrame(updatePanelHeight);
    window.addEventListener('resize', updatePanelHeight);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', updatePanelHeight);
    };
  }, [isExpanded, patient?.id, chartReady, activeChartTab, selectedSurgeries.length, layoutAnchor, demoReferences.length]);

  const showDemoReferences = demoReferences.length > 0;
  const demoPracticeFactor = useMemo(
    () => (isDemoOrDevApp() ? demoPracticeOverlayFactor(patient?.id) : null),
    [patient?.id],
  );
  const hasLivePracticeStats = Number(practiceStats?.n_practice) > 0;
  const showPracticeRateToggle =
    showsPracticeRateSourceToggle(activeChartTab) &&
    (hasLivePracticeStats || demoPracticeFactor != null);
  const showLivePracticeRates = showPracticeRateToggle && rateSource === 'practice' && hasLivePracticeStats;
  const showDemoPracticeRates =
    showPracticeRateToggle && rateSource === 'practice' && !hasLivePracticeStats && demoPracticeFactor != null;
  const practiceCaption = useMemo(() => {
    if (!showLivePracticeRates) return '';
    const firstKey =
      parseLevelSeriesKey(selectedSurgeries[0])?.procedureKey ||
      visibleAvailableSurgeries[0]?.procedureKey ||
      null;
    return formatPracticeMatchCaption(practiceStats, firstKey, {
      overallN: isDemoOrDevApp(),
    });
  }, [showLivePracticeRates, practiceStats, selectedSurgeries, visibleAvailableSurgeries]);
  const isBoundedNormalPanel = !isExpanded && normalPanelHeight != null;
  const shouldFillAvailableChartSpace = isExpanded || isBoundedNormalPanel;

  const bmiFactorControls = showBmiFactor ? (
    <>
      <VitalFactorInput
        label="Weight"
        value={weightInput}
        unit="lb"
        onChange={handleWeightChange}
        ariaLabel="Patient weight in pounds"
        inputClassName="jd-vital-factor-input--medium"
        afterUnit={<FactorChangePill action={currentWeightChangeAction} onClear={revertFactorChange} />}
        factorKey="weight"
      />
      <span className="jd-factor-with-change">
        <VitalFactorInput
          label="BMI"
          value={bmiInput}
          unit={bmiRangeToVerbalLabel(selectedBmi) || ''}
          onChange={handleBmiChange}
          ariaLabel="Patient body mass index"
          inputClassName="jd-vital-factor-input--medium"
          isMissing={shouldShowBmiMissingState}
          factorKey="bmi"
        />
        <FactorChangePill action={currentBmiChangeAction} onClear={revertFactorChange} />
      </span>
    </>
  ) : null;

  const smokingFactorControl = showSmokingFactor ? (
    <span className="jd-factor-with-change">
      <StratumFilterSelect
        label="Smoker"
        value={selectedSmoking}
        onChange={handleSmokingChange}
        options={smokingOptions}
        ariaLabel="Smoking status"
        minWidth="6.75rem"
      />
      <FactorChangePill action={currentSmokingChangeAction} onClear={revertFactorChange} />
    </span>
  ) : null;

  const diabetesFactorControl = showDiabetesFactor ? (
    <span className="jd-factor-with-change">
      <StratumFilterSelect
        label="Diabetes"
        value={selectedDiabetes}
        onChange={handleDiabetesChange}
        options={diabetesOptions}
        ariaLabel="Diabetes status"
        minWidth="7.5rem"
      />
      <FactorChangePill action={currentDiabetesChangeAction} onClear={revertFactorChange} />
    </span>
  ) : null;

  const antithromboticFactorControl = showAntithromboticFactor ? (
    <span className="jd-factor-with-change">
      <StratumFilterSelect
        label="Antithrombotics"
        value={selectedAntithrombotic}
        onChange={handleAntithromboticChange}
        options={antithromboticOptions}
        ariaLabel="Antithrombotic medication status"
        minWidth="8.5rem"
        allowBlank
        isMissing={isBlankFilterValue(selectedAntithrombotic)}
        factorKey="antithrombotic"
      />
      <FactorChangePill action={currentAntithromboticChangeAction} onClear={revertFactorChange} />
    </span>
  ) : null;

  return (
    <Card
      ref={cardRef}
      className={`joint-decisions-spreadsheet mb-3 ${
        isExpanded ? 'jointdec-expanded-panel shadow' : isBoundedNormalPanel ? 'jointdec-bounded-panel' : ''
      }${showDemoReferences ? ' jointdec-has-demo-references' : ''}`}
      onClick={onEngage}
      style={isBoundedNormalPanel ? { height: `${normalPanelHeight}px` } : undefined}
    >
      <Card.Body
        className={shouldFillAvailableChartSpace || showDemoReferences ? 'd-flex flex-column h-100' : undefined}
        style={shouldFillAvailableChartSpace || showDemoReferences ? { minHeight: 0 } : undefined}
      >
        <div className="jd-above-fold">
        <div className="jd-header-title-row flex-shrink-0">
          <div className="jd-header-title-main">
            <Card.Title className="mb-0 jd-section-title">Joint Decisions</Card.Title>
            {chartReady ? (
              <>
              <JdTitleTabsCarousel activeKey={activeChartTab}>
                <Nav
                  variant="tabs"
                  activeKey={activeChartTab}
                  onSelect={handleChartTabSelect}
                  className="jd-title-tabs"
                  aria-label="Joint Decisions graph tabs"
                >
                  {chartTabs.map((tab) => (
                    <Nav.Item key={tab.key}>
                      <Nav.Link eventKey={tab.key}>
                        {tab.label}
                        {tabNotifications[tab.key] > 0 && (
                          <Badge bg="success" pill className="jd-tab-notification-badge">
                            {tabNotifications[tab.key]}
                          </Badge>
                        )}
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>
              </JdTitleTabsCarousel>
              <button
                type="button"
                className={`jd-decide-button${approvedDecision ? ' jd-decide-button--approved' : ''}`}
                onClick={approvedDecision ? handleOpenApprovedDecision : handleOpenDecideModal}
                aria-label={
                  approvedDecision
                    ? 'View approved Joint Decisions note and decision'
                    : 'Decide on a procedure'
                }
              >
                {approvedDecision ? 'View decision' : 'Decide'}
              </button>
              {showPracticeRateToggle ? (
                <div className="jd-practice-toggle-wrap">
                  <RateSourceToggle
                    value={rateSource}
                    onChange={(next) => {
                      setHasEngagedJointDecisions(true);
                      setRateSource(next);
                    }}
                  />
                  {practiceCaption ? (
                    <span className="jd-practice-sample-caption">{practiceCaption}</span>
                  ) : null}
                </div>
              ) : null}
              </>
            ) : approvedDecision ? (
              <button
                type="button"
                className="jd-decide-button jd-decide-button--approved"
                onClick={handleOpenApprovedDecision}
                aria-label="View approved Joint Decisions note and decision"
              >
                View decision
              </button>
            ) : (
              <div className="jd-title-tabs-placeholder" aria-hidden />
            )}
          </div>
          <div className="jd-header-actions">
            <Button
              variant="light"
              size="sm"
              className="border shadow-sm jd-icon-button me-2"
              onClick={handleOpenProcedureConfig}
              aria-label="Configure procedure visibility"
              title="Configure procedures"
            >
              <Gear size={18} />
            </Button>
            <Button
              variant="light"
              size="sm"
              className={`border shadow-sm jd-fullscreen-button ${
                hasEngagedJointDecisions && !isExpanded ? 'jd-fullscreen-button--pulse' : ''
              }`}
              onClick={() => setIsExpanded((v) => !v)}
              aria-label={isExpanded ? 'Exit expanded view' : 'Expand Joint Decisions'}
              title={isExpanded ? 'Exit expanded view' : 'Expand'}
            >
              {isExpanded ? <FullscreenExit size={18} /> : <ArrowsFullscreen size={18} />}
            </Button>
          </div>
        </div>

        {showSpinner && (
          <div className="text-center py-4 flex-shrink-0">
            <Spinner animation="border" size="sm" />
            <span className="ms-2 text-muted">Reading patient chart…</span>
          </div>
        )}

        {loadError && (
          <p className="text-danger small mb-2" role="alert">
            {loadError}
          </p>
        )}
        {regressionModelsStatus === 'error' && !LOAD_STRATA_CDN_SLICE && (
          <p className="text-danger small mb-2" role="alert">
            Unable to load Joint Decisions models.
          </p>
        )}

        {patientChartLoaded && (
          <>
            <div className="jd-header-row flex-shrink-0">
              <div className="jd-header-filters" ref={headerFiltersRef}>
                {shouldCollapseStableFactors && (
                  <FactorModeToggle
                    exploreSelected={isExploreChangesMode}
                    describedBy={
                      isExploreChangesMode && chartReady ? 'jd-factor-mode-hint' : undefined
                    }
                    onExplore={() => {
                      setHasEngagedJointDecisions(true);
                      setStableFactorsExpanded(false);
                    }}
                    onCorrect={() => {
                      setHasEngagedJointDecisions(true);
                      setStableFactorsExpanded(true);
                    }}
                  />
                )}
                {isExploreChangesMode && chartReady && (
                  <Alert
                    variant="warning"
                    id="jd-factor-mode-hint"
                    className="py-1 mb-0 jd-factor-mode-hint jd-factor-mode-hint--inline"
                    role="status"
                  >
                    <span>Suggested improvements</span>
                    {renderSuggestedImprovementChips()}
                  </Alert>
                )}
                {showStableFactorControls && (
                  <HeightFactorInput
                    feet={heightFeetInput}
                    inches={heightInchesInput}
                    onFeetChange={handleHeightFeetChange}
                    onInchesChange={handleHeightInchesChange}
                  />
                )}
                {bmiFactorControls}
                {smokingFactorControl}
                {diabetesFactorControl}
                {showStableFactorControls && (
                  <StratumFilterSelect
                    label="Insurance"
                    value={selectedInsurance}
                    onChange={handleInsuranceChange}
                    options={insuranceOptions}
                    ariaLabel="Insurance type"
                    minWidth="7.5rem"
                    allowBlank
                    isMissing={isBlankFilterValue(selectedInsurance)}
                    factorKey="insurance"
                  />
                )}
                {antithromboticFactorControl}
                {showStableFactorControls && (
                  <StratumFilterSelect
                    label="Revision"
                    value={selectedRevisionSurgery}
                    onChange={handleRevisionSurgeryChange}
                    options={revisionSurgeryOptions}
                    ariaLabel="Revision surgery status"
                    minWidth="5.5rem"
                  />
                )}
              </div>
            </div>
            {needsRequiredInput && (
              <Alert variant="warning" className="py-2 mb-2 flex-shrink-0" role="status">
                Enter the missing patient factors above to load Joint Decisions:{' '}
                <strong>{missingRequiredFactorLabels.join(', ')}</strong>.
              </Alert>
            )}
          </>
        )}

        {dataReady && availableSurgeries.length > 0 && visibleAvailableSurgeries.length === 0 && selectedRegion && (
          <p className="text-muted small mb-3">
            {hiddenProcedureSet.size > 0 &&
            availableSurgeries.every((item) =>
              hiddenProcedureSet.has(item.procedureKey || parseLevelSeriesKey(item.surgery)?.procedureKey),
            )
              ? `All procedures for ${regionLabel || selectedRegion} are hidden in your organization settings. Use the gear button to show procedures your practice offers.`
              : `No ${pickerIsMultilevel ? '3+' : '1–2'} level procedures for ${regionLabel || selectedRegion} at this patient\u2019s stratum.`}
          </p>
        )}

        {dataReady && availableSurgeries.length === 0 && selectedRegion && (
          <p className="text-muted small mb-3">
            No procedures in the spreadsheet for {regionLabel || selectedRegion} at this patient&apos;s sex and age.
          </p>
        )}

        {patientChartLoaded && !needsRequiredInput && (
          waitingForCharts || (dataReady && !chartsRevealed) ? (
            <div
              className={`jd-main-stage jd-main-stage--fill${
                shouldFillAvailableChartSpace ? ' flex-grow-1' : ''
              }`}
              style={{ minHeight: shouldFillAvailableChartSpace ? '16rem' : '18rem' }}
              aria-busy="true"
              aria-live="polite"
            >
              {waitingForCharts ? (
                <div className="text-center py-4 flex-shrink-0 w-100">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2 text-muted">Loading Joint Decisions…</span>
                </div>
              ) : null}
            </div>
          ) : dataReady ? (
            <div
              className={`jd-main-stage jd-main-stage--fill${
                shouldFillAvailableChartSpace ? ' flex-grow-1' : ''
              }`}
              style={{ minHeight: shouldFillAvailableChartSpace ? '16rem' : '18rem' }}
            >
              {visibleAvailableSurgeries.length > 0 && (
                <aside className="jd-procedure-key-rail" aria-label="Procedure comparison key">
                  <div className="jd-procedure-picker jd-procedure-picker--rail">
                    <div className="jd-procedure-picker-header">
                      {selectedRegion && (
                        <StratumFilterSelect
                          label="Region"
                          value={selectedRegion}
                          onChange={handleRegionChange}
                          options={regionOptions}
                          ariaLabel="Anatomical region"
                          minWidth="5.25rem"
                          selectClassName="jd-region-select"
                        />
                      )}
                      <StratumFilterSelect
                        label="Levels"
                        value={selectedPickerLevels}
                        onChange={handlePickerLevelsChange}
                        options={levelsOptions}
                        ariaLabel="Surgical levels for procedure list"
                        minWidth="6.5rem"
                      />
                    </div>
                    <div className="jd-procedure-options jd-procedure-options--rail">
                      {hasPickedModification && (
                        <Form.Check
                          type="checkbox"
                          id="jd-modification-key"
                          className="jd-procedure-check jd-modification-check"
                          style={{ '--jd-surgery-color': JD_MODIFICATION_COLOR }}
                          label={<span>Post lifestyle change</span>}
                          checked={showModificationOverlay}
                          onChange={(e) => {
                            setHasEngagedJointDecisions(true);
                            setShowModificationOverlay(e.target.checked);
                          }}
                        />
                      )}
                      {activeChartTab === JD_CHART_TAB_KEYS.MEDICAL_RISKS && (
                        <Form.Check
                          type="checkbox"
                          id="jd-risk-control"
                          className="jd-procedure-check"
                          style={{ '--jd-surgery-color': '#6c757d' }}
                          label={<span>{SPREADSHEET_CONTROL_LABEL}</span>}
                          checked={includeRiskControl}
                          onChange={(e) => {
                            setHasEngagedJointDecisions(true);
                            setIncludeRiskControl(e.target.checked);
                          }}
                        />
                      )}
                      {visibleAvailableSurgeries.map((item) => {
                        const procedureKey =
                          item.procedureKey || parseLevelSeriesKey(item.surgery)?.procedureKey;
                        return (
                          <Form.Check
                            key={item.surgery}
                            type="checkbox"
                            id={`jd-surgery-${item.surgery}`}
                            className={`jd-procedure-check${
                              pickerIsMultilevel ? ' jd-procedure-check--multilevel' : ''
                            }`}
                            style={{
                              '--jd-surgery-color': getSpreadsheetSurgeryColor(procedureKey),
                            }}
                            label={
                              <span>{item.procedureLabel || item.label || procedureKey}</span>
                            }
                            checked={selectedSurgeries.includes(item.surgery)}
                            onChange={(e) =>
                              handleSurgeryCheckboxChange(item.surgery, e.target.checked)
                            }
                          />
                        );
                      })}
                    </div>
                  </div>
                </aside>
              )}

              <div className="jd-main-stage-chart">
                {chartReady && (
                  <ChartLevelLegend activeChartTab={activeChartTab} />
                )}
                {chartReady ? (
                  <JointDecisionsChartTabs
                    chartTabs={chartTabs}
                    activeChartTab={activeChartTab}
                    onActiveChartTabChange={handleChartTabSelect}
                    profiles={profiles}
                    scenarioProfiles={visibleScenarioProfilesForCurrentFactors}
                    riskLabelsByType={riskLabelsByType}
                    spreadsheetControls={spreadsheetControls}
                    scenarioSpreadsheetControls={visibleScenarioSpreadsheetControlsForCurrentFactors}
                    practiceOverlayFactor={showDemoPracticeRates ? demoPracticeFactor : null}
                    practiceStats={showLivePracticeRates ? practiceStats : null}
                    practiceOutcomeChangeScale={
                      showDemoPracticeRates ? DEMO_PRACTICE_OUTCOME_CHANGE_SCALE : null
                    }
                    chartOrientation={chartOrientation}
                    fillAvailable={shouldFillAvailableChartSpace}
                    useDynamicRiskScale={useDynamicRiskScale}
                    includeRiskControl={includeRiskControl}
                    selectedRegion={selectedRegion}
                    thresholdFilterEnabled={effectiveThresholdFilterEnabled}
                    tabNotifications={tabNotifications}
                    valuePayload={valuePayload}
                    valueStatus={valueStatus}
                    valueSetting={valueSetting}
                    onValueSettingChange={(key) => {
                      setHasEngagedJointDecisions(true);
                      setValueSetting(key);
                    }}
                    onToggleRiskScale={() => {
                      setHasEngagedJointDecisions(true);
                      setDynamicRiskScaleByTab((prev) => ({
                        ...prev,
                        [activeChartTab]: !prev[activeChartTab],
                      }));
                    }}
                  />
                ) : (
                  <Alert variant="warning" className="py-2 mb-0 flex-shrink-0" role="status">
                    Select a surgery to begin.
                  </Alert>
                )}
              </div>
            </div>
          ) : !loadError && !(regressionModelsStatus === 'error' && !LOAD_STRATA_CDN_SLICE) ? (
            <p className="text-muted small mb-0">No spreadsheet risk rows matched this patient stratum.</p>
          ) : null
        )}
        </div>
        {showDemoReferences ? (
          <JointDecisionsDemoReferences references={demoReferences} />
        ) : null}
      </Card.Body>
      <ProcedureConfigModal
        show={showProcedureConfig}
        onHide={() => {
          if (!procedureConfigSaving) setShowProcedureConfig(false);
        }}
        catalog={procedureCatalog}
        hiddenProcedures={hiddenProcedures}
        onSave={handleSaveProcedureConfig}
        saving={procedureConfigSaving}
        error={procedureConfigError}
      />
      <Modal
        show={showDecideModal}
        onHide={() => {
          setShowDecideModal(false);
          setEnrollmentSurgeryNotice(false);
        }}
        centered
        scrollable
        size="lg"
        className="jd-decide-modal"
        restoreFocus={false}
        onClick={(event) => event.stopPropagation()}
      >
        <Modal.Header closeButton>
          <Modal.Title>Decide on a procedure</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {enrollmentSurgeryNotice && (
            <Alert variant="warning" className="py-2 mb-3">
              In order to enroll in a study, this patient needs a scheduled surgery.
            </Alert>
          )}
          <Nav
            variant="tabs"
            activeKey={decideModalTab}
            onSelect={(key) => {
              if (!key) return;
              setDecideModalTab(key);
            }}
            className="jd-decide-modal-tabs"
            aria-label="Decide tabs"
          >
            <Nav.Item>
              <Nav.Link eventKey="procedure">Procedure</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="note">Note</Nav.Link>
            </Nav.Item>
          </Nav>
          {decideError && (
            <Alert variant="danger" className="py-2 mt-3">
              {decideError}
            </Alert>
          )}
          {decideModalTab === 'note' ? (
            <JointDecisionsEncounterNote
              noteText={displayedEncounterNote}
              onNoteTextChange={(text) => {
                setEncounterNotesEdited((prev) => ({ ...prev, [encounterNoteType]: true }));
                setEncounterNotes((prev) => ({ ...prev, [encounterNoteType]: text }));
              }}
              decision={encounterDecision}
              onDecisionChange={(value) => {
                setEncounterDecision(value);
                setEncounterNotesEdited(emptyEncounterNoteEdited());
              }}
              noteType={encounterNoteType}
              onNoteTypeChange={setEncounterNoteType}
              procedureLevelsText={procedureLevelsText}
              onProcedureLevelsTextChange={(value) => {
                setProcedureLevelsText(value);
                setEncounterNotesEdited((prev) => ({
                  ...prev,
                  [ENCOUNTER_NOTE_TYPE.SURGERY_DECISION]: false,
                }));
              }}
              regionLabel={regionLabel}
              showSurgeryDecisionControls
            />
          ) : (
            <>
          <Form.Group className="mb-3 mt-3">
            <Form.Label>Surgery</Form.Label>
            <Form.Select
              value={decideSurgeryKey}
              onChange={(event) => setDecideSurgeryKey(event.target.value)}
              aria-label="Surgery"
            >
              {MED_MANAGEMENT_SURGERIES.map((token) => (
                <option key={token} value={token}>
                  {formatMedManagementSurgeryLabel(token)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-0">
            <div className="d-flex align-items-baseline justify-content-between gap-2">
              <Form.Label className="mb-1">Surgery date</Form.Label>
              <span className="text-muted small">This can be changed later</span>
            </div>
            <Form.Control
              type="date"
              value={decideSurgeryDate}
              onChange={(event) => setDecideSurgeryDate(event.target.value)}
              aria-label="Surgery date"
            />
          </Form.Group>
          <Form.Group className="mt-3 mb-0">
            <Form.Label>
              Would you like to add, remove, or change any antithrombotic medications?
            </Form.Label>
            <div className="jd-decide-med-list" role="list">
              {decideMedications.map((item, index) => {
                const isLast = index === decideMedications.length - 1;
                const options = availableDecideMedicationOptions(
                  decideMedications,
                  item.id,
                  item.token,
                );
                return (
                  <div key={item.id} className="jd-decide-med-row" role="listitem">
                    <Form.Select
                      className="jd-decide-med-row-select"
                      size="sm"
                      value={item.token}
                      onChange={(event) => {
                        const token = event.target.value;
                        setDecideMedications((prev) =>
                          prev.map((row) =>
                            row.id === item.id
                              ? {
                                  ...row,
                                  token,
                                  dosage: token
                                    ? defaultDailyDosageForMedication(token)
                                    : '',
                                }
                              : row
                          )
                        );
                      }}
                      aria-label={isLast ? 'Add antithrombotic medication' : 'Antithrombotic medication'}
                    >
                      <option value="">Select a medication…</option>
                      {options.map((token) => (
                        <option key={token} value={token}>
                          {formatMedManagementSurgeryLabel(token)}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Label
                      className="jd-decide-med-row-dosage-label mb-0"
                      htmlFor={`jd-decide-dosage-${item.id}`}
                    >
                      Daily dosage
                    </Form.Label>
                    <Form.Control
                      id={`jd-decide-dosage-${item.id}`}
                      className="jd-decide-med-row-dosage-input"
                      type="text"
                      size="sm"
                      value={item.dosage}
                      disabled={!item.token}
                      onChange={(event) => {
                        const nextDosage = event.target.value;
                        setDecideMedications((prev) =>
                          prev.map((row) =>
                            row.id === item.id ? { ...row, dosage: nextDosage } : row
                          )
                        );
                      }}
                      aria-label="Daily dosage"
                    />
                    {isLast ? (
                      <button
                        type="button"
                        className="jd-decide-med-row-add"
                        aria-label="Add another medication"
                        onClick={() =>
                          setDecideMedications((prev) => [...prev, newDecideMedRow()])
                        }
                      >
                        <Plus size={18} />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="jd-decide-med-row-remove"
                        aria-label="Remove medication"
                        onClick={() =>
                          setDecideMedications((prev) => prev.filter((row) => row.id !== item.id))
                        }
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowDecideModal(false);
              setEnrollmentSurgeryNotice(false);
            }}
          >
            Cancel
          </Button>
          <Button
            className="jd-decide-submit-button"
            onClick={handleDecideSubmit}
            disabled={!decideSurgeryKey || !decideSurgeryDate}
          >
            {enrollmentSurgeryNotice ? 'Submit and go to enrollment' : 'Submit and go to Med Management'}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showApprovedDecisionModal}
        onHide={() => setShowApprovedDecisionModal(false)}
        centered
        scrollable
        size="lg"
        className="jd-decide-modal"
        restoreFocus={false}
        onClick={(event) => event.stopPropagation()}
      >
        <Modal.Header closeButton>
          <Modal.Title>Approved Joint Decision</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {approvedDecision ? (
            <>
              <div className="jd-approved-review-meta">
                <span className="jd-approved-review-pill">Approved</span>
                {formatDecisionTimestamp(approvedDecision.approval_time) ? (
                  <span className="text-muted">
                    {formatDecisionTimestamp(approvedDecision.approval_time)}
                  </span>
                ) : null}
              </div>
              {approvedSaveError && (
                <Alert variant="danger" className="py-2">
                  {approvedSaveError}
                </Alert>
              )}
              <Form.Group className="mb-3">
                <Form.Label>Surgery</Form.Label>
                <Form.Select
                  value={approvedDraftSurgeryKey}
                  onChange={(event) => setApprovedDraftSurgeryKey(event.target.value)}
                  aria-label="Approved surgery"
                >
                  {!approvedDraftSurgeryKey ? (
                    <option value="">Select a surgery…</option>
                  ) : null}
                  {approvedSurgeryOptions.map((token) => (
                    <option key={token} value={token}>
                      {formatMedManagementSurgeryLabel(token)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Surgery date</Form.Label>
                <Form.Control
                  type="date"
                  value={approvedDraftSurgeryDate}
                  onChange={(event) => setApprovedDraftSurgeryDate(event.target.value)}
                  aria-label="Approved surgery date"
                />
              </Form.Group>
              <JointDecisionsEncounterNote
                idPrefix="jd-approved-note"
                noteText={displayedApprovedNote}
                onNoteTextChange={(text) => {
                  setApprovedDraftNotes((prev) => ({
                    ...prev,
                    [approvedDraftNoteType]: text,
                  }));
                }}
                decision={approvedDraftDecision}
                onDecisionChange={setApprovedDraftDecision}
                noteType={approvedDraftNoteType}
                onNoteTypeChange={setApprovedDraftNoteType}
                hint="Toggle note type or edit the note, then save. Changing surgery or date deactivates the current Med Management appointment and creates a new one."
              />
            </>
          ) : (
            <p className="text-muted mb-0">No approved Joint Decision is saved for this patient.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowApprovedDecisionModal(false)}
            disabled={approvedSaving}
          >
            Close
          </Button>
          <Button
            className="jd-decide-submit-button"
            onClick={handleSaveApprovedDecision}
            disabled={!approvedDecisionDirty || approvedSaving || !approvedDraftSurgeryKey || !approvedDraftSurgeryDate}
          >
            {approvedSaving
              ? (approvedSurgeryChanged ? 'Submitting…' : 'Saving…')
              : (approvedSurgeryChanged
                ? 'Submit and go to Med Management'
                : 'Save')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
}

export default JointDecisionsSpreadsheet;
